import { getAssignedDestinations } from '../Apps/assignedAppApi';
import AdRotationEditor from './AdRotationEditor';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Layers, Link, List, Pencil, Plus, Save, Trash2, Tv2 } from 'lucide-react';
import { Rnd } from 'react-rnd';
import {
  fmtDateTimeLocal,
  parseDateTimeLocal,
  rruleLabel,
  Badge,
  Modal,
  SectionCard,
  startOfDay,
  startOfWeek,
  startOfMonth,
  addDays,
  addMonths,
} from './utils';
import { createScanId, debugPlaybackLog } from '../../utils/debugPlayback';
import {
  listExternalAppContentTargets,
  listExternalAppProfiles,
  previewExternalMediaLink,
  saveExternalMediaLinkTarget,
} from '../Apps/externalAppControlApi';
import {
  encodeScheduledContentTarget,
  encodeScheduledAppTarget,
  hasIntentLaunchRecipe,
  parseScheduledAppTarget,
} from './scheduledAppTarget';
import GigsCampaignsLane from './GigsCampaignsLane';
import GigEditorModal from '../Gigs/GigEditorModal';
import GigRecurringTemplateModal from '../Gigs/GigRecurringTemplateModal';
import { listGigs as fetchGigItems } from '../Gigs/gigApi';
import ProgrammeAutomationPreview from './ProgrammeAutomationPreview';
import { formatGigDisplayStatusLabel, getGigDisplayStatus } from '../Gigs/gigStatus';
import {
  resolveSchedulerTargetGroupDeviceIds,
  targetGroupAvailabilityMessage,
} from '../VenueTargets/targetGroupUtils';
import {
  buildUpcomingGigSeriesItems,
  buildGigWarnings,
  buildSchedulerListItems,
  formatSchedulerRangeLabel,
  getRecurringGigSeriesKey,
  isMultiDayGig,
  isRecurringGig,
} from './schedulerView';
import {
  DAY_KEYS,
  DAY_LABELS,
  buildAllowedSegmentsForRange,
  createEmptyWeeklySchedule,
  normalizeWeeklyPowerSchedule,
  normalizeTimeString,
  detectPowerScheduleMode,
  derivePowerScheduleFromSite,
  buildClosedSegmentsForRange,
  buildPowerMarkersForRange,
  isRangeAllowedByPowerSchedule,
} from './powerSchedule';
import {
  getTimelineBlockLayout,
  getVisibleDurationMs,
  normalizeVisibleWindows,
  projectTimeToLaneX,
} from './timelineMath';

const ITEM_TYPES = [
  { id: 'app', label: 'App', color: 'bg-cyan-500/20 text-cyan-200', dot: '#22d3ee' },
  { id: 'playlist', label: 'Playlist', color: 'bg-sky-500/20 text-sky-200', dot: '#38bdf8' },
  { id: 'ad', label: 'Ad', color: 'bg-red-500/20 text-red-200', dot: '#f87171' },
  { id: 'overlay', label: 'Overlay Ads', color: 'bg-purple-500/20 text-purple-200', dot: '#a78bfa' },
  { id: 'power', label: 'Power', color: 'bg-emerald-500/20 text-emerald-200', dot: '#34d399' },
  { id: 'input', label: 'Input/Channel', color: 'bg-amber-500/20 text-amber-200', dot: '#f59e0b' },
  { id: 'event', label: 'Event', color: 'bg-orange-500/20 text-orange-200', dot: '#fb923c' },
];

const typeMeta = (id) => ITEM_TYPES.find((t) => t.id === id) || ITEM_TYPES[0];

const resolveViewRangeStart = (date, viewMode) => {
  const base = date instanceof Date ? date : new Date(date);
  if (viewMode === 'week') return startOfWeek(base);
  if (viewMode === 'month') return startOfMonth(base);
  return startOfDay(base);
};

const resolveViewRangeEnd = (rangeStart, viewMode) => {
  if (viewMode === 'week') return addDays(rangeStart, 7);
  if (viewMode === 'month') return addMonths(rangeStart, 1);
  return addDays(rangeStart, 1);
};

const resolveViewAdvanceDate = (date, viewMode, direction) => {
  if (viewMode === 'week') return addDays(date, direction * 7);
  if (viewMode === 'month') return addMonths(date, direction);
  return addDays(date, direction);
};

const hashSeriesKey = (value) => {
  const input = String(value || '');
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

const getAdSeriesColorStyle = (seriesSelectionId) => {
  const paletteIndex = hashSeriesKey(seriesSelectionId) % AD_SERIES_COLOR_STYLES.length;
  return AD_SERIES_COLOR_STYLES[paletteIndex];
};

const timeLabel = (date) =>
  new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', hour12: true }).format(date);
const dateLabel = (date) =>
  date.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

const SNAP_THRESHOLD_MS = 3 * 60 * 1000;
const MIN_EVENT_MS = 60 * 1000;
const DEFAULT_LIVE_EVENT_PRESTART_MS = 60 * 60 * 1000;
const DEFAULT_EVENT_POST_DURATION_SEC = 600;
const DEFAULT_EVENT_TEASER_MS = 30 * 1000;
const DEFAULT_APP_DURATION_MS = 60 * 60 * 1000;
const DEFAULT_DROPPED_ITEM_DURATION_MS = 3 * 60 * 60 * 1000;
const DEFAULT_AD_WINDOW_MINUTES = 60;
const TV_DEVICE_TYPES = new Set(['TV', 'tv-control', 'stb', 'lgtv']);
const HOUR_MS = 60 * 60 * 1000;

const VIEW_PRESETS = Object.freeze({
  day: {
    label: 'Day',
    tickHours: 1,
    tickWidth: 96,
  },
  week: {
    label: 'Week',
    tickHours: 6,
    tickWidth: 88,
  },
  month: {
    label: 'Month',
    tickHours: 24,
    tickWidth: 108,
  },
});

const AD_SERIES_COLOR_STYLES = Object.freeze([
  { className: 'bg-rose-500/95 ring-rose-100/90 hover:bg-rose-400/95', dotClassName: 'bg-rose-300', rowClassName: 'bg-rose-500/10 ring-rose-300/30' },
  { className: 'bg-orange-500/95 ring-orange-100/90 hover:bg-orange-400/95', dotClassName: 'bg-orange-300', rowClassName: 'bg-orange-500/10 ring-orange-300/30' },
  { className: 'bg-amber-500/95 ring-amber-100/90 hover:bg-amber-400/95', dotClassName: 'bg-amber-300', rowClassName: 'bg-amber-500/10 ring-amber-300/30' },
  { className: 'bg-lime-500/95 ring-lime-100/90 hover:bg-lime-400/95', dotClassName: 'bg-lime-300', rowClassName: 'bg-lime-500/10 ring-lime-300/30' },
  { className: 'bg-emerald-500/95 ring-emerald-100/90 hover:bg-emerald-400/95', dotClassName: 'bg-emerald-300', rowClassName: 'bg-emerald-500/10 ring-emerald-300/30' },
  { className: 'bg-teal-500/95 ring-teal-100/90 hover:bg-teal-400/95', dotClassName: 'bg-teal-300', rowClassName: 'bg-teal-500/10 ring-teal-300/30' },
  { className: 'bg-cyan-500/95 ring-cyan-100/90 hover:bg-cyan-400/95', dotClassName: 'bg-cyan-300', rowClassName: 'bg-cyan-500/10 ring-cyan-300/30' },
  { className: 'bg-sky-500/95 ring-sky-100/90 hover:bg-sky-400/95', dotClassName: 'bg-sky-300', rowClassName: 'bg-sky-500/10 ring-sky-300/30' },
  { className: 'bg-violet-500/95 ring-violet-100/90 hover:bg-violet-400/95', dotClassName: 'bg-violet-300', rowClassName: 'bg-violet-500/10 ring-violet-300/30' },
  { className: 'bg-fuchsia-500/95 ring-fuchsia-100/90 hover:bg-fuchsia-400/95', dotClassName: 'bg-fuchsia-300', rowClassName: 'bg-fuchsia-500/10 ring-fuchsia-300/30' },
]);

const formatDateInputValue = (value) => fmtDateTimeLocal(new Date(value)).slice(0, 10);
const formatTimeInputValue = (value) => fmtDateTimeLocal(new Date(value)).slice(11, 16);
const parseDateAndTimeLocal = (dateValue, timeValue = '00:00') =>
  parseDateTimeLocal(`${dateValue}T${normalizeTimeString(timeValue) || '00:00'}`);
const timeStringToMinutes = (value) => {
  const normalized = normalizeTimeString(value);
  if (!normalized) return null;
  const [hour, minute] = normalized.split(':').map(Number);
  return hour * 60 + minute;
};

const clampToNow = (start, end) => {
  const now = new Date();
  if (start < now) {
    const dur = end.getTime() - start.getTime();
    const safeStart = now;
    const safeEnd = new Date(safeStart.getTime() + Math.max(dur, 5 * 60 * 1000));
    return { start: safeStart, end: safeEnd };
  }
  return { start, end };
};

const clampStartToNow = (start, endFixed) => {
  const now = new Date();
  if (start < now) {
    const safeStart = now;
    const safeEnd = endFixed;
    return { start: safeStart, end: safeEnd };
  }
  return { start, end: endFixed };
};

const shouldPreservePastStart = (item = {}) => Boolean(String(item?.recurrence_rule || '').trim());

const getScheduledItemRenderKey = (item = {}) =>
  String(item?.occurrence_key || `${item?.id}:${item?.start_time || item?.start || ''}`);

const applyStartAnchoredDragImage = (event, label, accent = '#38bdf8') => {
  const dataTransfer = event?.dataTransfer;
  if (!dataTransfer || typeof document === 'undefined') return;

  const ghost = document.createElement('div');
  ghost.textContent = label || 'Schedule item';
  ghost.style.position = 'fixed';
  ghost.style.top = '-9999px';
  ghost.style.left = '-9999px';
  ghost.style.padding = '8px 12px';
  ghost.style.borderRadius = '12px';
  ghost.style.border = `1px solid ${accent}`;
  ghost.style.background = 'rgba(15, 23, 42, 0.96)';
  ghost.style.color = '#f8fafc';
  ghost.style.fontSize = '12px';
  ghost.style.fontWeight = '600';
  ghost.style.whiteSpace = 'nowrap';
  ghost.style.boxShadow = '0 10px 24px rgba(15, 23, 42, 0.35)';
  ghost.style.pointerEvents = 'none';
  document.body.appendChild(ghost);
  dataTransfer.setDragImage(ghost, 12, Math.round(ghost.offsetHeight / 2));
  requestAnimationFrame(() => ghost.remove());
};

const schedulerFetch = async (path, options = {}) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const normalizedPath = path.startsWith('/api/') ? path.slice(4) : path;
  const res = await fetch(`/api/scheduler${normalizedPath}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Scheduler request failed');
  }
  return res.json();
};

const appFetch = async (path, options = {}) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const normalizedPath = path.startsWith('/api/') ? path.slice(4) : path;
  const res = await fetch(`/api${normalizedPath}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'API request failed');
  }
  return res.json();
};

const getRequestErrorMessage = (error, fallback) => {
  const rawMessage = String(error?.message || '').trim();
  if (rawMessage) {
    try {
      const payload = JSON.parse(rawMessage);
      if (payload?.message) return String(payload.message);
      if (payload?.error) return String(payload.error);
    } catch (_parseError) {
      // Keep the human-readable error below when the response is not JSON.
    }
  }
  return rawMessage || fallback;
};

const LANE_HEIGHT = 68;
const TICK_HEIGHT = 28;
const OVERLAY_BLOCK_Y = 6;
const OVERLAY_BLOCK_HEIGHT = 24;
const EVENT_BLOCK_Y = 22;
const EVENT_BLOCK_HEIGHT = LANE_HEIGHT - EVENT_BLOCK_Y - 2;
const MIN_TICK_WIDTH = 48;
const MAX_TICK_WIDTH = 220;
const TIMELINE_ZOOM_STEP = 16;
const overlayTypeStyle = (type) => {
  const normalized = String(type || '').toLowerCase();
  if (normalized === 'announcement') {
    return {
      className: 'bg-cyan-500/95 ring-cyan-100/90 hover:bg-cyan-400/95',
      dotClassName: 'bg-cyan-300',
    };
  }
  if (normalized === 'full') {
    return {
      className: 'bg-emerald-500/95 ring-emerald-100/90 hover:bg-emerald-400/95',
      dotClassName: 'bg-emerald-300',
    };
  }
  if (normalized === 'top') {
    return {
      className: 'bg-fuchsia-600/95 ring-fuchsia-100/90 hover:bg-fuchsia-500/95',
      dotClassName: 'bg-fuchsia-300',
    };
  }
  if (normalized === 'bottom') {
    return {
      className: 'bg-orange-500/95 ring-orange-100/90 hover:bg-orange-400/95',
      dotClassName: 'bg-orange-300',
    };
  }
  return {
    className: 'bg-slate-500/90 ring-slate-100/70 hover:bg-slate-400/90',
    dotClassName: 'bg-slate-300',
  };
};

const clampTickWidth = (value) => Math.max(MIN_TICK_WIDTH, Math.min(MAX_TICK_WIDTH, value));

const isTimelinePlaylistItem = (event) => {
  const normalized = String(event?.item_type || '').toLowerCase();
  return normalized === 'playlist' || normalized === 'ad';
};

const getSeriesSelectionId = (series) => {
  if (!series) return null;
  const selectionId = Number(series.sync_owner_series_id || series.id);
  return Number.isFinite(selectionId) && selectionId > 0 ? selectionId : null;
};

const getAdContentType = (item) => {
  if (!item) return 'overlay';
  const raw = String(item.content_type || item.contentType || '').trim().toLowerCase();
  if (raw === 'announcement' || item.announcement_id || item.announcementId) return 'announcement';
  return 'overlay';
};

const normalizeAdPlaybackMode = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return ['auto', 'suitestream', 'system'].includes(raw) ? raw : 'auto';
};

const clampAdTransparency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 60;
  return Math.max(0, Math.min(100, Math.round(n)));
};

function EventBlock({
  event,
  previewId,
  laneStart,
  msPerPx,
  laneWidth,
  rangeStart,
  rangeEnd,
  visibleWindows,
  interactive = true,
  onMove,
  onResize,
  onOpen,
  onDelete,
  draftPosition,
  onPreview,
  onPreviewEnd,
  formatTimeLabel,
  resolveItemName,
  blockY = EVENT_BLOCK_Y,
  blockHeight = EVENT_BLOCK_HEIGHT,
}) {
  const startMs = new Date(event.start_time).getTime();
  const endMs = new Date(event.end_time).getTime();
  const durationMs = endMs - startMs;
  const layout = getTimelineBlockLayout({
    startsAt: event.start_time,
    endsAt: event.end_time,
    rangeStart,
    rangeEnd,
    laneWidth,
    visibleWindows,
    minWidth: 6,
  });
  if (!layout) return null;
  const leftPx = layout.left;
  const widthPx = layout.width;
  const currentX = draftPosition?.x ?? leftPx;
  const currentWidth = draftPosition?.width ?? widthPx;
  const meta = typeMeta(event.item_type);
  const name = resolveItemName?.(event) || '';
  const eventMode = event.event_mode || 'live';
  const isResizable = !(event.item_type === 'event' && eventMode === 'live');

  if (!interactive) {
    return (
      <button
        type="button"
        className="absolute overflow-hidden rounded-xl border border-white/10 bg-white/10 px-3 text-left shadow-lg"
        style={{ left: currentX, width: currentWidth, top: blockY, height: blockHeight, zIndex: 12 }}
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.(event);
        }}
        title={`${name} • ${formatTimeLabel(new Date(event.start_time))} – ${formatTimeLabel(new Date(event.end_time))}`}
      >
        <div className={`flex h-full items-center px-0 py-2 text-xs text-white ${meta.color}`}>
          <div className="truncate text-[12px] font-semibold leading-tight">{name}</div>
        </div>
      </button>
    );
  }

  return (
    <Rnd
      size={{ width: currentWidth, height: blockHeight }}
      position={{ x: currentX, y: blockY }}
      bounds="parent"
      enableResizing={isResizable ? { left: true, right: true, top: false, bottom: false } : false}
      dragAxis="x"
      cancel=".scheduler-item-action"
      onDragStart={(e, d) => onPreview?.(previewId, d.x, currentWidth)}
      onDrag={(e, d) => {
        if (event.item_type === 'event' && eventMode === 'live') {
          const fixedEndMs = endMs;
          const nextStartMs = laneStart + d.x * msPerPx;
          const nextWidth = Math.max(6, (fixedEndMs - nextStartMs) / msPerPx);
          onPreview?.(previewId, d.x, nextWidth);
        } else {
          onPreview?.(previewId, d.x, currentWidth);
        }
      }}
      onDragStop={(e, d) => {
        onPreviewEnd?.(previewId);
        const newStartMs = laneStart + d.x * msPerPx;
        if (event.item_type === 'event' && eventMode === 'live') {
          onMove(new Date(newStartMs), new Date(endMs), event);
        } else {
          const newEndMs = newStartMs + durationMs;
          onMove(new Date(newStartMs), new Date(newEndMs), event);
        }
      }}
      onResize={(e, dir, ref, delta, position) => {
        onPreview?.(previewId, position.x, ref.offsetWidth);
      }}
      onResizeStop={(e, dir, ref, delta, position) => {
        onPreviewEnd?.(previewId);
        const newStartMs = laneStart + position.x * msPerPx;
        const newEndMs = newStartMs + ref.offsetWidth * msPerPx;
        onResize(new Date(newStartMs), new Date(newEndMs), event);
      }}
      className="rounded-xl shadow-lg border border-white/10 bg-white/10 overflow-hidden cursor-move"
      style={{ zIndex: draftPosition ? 26 : 8 }}
    >
      <div className={`w-full h-full px-3 py-2 text-xs text-white flex items-center ${meta.color}`}>
        <div className="text-[12px] font-semibold leading-tight truncate">{name}</div>
        <div className="absolute right-2 top-2 flex gap-2">
          <button
            type="button"
            className="scheduler-item-action text-[10px] px-2 py-1 rounded-lg bg-white/20 hover:bg-white/30"
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(event);
            }}
          >
            Open
          </button>
          <button
            type="button"
            className="scheduler-item-action text-[10px] px-2 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(event);
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </Rnd>
  );
}

function OverlayBlock({
  item,
  laneStart,
  msPerPx,
  laneWidth,
  rangeStart,
  rangeEnd,
  visibleWindows,
  interactive = true,
  onOpen,
  onMoveOrResize,
  draftPosition,
  onPreview,
  onPreviewEnd,
  title,
  formatTimeLabel,
  isResizable,
  className,
  style,
  label,
  selected,
}) {
  const durationMs = new Date(item.end_time).getTime() - new Date(item.start_time).getTime();
  const layout = getTimelineBlockLayout({
    startsAt: item.start_time,
    endsAt: item.end_time,
    rangeStart,
    rangeEnd,
    laneWidth,
    visibleWindows,
    minWidth: 4,
  });
  if (!layout) return null;
  const leftPx = layout.left;
  const widthPx = layout.width;
  const currentX = draftPosition?.x ?? leftPx;
  const currentWidth = draftPosition?.width ?? widthPx;

  if (!interactive || !isResizable) {
    return (
      <button
        type="button"
        className={`absolute rounded-md ring-1 hover:opacity-95 ${className || 'bg-purple-500/40 ring-purple-300/40'}`}
        style={{
          left: currentX,
          width: currentWidth,
          top: OVERLAY_BLOCK_Y,
          height: OVERLAY_BLOCK_HEIGHT,
          zIndex: selected ? 42 : 38,
          ...(style || {}),
        }}
        title={`${title} • ${formatTimeLabel(new Date(item.start_time))} – ${formatTimeLabel(new Date(item.end_time))}`}
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.(item);
        }}
      >
        <div className="flex h-full items-center px-2 text-[10px] font-semibold tracking-[0.01em] text-white whitespace-nowrap overflow-hidden">
          <span className="truncate">{label || title}</span>
        </div>
      </button>
    );
  }

  return (
    <Rnd
      size={{ width: currentWidth, height: OVERLAY_BLOCK_HEIGHT }}
      position={{ x: currentX, y: OVERLAY_BLOCK_Y }}
      bounds="parent"
      enableResizing={{ left: true, right: true, top: false, bottom: false }}
      dragAxis="x"
      onDragStart={(e, d) => onPreview?.(item._key, d.x, currentWidth)}
      onDrag={(e, d) => onPreview?.(item._key, d.x, currentWidth)}
      onDragStop={(e, d) => {
        onPreviewEnd?.(item._key);
        const newStartMs = laneStart + d.x * msPerPx;
        const newEndMs = newStartMs + durationMs;
        onMoveOrResize?.(new Date(newStartMs), new Date(newEndMs), item);
      }}
      onResize={(e, dir, ref, delta, position) => {
        onPreview?.(item._key, position.x, ref.offsetWidth);
      }}
      onResizeStop={(e, dir, ref, delta, position) => {
        onPreviewEnd?.(item._key);
        const newStartMs = laneStart + position.x * msPerPx;
        const newEndMs = newStartMs + ref.offsetWidth * msPerPx;
        onMoveOrResize?.(new Date(newStartMs), new Date(newEndMs), item);
      }}
      className={`rounded-md ring-1 overflow-hidden cursor-move ${className || 'bg-purple-500/40 ring-purple-300/40'}`}
      style={{ zIndex: selected ? 42 : 38 }}
      onClick={(e) => {
        e.stopPropagation();
        onOpen?.(item);
      }}
      title={`${title} • ${formatTimeLabel(new Date(item.start_time))} – ${formatTimeLabel(new Date(item.end_time))}`}
    >
      <div className="flex h-full items-center px-2 text-[10px] font-semibold tracking-[0.01em] text-white whitespace-nowrap overflow-hidden">
        <span className="truncate">{label || title}</span>
      </div>
    </Rnd>
  );
}

export default function Scheduler({
  device,
  area,
  site,
  onDeviceChange,
  requestedGigId = null,
  openRequestKey = 0,
  onEditDeviceApps = null,
}) {
  const [devices, setDevices] = useState([]);
  const [allGigs, setAllGigs] = useState([]);
  const [gigLaneItems, setGigLaneItems] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [playlistCache, setPlaylistCache] = useState({});
  const [playlistDurations, setPlaylistDurations] = useState({});
  const [eventInstances, setEventInstances] = useState([]);
  const [targetGroups, setTargetGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [overlays, setOverlays] = useState([]);
  const [overlayCache, setOverlayCache] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [announcementCache, setAnnouncementCache] = useState({});
  const [defaultAdOverlays, setDefaultAdOverlays] = useState([]);
  const [apps, setApps] = useState([]);
  const [appProfiles, setAppProfiles] = useState([]);
  const [savedLinks, setSavedLinks] = useState([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkForm, setLinkForm] = useState({ url: '', title: '', profileId: '', showOnPhoneRemote: true });
  const [appsStatus, setAppsStatus] = useState('idle');
  const [appsMessage, setAppsMessage] = useState('');
  const [scheduledOverlays, setScheduledOverlays] = useState([]);
  const [adSeries, setAdSeries] = useState([]);
  const [adSettings, setAdSettings] = useState({ ad_breaks: 0 });
  const [adRotations, setAdRotations] = useState([]);
  const [availableFullScreenAds, setAvailableFullScreenAds] = useState([]);
  const playableFullScreenAds = useMemo(
    () => availableFullScreenAds.filter((ad) => ad.has_artwork !== false),
    [availableFullScreenAds]
  );
  const [selectedAdRotationId, setSelectedAdRotationId] = useState(null);
  const [rotationSaving, setRotationSaving] = useState(false);
  const [rotationModalOpen, setRotationModalOpen] = useState(false);
  const [createRotationForm, setCreateRotationForm] = useState({ name: '', mode: 'custom', duration_sec: 60, showInEpg: false, overlayIds: [] });
  const [rotationForm, setRotationForm] = useState({
    name: '',
    mode: 'custom',
    duration_sec: 60,
    showInEpg: false,
    overlayIds: [],
  });
  const [selectedDeviceId, setSelectedDeviceId] = useState(device?.id || null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adModalOpen, setAdModalOpen] = useState(false);
  const [adEditing, setAdEditing] = useState(null);
  const [overlaySlotPreview, setOverlaySlotPreview] = useState(null);
  const [overlaySlotPreviewLoading, setOverlaySlotPreviewLoading] = useState(false);
  const [manualOverlayModalOpen, setManualOverlayModalOpen] = useState(false);
  const [manualOverlayEditing, setManualOverlayEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('playlist');
  const [overlayTypeFilter, setOverlayTypeFilter] = useState('bottom');
  const [viewMode, setViewMode] = useState('day');
  const [surfaceMode, setSurfaceMode] = useState('timeline');
  const [tickWidth, setTickWidth] = useState(VIEW_PRESETS.day.tickWidth);
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [gigTimelineLoading, setGigTimelineLoading] = useState(false);
  const [gigListLoading, setGigListLoading] = useState(false);
  const [eventsTabFilter, setEventsTabFilter] = useState('all');
  const [gigModalOpen, setGigModalOpen] = useState(false);
  const [selectedGigId, setSelectedGigId] = useState(null);
  const [pendingGigWindow, setPendingGigWindow] = useState(null);
  const [gigRecurringTemplateModalOpen, setGigRecurringTemplateModalOpen] = useState(false);
  const [programmeModalOpen, setProgrammeModalOpen] = useState(false);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [programmeSaving, setProgrammeSaving] = useState(false);
  const [programmeForm, setProgrammeForm] = useState(null);
  const [draftPositions, setDraftPositions] = useState({});
  const [laneDropPreview, setLaneDropPreview] = useState(null);
  const [autoScrolled, setAutoScrolled] = useState(false);
  const [showAds, setShowAds] = useState(true);
  const [showPlaylists, setShowPlaylists] = useState(true);
  const [selectedGigIdsBySeries, setSelectedGigIdsBySeries] = useState({});
  const [selectedAdSeriesId, setSelectedAdSeriesId] = useState(null);
  const [powerSchedules, setPowerSchedules] = useState([]);
  const [powerLoading, setPowerLoading] = useState(false);
  const [powerSaving, setPowerSaving] = useState(false);
  const [powerSelectedDeviceIds, setPowerSelectedDeviceIds] = useState([]);
  const [assistantStatus, setAssistantStatus] = useState({
    loading: false,
    connected: false,
    needsReauth: false,
    lastError: null,
  });
  const [powerEditor, setPowerEditor] = useState({
    enabled: false,
    mode: 'every_day',
    schedule: createEmptyWeeklySchedule(),
    mixed: false,
  });
  const timelineRef = useRef(null);
  const pendingTimelineScrollRef = useRef(null);
  const mixTimersRef = useRef(new Map());
  const lastHandledGigRequestKeyRef = useRef(0);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const activeSiteId = useMemo(() => {
    return (
      site?.id ||
      area?.site_id ||
      area?.siteId ||
      device?.site_id ||
      device?.siteId ||
      null
    );
  }, [site?.id, area?.site_id, area?.siteId, device?.site_id, device?.siteId]);

  const defaultPowerSchedule = useMemo(() => {
    return normalizeWeeklyPowerSchedule(derivePowerScheduleFromSite(site));
  }, [site]);
  const hasSiteOpeningHours = useMemo(
    () => DAY_KEYS.some((dayKey) => defaultPowerSchedule[dayKey]?.enabled),
    [defaultPowerSchedule]
  );

  const powerSchedulesByDevice = useMemo(() => {
    return powerSchedules.reduce((acc, schedule) => {
      acc[schedule.device_id] = schedule;
      return acc;
    }, {});
  }, [powerSchedules]);

  const isDefaultAdOverlay = useCallback((overlay) => {
    return String(overlay?.name || '').startsWith('Default Ads:');
  }, []);

  const activeViewPreset = VIEW_PRESETS[viewMode] || VIEW_PRESETS.day;
  const rangeStart = useMemo(() => resolveViewRangeStart(selectedDate, viewMode), [selectedDate, viewMode]);
  const rangeEnd = useMemo(() => resolveViewRangeEnd(rangeStart, viewMode), [rangeStart, viewMode]);
  const rangeMs = rangeEnd.getTime() - rangeStart.getTime();
  const tickHours = activeViewPreset.tickHours;
  const activeOverlayVisualFilter = ['full', 'top', 'bottom'].includes(overlayTypeFilter)
    ? overlayTypeFilter
    : 'full';
  const siteTimeZone =
    site?.time_zone ||
    site?.timeZone ||
    (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null) ||
    'UTC';
  const compactTimeline = surfaceMode === 'timeline' && viewMode !== 'day' && hasSiteOpeningHours;
  const visibleWindows = useMemo(() => {
    if (!compactTimeline) {
      return [{ start: rangeStart, end: rangeEnd }];
    }
    return normalizeVisibleWindows(
      buildAllowedSegmentsForRange({
        schedule: defaultPowerSchedule,
        enabled: true,
        timeZone: siteTimeZone,
        rangeStart,
        rangeEnd,
      }),
      rangeStart,
      rangeEnd
    );
  }, [compactTimeline, defaultPowerSchedule, rangeEnd, rangeStart, siteTimeZone]);
  const visibleDurationMs = useMemo(
    () => getVisibleDurationMs({ rangeStart, rangeEnd, visibleWindows }),
    [rangeEnd, rangeStart, visibleWindows]
  );
  const tickCount = useMemo(
    () => Math.max(1, Math.ceil(visibleDurationMs / (tickHours * HOUR_MS))),
    [tickHours, visibleDurationMs]
  );
  const laneWidth = useMemo(() => tickCount * tickWidth, [tickCount, tickWidth]);
  const msPerPx = rangeMs / laneWidth;
  const timelineInteractive = surfaceMode === 'timeline' && viewMode === 'day';
  const rangeLabel = useMemo(
    () => formatSchedulerRangeLabel(viewMode, rangeStart, rangeEnd),
    [rangeEnd, rangeStart, viewMode]
  );

  const sortedEvents = useMemo(() => {
    return [...events].sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
  }, [events]);

  const eventsByDevice = useMemo(() => {
    const map = new Map();
    devices.forEach((d) => map.set(d.id, []));
    sortedEvents.forEach((e) => {
      if (!map.has(e.device_id)) map.set(e.device_id, []);
      map.get(e.device_id).push(e);
    });
    return map;
  }, [devices, sortedEvents]);

  const appsByPackage = useMemo(() => {
    const map = {};
    (apps || []).forEach((app) => {
      const pkg = String(app?.package_name || '').trim();
      if (pkg) {
        map[pkg] = app;
      }
    });
    return map;
  }, [apps]);

  const appProfilesByPackage = useMemo(() => {
    const map = {};
    (appProfiles || []).forEach((profile) => {
      const pkg = String(profile?.packageName || '').trim();
      if (pkg) map[pkg] = profile;
    });
    return map;
  }, [appProfiles]);

  const [assignedDestinations, setAssignedDestinations] = useState([]);
  useEffect(() => {
    let active = true;
    setAssignedDestinations([]);
    if (selectedDeviceId) getAssignedDestinations(selectedDeviceId).then((entries) => {
      if (active) setAssignedDestinations(entries || []);
    }).catch(() => { if (active) setAppsMessage('Assigned apps could not be loaded. Refresh to retry.'); });
    return () => { active = false; };
  }, [selectedDeviceId, appsStatus]);

  const enabledApps = useMemo(
    () => (apps || []).filter((app) => Number(app?.enabled) === 1 && Number(app?.launchable) === 1),
    [apps]
  );

  const savedLinksById = useMemo(
    () => Object.fromEntries(savedLinks.map((target) => [Number(target.id), target])),
    [savedLinks]
  );

  const seriesById = useMemo(() => {
    const map = new Map();
    adSeries.forEach((s) => map.set(s.id, s));
    return map;
  }, [adSeries]);

  const visibleAdSeries = useMemo(() => {
    const seriesBySelectionId = new Map();
    adSeries.forEach((series) => {
      const selectionId = getSeriesSelectionId(series) || Number(series.id);
      const current = seriesBySelectionId.get(selectionId);
      const currentIsOwner = current ? !current.sync_owner_series_id : false;
      const seriesIsOwner = !series.sync_owner_series_id;
      if (!current || (!currentIsOwner && seriesIsOwner)) {
        seriesBySelectionId.set(selectionId, series);
      }
    });
    return [...seriesBySelectionId.values()].sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
  }, [adSeries]);

  const syncedTvDevices = useMemo(
    () => devices.filter((entry) => TV_DEVICE_TYPES.has(String(entry.type || '').trim())),
    [devices]
  );

  const adEditingPrimaryDevice = useMemo(
    () => devices.find((entry) => entry.id === adEditing?.deviceId) || null,
    [devices, adEditing?.deviceId]
  );

  const adEditingSyncCandidates = useMemo(
    () => syncedTvDevices.filter((entry) => entry.id !== adEditing?.deviceId),
    [syncedTvDevices, adEditing?.deviceId]
  );

  const adEditingOverlay = useMemo(
    () => overlays.find((entry) => Number(entry.id) === Number(adEditing?.overlayId)) || null,
    [adEditing?.overlayId, overlays]
  );

  const adEditingRequestedSlot = adEditing?.contentType === 'announcement'
    ? 'full'
    : (adEditing?.requestedSlot || adEditingOverlay?.type || 'full');
  const adEditingCompatibleSlots = useMemo(() => {
    if (adEditingRequestedSlot === 'full') return ['full'];
    if (Array.isArray(adEditing?.compatibleSlots) && adEditing.compatibleSlots.length) {
      return adEditing.compatibleSlots;
    }
    return [adEditingRequestedSlot, adEditingRequestedSlot === 'top' ? 'bottom' : 'top'];
  }, [adEditing?.compatibleSlots, adEditingRequestedSlot]);

  const scheduledOverlaysByDevice = useMemo(() => {
    const map = new Map();
    devices.forEach((d) => map.set(d.id, []));
    scheduledOverlays.forEach((o) => {
      if (!map.has(o.device_id)) map.set(o.device_id, []);
      map.get(o.device_id).push(o);
    });
    for (const [deviceId, list] of map.entries()) {
      map.set(
        deviceId,
        [...list].sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      );
    }
    return map;
  }, [devices, scheduledOverlays]);

  const gigsById = useMemo(
    () => new Map((allGigs || []).map((gig) => [Number(gig.id), gig])),
    [allGigs]
  );

  const gigWarningsById = useMemo(
    () =>
      new Map(
        (allGigs || []).map((gig) => [Number(gig.id), buildGigWarnings(gig)])
      ),
    [allGigs]
  );

  const gigLaneRows = useMemo(
    () =>
      (gigLaneItems || []).map((item) => {
        const gigId = item.sourceType === 'gig' ? Number(item.sourceId) : null;
        const fullGig = gigId ? gigsById.get(gigId) : null;
        const warnings = gigId ? (gigWarningsById.get(gigId) || []) : [];
        return {
          ...item,
          performerSummary: fullGig?.performerSummary || item.performerSummary,
          performers: fullGig?.performers || [],
          warnings,
        };
      }),
    [gigLaneItems, gigWarningsById, gigsById]
  );

  const gigSeriesByKey = useMemo(() => {
    const map = new Map();
    (allGigs || []).forEach((gig) => {
      const seriesKey = getRecurringGigSeriesKey(gig);
      if (!seriesKey) return;
      if (!map.has(seriesKey)) {
        map.set(seriesKey, []);
      }
      map.get(seriesKey).push(gig);
    });
    map.forEach((seriesGigs, seriesKey) => {
      map.set(
        seriesKey,
        [...seriesGigs].sort((left, right) => {
          const leftStart = new Date(left?.startsAtUtc || left?.starts_at_utc || 0).getTime();
          const rightStart = new Date(right?.startsAtUtc || right?.starts_at_utc || 0).getTime();
          return leftStart - rightStart || Number(left?.id || 0) - Number(right?.id || 0);
        })
      );
    });
    return map;
  }, [allGigs]);

  const gigOccurrenceStateByGigId = useMemo(() => {
    const map = new Map();
    gigSeriesByKey.forEach((seriesGigs, seriesKey) => {
      seriesGigs.forEach((gig, index) => {
        map.set(Number(gig?.id || 0), {
          seriesKey,
          gigs: seriesGigs,
          currentIndex: index,
          hasPrevious: index > 0,
          hasNext: index < seriesGigs.length - 1,
          previousGig: seriesGigs[index - 1] || null,
          nextGig: seriesGigs[index + 1] || null,
          isRecurringSeries: seriesGigs.length > 1 && isRecurringGig(gig),
        });
      });
    });
    return map;
  }, [gigSeriesByKey]);

  const upcomingEventSeriesItems = useMemo(
    () => buildUpcomingGigSeriesItems(allGigs, eventsTabFilter, new Date(), selectedGigIdsBySeries),
    [allGigs, eventsTabFilter, selectedGigIdsBySeries]
  );

  const schedulerListItems = useMemo(
    () =>
      buildSchedulerListItems({
        gigs: allGigs,
        events,
        scheduledOverlays,
        devices,
        playlists,
        playlistCache,
        overlays,
        overlayCache,
        announcements,
        announcementCache,
        appsByPackage,
        rangeStart,
        rangeEnd,
      }),
    [
      allGigs,
      announcementCache,
      announcements,
      appsByPackage,
      devices,
      events,
      overlayCache,
      overlays,
      playlistCache,
      playlists,
      rangeEnd,
      rangeStart,
      scheduledOverlays,
    ]
  );

  const resolveTimezone = useCallback(
    (deviceId) => devices.find((d) => d.id === deviceId)?.time_zone || 'UTC',
    [devices]
  );

  const updatePreviewPosition = useCallback((id, x, width) => {
    setDraftPositions((prev) => {
      if (prev[id] && prev[id].x === x && prev[id].width === width) return prev;
      return { ...prev, [id]: { x, width } };
    });
  }, []);

  const clearPreviewPosition = useCallback((id) => {
    setDraftPositions((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const clearLaneDropPreview = useCallback((deviceId = null) => {
    setLaneDropPreview((prev) => {
      if (!prev) return prev;
      if (deviceId != null && prev.deviceId !== deviceId) return prev;
      return null;
    });
  }, []);

  const resolveLaneDropPlacement = useCallback((clientX, currentTarget) => {
    const rect = currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(laneWidth, clientX - rect.left));
    const start = new Date(rangeStart.getTime() + x * msPerPx);
    return { x, start };
  }, [laneWidth, msPerPx, rangeStart]);

  const fetchDevices = useCallback(async () => {
    if (!area?.id) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/areas/${area.id}/devices`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load devices');
      const data = await res.json();
      const deviceList = data.devices || [];
      setDevices(deviceList);
      if (!selectedDeviceId && deviceList.length) {
        setSelectedDeviceId(deviceList[0].id);
        onDeviceChange?.(deviceList[0].id);
      }
    } catch (err) {
      console.error('[Scheduler] Error loading devices', err);
    } finally {
      setLoading(false);
    }
  }, [area?.id, token, onDeviceChange, selectedDeviceId]);

  const fetchTargetGroups = useCallback(async () => {
    if (!area?.id || !token) {
      setTargetGroups([]);
      return;
    }
    try {
      const response = await fetch(`/api/areas/${area.id}/target-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to load target groups');
      setTargetGroups(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      console.error('[Scheduler] Error loading target groups', error);
      setTargetGroups([]);
    }
  }, [area?.id, token]);

  const fetchPlaylists = useCallback(async () => {
    const siteId =
      site?.id ||
      area?.site_id ||
      area?.siteId ||
      device?.site_id ||
      device?.siteId ||
      null;
    const params = new URLSearchParams();
    if (siteId) params.set('siteId', siteId);
    if (area?.id) params.set('areaId', area.id);
    const qs = params.toString() ? `?${params.toString()}` : '';
    try {
      const res = await fetch(`/api/playlists${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load playlists');
      const data = await res.json();
      const pls = data.playlists || [];
      setPlaylists(pls);
      setPlaylistCache((prev) => {
        const next = { ...prev };
        pls.forEach((p) => {
          next[p.id] = p;
        });
        return next;
      });
      setPlaylistDurations((prev) => {
        const next = { ...prev };
        pls.forEach((p) => {
          const items = Array.isArray(p.items) ? p.items : [];
          const totalMs =
            items.reduce((sum, item) => {
              const durSec = Number(item.duration);
              const fallbackSec = 180;
              return sum + (Number.isFinite(durSec) && durSec > 0 ? durSec : fallbackSec) * 1000;
            }, 0) || 30 * 60 * 1000;
          next[p.id] = totalMs;
        });
        return next;
      });
    } catch (err) {
      console.error('[Scheduler] Error loading playlists', err);
    }
  }, [site?.id, area?.id, area?.site_id, area?.siteId, device?.site_id, device?.siteId, token]);

  const fetchEventInstances = useCallback(async () => {
    if (!activeSiteId) return;
    try {
      const res = await schedulerFetch(`/api/event-instances?siteId=${encodeURIComponent(activeSiteId)}`);
      setEventInstances(Array.isArray(res.items) ? res.items : []);
    } catch (err) {
      console.error('[Scheduler] Error loading event instances', err);
      setEventInstances([]);
    }
  }, [activeSiteId]);

  const fetchOverlays = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/overlays', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load overlays');
      const data = await res.json();
      const list = Array.isArray(data.overlays) ? data.overlays : [];
      setOverlays(list);
      setOverlayCache((prev) => {
        const next = { ...prev };
        list.forEach((o) => {
          next[o.id] = o;
        });
        return next;
      });
    } catch (err) {
      console.error('[Scheduler] Error loading overlays', err);
    }
  }, [token]);

  const fetchAnnouncements = useCallback(async () => {
    if (!token) {
      setAnnouncements([]);
      setAnnouncementCache({});
      return;
    }
    try {
      const res = await fetch('/api/announcements', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load announcements');
      const data = await res.json();
      const list = (Array.isArray(data) ? data : []).filter((item) => item && item.s3_url);
      setAnnouncements(list);
      setAnnouncementCache(
        list.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {})
      );
    } catch (err) {
      console.error('[Scheduler] Error loading announcements', err);
      setAnnouncements([]);
      setAnnouncementCache({});
    }
  }, [token]);

  const fetchApps = useCallback(async () => {
    if (!token || !selectedDeviceId) {
      setApps([]);
      setAppProfiles([]);
      return;
    }
    setAppsStatus('loading');
    setAppsMessage('');
    try {
      const res = await fetch(`/api/devices/get-installed-apps?deviceId=${selectedDeviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      const data = await res.json().catch(() => ({}));
      if (res.status !== 304 && (!res.ok || !data.success)) {
        setApps([]);
        setAppProfiles([]);
        setAppsStatus('error');
        setAppsMessage(data.message || 'Failed to load apps');
        return;
      }
      if (res.status !== 304) {
        setApps(Array.isArray(data.apps) ? data.apps : []);
      }
      try {
        const [profiles, targets] = await Promise.all([
          listExternalAppProfiles(selectedDeviceId, { cacheBust: true }),
          listExternalAppContentTargets(selectedDeviceId, { cacheBust: true }),
        ]);
        setAppProfiles(profiles);
        setSavedLinks(targets);
      } catch (profileError) {
        console.warn('[Scheduler] Failed to load external app launch recipes', profileError);
        setAppProfiles([]);
        setSavedLinks([]);
      }
      setAppsStatus('idle');
    } catch (err) {
      console.error('[Scheduler] Failed to fetch apps', err);
      setApps([]);
      setAppProfiles([]);
      setSavedLinks([]);
      setAppsStatus('error');
      setAppsMessage('Failed to load apps');
    }
  }, [selectedDeviceId, token]);

  const fetchDefaultAds = useCallback(async () => {
    if (!token || !activeSiteId) {
      setDefaultAdOverlays([]);
      return;
    }
    try {
      const res = await fetch(
        `/api/default-ads/overlays?siteId=${encodeURIComponent(activeSiteId)}&activeOnly=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to load default ads');
      const data = await res.json();
      const list = Array.isArray(data.overlays) ? data.overlays : [];
      setDefaultAdOverlays(list);
      setOverlayCache((prev) => {
        const next = { ...prev };
        list.forEach((o) => {
          next[o.id] = o;
        });
        return next;
      });
    } catch (err) {
      console.error('[Scheduler] Error loading default ads', err);
      setDefaultAdOverlays([]);
    }
  }, [activeSiteId, token]);

  const fetchGigLane = useCallback(async () => {
    if (!activeSiteId) {
      setGigLaneItems([]);
      return;
    }
    try {
      setGigTimelineLoading(true);
      const params = new URLSearchParams({
        siteId: String(activeSiteId),
        from: rangeStart.toISOString(),
        to: rangeEnd.toISOString(),
      });
      const data = await appFetch(`/api/venue-programmes/timeline?${params}`);
      setGigLaneItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      console.error('[Scheduler] Failed to fetch gig timeline', err);
      setGigLaneItems([]);
    } finally {
      setGigTimelineLoading(false);
    }
  }, [activeSiteId, rangeEnd, rangeStart]);

  const fetchGigs = useCallback(async () => {
    if (!activeSiteId) {
      setAllGigs([]);
      return;
    }
    try {
      setGigListLoading(true);
      const items = await fetchGigItems({ siteId: activeSiteId });
      setAllGigs(items);
    } catch (err) {
      console.error('[Scheduler] Failed to fetch gigs', err);
      setAllGigs([]);
    } finally {
      setGigListLoading(false);
    }
  }, [activeSiteId]);

  const fetchScheduled = useCallback(
    async (deviceIds) => {
      if (!deviceIds.length) return;
      try {
        const start = rangeStart;
        const end = rangeEnd;
        const results = await Promise.all(
          deviceIds.map((id) =>
            schedulerFetch(
              `/api/scheduled-items?deviceId=${id}&start=${start.toISOString()}&end=${end.toISOString()}`
            ).catch((err) => {
              console.error(`[Scheduler] Failed to fetch items for device ${id}`, err);
              return { items: [] };
            })
          )
        );
        const merged = results.flatMap((r, idx) =>
          (r.items || []).map((item) => ({
            ...item,
            device_id: item.device_id || deviceIds[idx],
          }))
        );
        setEvents(merged);
      } catch (err) {
        console.error('[Scheduler] Error fetching scheduled items', err);
      }
    },
    [rangeStart, rangeEnd]
  );

  const fetchScheduledOverlays = useCallback(
    async (deviceIds) => {
      if (!deviceIds.length) return;
      try {
        const start = rangeStart;
        const end = rangeEnd;
        const results = await Promise.all(
          deviceIds.map((id) =>
            schedulerFetch(
              `/api/scheduled-overlays?deviceId=${id}&start=${start.toISOString()}&end=${end.toISOString()}`
            ).catch((err) => {
              console.error(`[Scheduler] Failed to fetch overlays for device ${id}`, err);
              return { items: [] };
            })
          )
        );
        const merged = results.flatMap((r, idx) =>
          (r.items || []).map((item) => ({
            ...item,
            device_id: item.device_id || deviceIds[idx],
            _key: `overlay:${item.id}`,
          }))
        );
        setScheduledOverlays(merged);
      } catch (err) {
        console.error('[Scheduler] Error fetching scheduled overlays', err);
      }
    },
    [rangeStart, rangeEnd]
  );

  const fetchAdSeriesForDevices = useCallback(async (deviceIds, focusDeviceId = null) => {
    if (!Array.isArray(deviceIds) || !deviceIds.length) {
      setAdSeries([]);
      setAdSettings({ ad_breaks: 0 });
      return;
    }
    try {
      const results = await Promise.all(
        deviceIds.map(async (deviceId) => {
          const res = await schedulerFetch(`/api/scheduled-ad-series?deviceId=${deviceId}`).catch((err) => {
            console.error(`[Scheduler] Failed to fetch ad series for device ${deviceId}`, err);
            return { series: [], settings: null };
          });
          return { deviceId, res };
        })
      );
      const merged = results
        .flatMap(({ res }) => (Array.isArray(res.series) ? res.series : []))
        .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
      setAdSeries(merged);
      const settingsDeviceId = focusDeviceId || selectedDeviceId || deviceIds[0];
      const settingsResult =
        results.find(({ deviceId }) => String(deviceId) === String(settingsDeviceId)) || results[0];
      setAdSettings(settingsResult?.res?.settings || { ad_breaks: 0 });
    } catch (err) {
      console.error('[Scheduler] Failed to fetch ad series', err);
      setAdSeries([]);
      setAdSettings({ ad_breaks: 0 });
    }
  }, [selectedDeviceId]);

  const fetchAdRotations = useCallback(async () => {
    if (!activeSiteId) {
      setAdRotations([]);
      setAvailableFullScreenAds([]);
      return;
    }
    try {
      const res = await schedulerFetch(`/api/ad-rotations?siteId=${encodeURIComponent(activeSiteId)}`);
      const rotations = Array.isArray(res.rotations) ? res.rotations : [];
      setAdRotations(rotations);
      setAvailableFullScreenAds(Array.isArray(res.availableAds) ? res.availableAds : []);
      setPlaylistCache((prev) => {
        const next = { ...prev };
        rotations.forEach((rotation) => {
          next[rotation.playlist_id || rotation.id] = {
            id: rotation.playlist_id || rotation.id,
            name: rotation.name,
            media_type: 'video',
            is_ad_rotation: true,
          };
        });
        return next;
      });
      setPlaylistDurations((prev) => {
        const next = { ...prev };
        rotations.forEach((rotation) => {
          const id = rotation.playlist_id || rotation.id;
          next[id] = Math.max(
            MIN_EVENT_MS,
            (Number(rotation.duration_sec) || 60) * Math.max(1, Number(rotation.item_count) || 1) * 1000
          );
        });
        return next;
      });
      if (!selectedAdRotationId && rotations.length) {
        setSelectedAdRotationId(rotations[0].id);
      }
    } catch (err) {
      console.error('[Scheduler] Failed to fetch ad rotations', err);
      setAdRotations([]);
      setAvailableFullScreenAds([]);
    }
  }, [activeSiteId, selectedAdRotationId, defaultAdOverlays.length]);

  const fetchPowerSchedules = useCallback(async (deviceIds) => {
    if (!deviceIds.length) {
      setPowerSchedules([]);
      return;
    }
    try {
      setPowerLoading(true);
      const res = await schedulerFetch(`/api/power-schedules?deviceIds=${deviceIds.join(',')}`);
      setPowerSchedules(Array.isArray(res.schedules) ? res.schedules : []);
    } catch (err) {
      console.error('[Scheduler] Failed to fetch power schedules', err);
      setPowerSchedules([]);
    } finally {
      setPowerLoading(false);
    }
  }, []);

  const snapToPrevious = useCallback(
    (deviceId, startDate, durationMs, ignoreId = null) => {
      const same = (eventsByDevice.get(deviceId) || []).filter((e) => e.id !== ignoreId);
      const prior = same
        .map((ev) => ({
          start: new Date(ev.start_time),
          end: new Date(ev.end_time),
        }))
        .filter((ev) => ev.end <= startDate)
        .sort((a, b) => b.end - a.end)[0];
      const snappedStart =
        prior && Math.abs(startDate.getTime() - prior.end.getTime()) <= SNAP_THRESHOLD_MS
          ? prior.end
          : startDate;
      const snappedEnd = new Date(snappedStart.getTime() + Math.max(durationMs, MIN_EVENT_MS));
      return { start: snappedStart, end: snappedEnd };
    },
    [eventsByDevice]
  );

  const allPowerDevicesSelected = devices.length > 0 && powerSelectedDeviceIds.length === devices.length;

  const getDeviceTimeZone = useCallback(
    (deviceId) => devices.find((entry) => entry.id === deviceId)?.time_zone || 'UTC',
    [devices]
  );

  const getAdDaySegments = useCallback(
    (deviceId, scheduleDate) => {
      if (!scheduleDate) return [];
      const dayStart = parseDateTimeLocal(`${scheduleDate}T00:00`);
      const dayEnd = addDays(dayStart, 1);
      const scheduleRow = powerSchedulesByDevice[deviceId];
      if (!scheduleRow?.enabled) {
        return [{ start: dayStart, end: dayEnd }];
      }
      return buildAllowedSegmentsForRange({
        schedule: scheduleRow.schedule,
        enabled: true,
        timeZone: getDeviceTimeZone(deviceId),
        rangeStart: dayStart,
        rangeEnd: dayEnd,
      }).filter((segment) => segment.end > dayStart && segment.start < dayEnd);
    },
    [getDeviceTimeZone, powerSchedulesByDevice]
  );

  const constrainAdTimeRange = useCallback(
    ({ deviceId, scheduleDate, startTimeLocal, endTimeLocal }) => {
      const normalizedStartTime = normalizeTimeString(startTimeLocal) || '00:00';
      const normalizedEndTime = normalizeTimeString(endTimeLocal) || normalizedStartTime;
      const segments = getAdDaySegments(deviceId, scheduleDate);
      if (!segments.length) {
        return {
          startTimeLocal: normalizedStartTime,
          endTimeLocal: normalizedEndTime,
          segment: null,
          hasPowerWindow: false,
        };
      }

      const desiredStart = parseDateAndTimeLocal(scheduleDate, normalizedStartTime);
      const desiredEnd = parseDateAndTimeLocal(scheduleDate, normalizedEndTime);
      const segment =
        segments.find((entry) => desiredStart >= entry.start && desiredStart < entry.end) ||
        segments.find((entry) => entry.end > desiredStart) ||
        segments[0];

      let safeStart = desiredStart < segment.start ? segment.start : desiredStart;
      if (safeStart >= segment.end) {
        safeStart = segment.start;
      }

      let safeEnd = desiredEnd > safeStart ? desiredEnd : new Date(safeStart.getTime() + 60 * 1000);
      if (safeEnd > segment.end) {
        safeEnd = segment.end;
      }
      if (safeEnd <= safeStart) {
        safeEnd = segment.end;
      }

      return {
        startTimeLocal: formatTimeInputValue(safeStart),
        endTimeLocal: formatTimeInputValue(safeEnd),
        segment,
        hasPowerWindow: true,
      };
    },
    [getAdDaySegments]
  );

  const buildAdEditingState = useCallback(
    ({
      id = null,
      deviceId,
      contentType,
      overlayId = '',
      announcementId = '',
      startDate = new Date(),
      repeatMode = 'once',
      allDay = false,
      startTimeLocal = null,
      endTimeLocal = null,
      impressionsPerHour = 6,
      durationSec = null,
      playContinuously = false,
      syncDeviceIds = [],
      holdVisualSeconds = 0,
      adTransparency = 60,
      playbackMode = 'auto',
      requestedSlot = null,
      compatibleSlots = null,
      priorityValue = null,
      overridePolicy = 'never',
    }) => {
      const normalizedStart = startDate instanceof Date ? startDate : new Date(startDate);
      const scheduleDate = formatDateInputValue(normalizedStart);
      const constrained = constrainAdTimeRange({
        deviceId,
        scheduleDate,
        startTimeLocal: startTimeLocal || formatTimeInputValue(normalizedStart),
        endTimeLocal:
          endTimeLocal ||
          formatTimeInputValue(new Date(normalizedStart.getTime() + DEFAULT_AD_WINDOW_MINUTES * 60 * 1000)),
      });
      return {
        id,
        deviceId,
        contentType,
        overlayId,
        announcementId,
        scheduleDate,
        repeatMode: repeatMode === 'daily' ? 'daily' : 'once',
        allDay: !!allDay,
        startTimeLocal: constrained.startTimeLocal,
        endTimeLocal: constrained.endTimeLocal,
        impressions_per_hour: Number(impressionsPerHour) || 6,
        duration_sec:
          Number(durationSec) ||
          (contentType === 'announcement' ? 30 : 60),
        play_continuously: contentType === 'announcement' ? false : !!playContinuously,
        syncDeviceIds: Array.isArray(syncDeviceIds) ? syncDeviceIds : [],
        hold_visual_seconds:
          contentType === 'announcement' ? Math.max(0, Number(holdVisualSeconds) || 0) : 0,
        ad_transparency: contentType === 'overlay' ? clampAdTransparency(adTransparency) : 60,
        playbackMode: contentType === 'overlay' ? normalizeAdPlaybackMode(playbackMode) : 'visual_only',
        requestedSlot: requestedSlot || null,
        compatibleSlots: Array.isArray(compatibleSlots) ? compatibleSlots : null,
        priorityValue: Number(priorityValue) || (contentType === 'announcement' ? 80 : 40),
        overridePolicy: overridePolicy === 'explicit' ? 'explicit' : 'never',
      };
    },
    [constrainAdTimeRange]
  );

  const adEditingPowerWindow = useMemo(() => {
    if (!adEditing?.deviceId || !adEditing?.scheduleDate) return null;
    const constrained = constrainAdTimeRange({
      deviceId: adEditing.deviceId,
      scheduleDate: adEditing.scheduleDate,
      startTimeLocal: adEditing.startTimeLocal || '00:00',
      endTimeLocal: adEditing.endTimeLocal || '23:59',
    });
    return constrained.segment
      ? {
          startTimeLocal: formatTimeInputValue(constrained.segment.start),
          endTimeLocal: formatTimeInputValue(constrained.segment.end),
          hasPowerWindow: constrained.hasPowerWindow,
        }
      : {
          startTimeLocal: null,
          endTimeLocal: null,
          hasPowerWindow: false,
        };
  }, [
    adEditing?.deviceId,
    adEditing?.endTimeLocal,
    adEditing?.scheduleDate,
    adEditing?.startTimeLocal,
    constrainAdTimeRange,
  ]);

  const isRangeAllowedForDevice = useCallback(
    (deviceId, start, end) => {
      const scheduleRow = powerSchedulesByDevice[deviceId];
      if (!scheduleRow?.enabled) return true;
      return isRangeAllowedByPowerSchedule({
        schedule: scheduleRow.schedule,
        enabled: true,
        timeZone: getDeviceTimeZone(deviceId),
        start,
        end,
      });
    },
    [getDeviceTimeZone, powerSchedulesByDevice]
  );

  const validateRangeAcrossDevices = useCallback(
    (deviceIds, start, end) => {
      for (const deviceId of deviceIds || []) {
        if (!isRangeAllowedForDevice(deviceId, start, end)) {
          const deviceName = devices.find((entry) => entry.id === deviceId)?.name || `Device ${deviceId}`;
          return {
            ok: false,
            message: `${deviceName} is outside its allowed power hours for that time.`,
          };
        }
      }
      return { ok: true };
    },
    [devices, isRangeAllowedForDevice]
  );

  const powerVisualsByDevice = useMemo(() => {
    return devices.reduce((acc, entry) => {
      const scheduleRow = powerSchedulesByDevice[entry.id];
      if (!scheduleRow?.enabled) {
        acc[entry.id] = { closedSegments: [], markers: [] };
        return acc;
      }
      acc[entry.id] = {
        closedSegments: buildClosedSegmentsForRange({
          schedule: scheduleRow.schedule,
          enabled: true,
          timeZone: entry.time_zone || 'UTC',
          rangeStart,
          rangeEnd,
        }),
        markers: buildPowerMarkersForRange({
          schedule: scheduleRow.schedule,
          enabled: true,
          timeZone: entry.time_zone || 'UTC',
          rangeStart,
          rangeEnd,
        }),
      };
      return acc;
    }, {});
  }, [devices, powerSchedulesByDevice, rangeEnd, rangeStart]);

  const setPowerMode = useCallback((mode) => {
    setPowerEditor((prev) => {
      if (mode === prev.mode) return prev;
      if (mode === 'every_day') {
        const templateDay =
          DAY_KEYS.map((dayKey) => prev.schedule[dayKey]).find((day) => day?.enabled) ||
          prev.schedule.monday ||
          { enabled: true, on: '09:00', off: '17:00' };
        const nextSchedule = DAY_KEYS.reduce((acc, dayKey) => {
          acc[dayKey] = {
            enabled: true,
            on: templateDay.on || '09:00',
            off: templateDay.off || '17:00',
          };
          return acc;
        }, {});
        return { ...prev, mode, schedule: nextSchedule, mixed: false };
      }
      return { ...prev, mode, mixed: false };
    });
  }, []);

  const updateEveryDayField = useCallback((field, value) => {
    setPowerEditor((prev) => {
      const nextSchedule = DAY_KEYS.reduce((acc, dayKey) => {
        acc[dayKey] = {
          ...prev.schedule[dayKey],
          enabled: true,
          [field]: value,
        };
        return acc;
      }, {});
      return { ...prev, schedule: nextSchedule, mixed: false };
    });
  }, []);

  const updatePerDaySchedule = useCallback((dayKey, patch) => {
    setPowerEditor((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [dayKey]: {
          ...prev.schedule[dayKey],
          ...patch,
        },
      },
      mixed: false,
    }));
  }, []);

  const savePowerSchedule = useCallback(async () => {
    if (!powerSelectedDeviceIds.length) {
      alert('Select at least one device.');
      return;
    }
    try {
      setPowerSaving(true);
      const schedule = normalizeWeeklyPowerSchedule(powerEditor.schedule);
      await schedulerFetch('/api/power-schedules', {
        method: 'PUT',
        body: JSON.stringify({
          deviceIds: powerSelectedDeviceIds,
          enabled: powerEditor.enabled ? 1 : 0,
          mode: powerEditor.mode,
          schedule,
        }),
      });
      await fetchPowerSchedules(devices.map((entry) => entry.id));
      setMessage('Power schedule saved');
    } catch (err) {
      console.error('[Scheduler] Failed to save power schedule', err);
      alert(err.message || 'Could not save power schedule.');
    } finally {
      setPowerSaving(false);
    }
  }, [devices, fetchPowerSchedules, powerEditor.enabled, powerEditor.mode, powerEditor.schedule, powerSelectedDeviceIds]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    fetchTargetGroups();
  }, [fetchTargetGroups]);

  useEffect(() => {
    const handleDragEnd = () => {
      setLaneDropPreview(null);
    };
    window.addEventListener('dragend', handleDragEnd);
    return () => window.removeEventListener('dragend', handleDragEnd);
  }, []);

  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  useEffect(() => {
    fetchEventInstances();
  }, [fetchEventInstances]);

  useEffect(() => {
    fetchOverlays();
  }, [fetchOverlays]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  useEffect(() => {
    fetchDefaultAds();
  }, [fetchDefaultAds]);

  useEffect(() => {
    fetchGigLane();
  }, [fetchGigLane]);

  useEffect(() => {
    fetchGigs();
  }, [fetchGigs]);

  useEffect(() => {
    let cancelled = false;

    if (!token || !activeSiteId) {
      setAssistantStatus({
        loading: false,
        connected: false,
        needsReauth: false,
        lastError: null,
      });
      return undefined;
    }

    setAssistantStatus((prev) => ({
      ...prev,
      loading: true,
    }));

    fetch(`/api/googlehome/status?site=${encodeURIComponent(activeSiteId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Failed to load Google Assistant status');
        }
        if (cancelled) return;
        setAssistantStatus({
          loading: false,
          connected: !!data.google_assistant,
          needsReauth: !!data.google_assistant_needs_reauth,
          lastError: data.google_assistant_last_error || null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[Scheduler] Failed to load Google Assistant status', err);
        setAssistantStatus({
          loading: false,
          connected: false,
          needsReauth: false,
          lastError: null,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [activeSiteId, token]);

  useEffect(() => {
    if (devices.length) {
      fetchScheduled(devices.map((d) => d.id));
    }
  }, [devices, fetchScheduled]);

  useEffect(() => {
    if (devices.length) {
      fetchScheduledOverlays(devices.map((d) => d.id));
    }
  }, [devices, fetchScheduledOverlays]);

  useEffect(() => {
    if (devices.length) {
      fetchAdSeriesForDevices(devices.map((d) => d.id), selectedDeviceId);
    }
  }, [devices, selectedDeviceId, fetchAdSeriesForDevices]);

  useEffect(() => {
    fetchAdRotations();
  }, [fetchAdRotations]);

  useEffect(() => {
    if (devices.length) {
      fetchPowerSchedules(devices.map((d) => d.id));
    } else {
      setPowerSchedules([]);
    }
  }, [devices, fetchPowerSchedules]);

  useEffect(() => {
    const availableIds = new Set(devices.map((entry) => String(entry.id)));
    const preferredId =
      (device?.id && availableIds.has(String(device.id)) ? device.id : null) ||
      (selectedDeviceId && availableIds.has(String(selectedDeviceId)) ? selectedDeviceId : null) ||
      devices[0]?.id ||
      null;

    if (String(preferredId || '') === String(selectedDeviceId || '')) return;
    setSelectedDeviceId(preferredId);
  }, [device?.id, devices, selectedDeviceId]);

  const handleSaveLink = async () => {
    const profileId = Number(linkForm.profileId);
    if (!profileId || !linkForm.url.trim()) return;
    try {
      setLinkSaving(true);
      setAppsMessage('');
      const media = await previewExternalMediaLink(linkForm.url.trim());
      const result = await saveExternalMediaLinkTarget(profileId, {
        url: media.canonicalUrl,
        title: linkForm.title.trim() || media.title,
        showOnPhoneRemote: linkForm.showOnPhoneRemote,
      });
      await fetchApps();
      setLinkModalOpen(false);
      setLinkForm({ url: '', title: '', profileId: '', showOnPhoneRemote: true });
      setAppsMessage(result.created ? 'Link saved.' : 'That link was already saved.');
    } catch (error) {
      setAppsMessage(error.response?.data?.error || error.message || 'Failed to save link.');
    } finally {
      setLinkSaving(false);
    }
  };

  useEffect(() => {
    const availableIds = new Set(devices.map((entry) => entry.id));
    setPowerSelectedDeviceIds((prev) => {
      const filtered = prev.filter((id) => availableIds.has(id));
      if (filtered.length) return filtered;
      return devices.map((entry) => entry.id);
    });
  }, [devices]);

  useEffect(() => {
    const selectedIds = powerSelectedDeviceIds.filter((id) => powerSchedulesByDevice[id] || devices.some((d) => d.id === id));
    if (!selectedIds.length) {
      setPowerEditor({
        enabled: false,
        mode: detectPowerScheduleMode(defaultPowerSchedule),
        schedule: normalizeWeeklyPowerSchedule(defaultPowerSchedule),
        mixed: false,
      });
      return;
    }

    const selectedSchedules = selectedIds
      .map((id) => powerSchedulesByDevice[id] || null)
      .filter(Boolean);

    if (!selectedSchedules.length) {
      setPowerEditor({
        enabled: false,
        mode: detectPowerScheduleMode(defaultPowerSchedule),
        schedule: normalizeWeeklyPowerSchedule(defaultPowerSchedule),
        mixed: false,
      });
      return;
    }

    const first = selectedSchedules[0];
    const serializedFirst = JSON.stringify({
      enabled: !!first.enabled,
      mode: first.mode || detectPowerScheduleMode(first.schedule),
      schedule: normalizeWeeklyPowerSchedule(first.schedule),
    });
    const mixed = selectedSchedules.some((entry) => {
      return (
        JSON.stringify({
          enabled: !!entry.enabled,
          mode: entry.mode || detectPowerScheduleMode(entry.schedule),
          schedule: normalizeWeeklyPowerSchedule(entry.schedule),
        }) !== serializedFirst
      );
    });

    setPowerEditor({
      enabled: !!first.enabled,
      mode: first.mode || detectPowerScheduleMode(first.schedule),
      schedule: normalizeWeeklyPowerSchedule(first.schedule),
      mixed,
    });
  }, [defaultPowerSchedule, devices, powerSchedulesByDevice, powerSelectedDeviceIds]);

  const focusSchedulerDate = useCallback((value) => {
    const targetDate = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(targetDate.getTime())) return;

    const previewRangeStart = resolveViewRangeStart(targetDate, viewMode);
    const previewRangeEnd = resolveViewRangeEnd(previewRangeStart, viewMode);
    const previewVisibleWindows = compactTimeline
      ? normalizeVisibleWindows(
          buildAllowedSegmentsForRange({
            schedule: defaultPowerSchedule,
            enabled: true,
            timeZone: siteTimeZone,
            rangeStart: previewRangeStart,
            rangeEnd: previewRangeEnd,
          }),
          previewRangeStart,
          previewRangeEnd
        )
      : [{ start: previewRangeStart, end: previewRangeEnd }];
    const previewVisibleDurationMs = getVisibleDurationMs({
      rangeStart: previewRangeStart,
      rangeEnd: previewRangeEnd,
      visibleWindows: previewVisibleWindows,
    });
    const previewTickCount = Math.max(
      1,
      Math.ceil(previewVisibleDurationMs / (tickHours * HOUR_MS))
    );
    const previewLaneWidth = previewTickCount * tickWidth;
    const targetX = compactTimeline
      ? projectTimeToLaneX({
          time: targetDate,
          rangeStart: previewRangeStart,
          rangeEnd: previewRangeEnd,
          laneWidth: previewLaneWidth,
          visibleWindows: previewVisibleWindows,
        })
      : (targetDate.getTime() - previewRangeStart.getTime()) / ((previewRangeEnd.getTime() - previewRangeStart.getTime()) / previewLaneWidth);

    pendingTimelineScrollRef.current = targetX == null ? null : Math.max(0, (targetX || 0) - 200);
    setSelectedDate(targetDate);
  }, [compactTimeline, defaultPowerSchedule, siteTimeZone, tickHours, tickWidth, viewMode]);

  const goToNow = useCallback(() => {
    focusSchedulerDate(new Date());
  }, [focusSchedulerDate]);

  useEffect(() => {
    if (autoScrolled) return;
    goToNow();
    setAutoScrolled(true);
  }, [goToNow, autoScrolled]);

  useEffect(() => {
    if (!timelineRef.current) return;

    if (pendingTimelineScrollRef.current != null) {
      timelineRef.current.scrollLeft = pendingTimelineScrollRef.current;
      pendingTimelineScrollRef.current = null;
      return;
    }

    const now = new Date();
    if (now < rangeStart || now > rangeEnd) return;

    const xPosition = compactTimeline
      ? projectTimeToLaneX({
          time: now,
          rangeStart,
          rangeEnd,
          laneWidth,
          visibleWindows,
        })
      : (now.getTime() - rangeStart.getTime()) / msPerPx;
    if (xPosition == null) return;
    timelineRef.current.scrollLeft = Math.max(0, xPosition - 200);
  }, [compactTimeline, laneWidth, msPerPx, rangeEnd, rangeStart, visibleWindows]);

  const setTimelineZoom = useCallback(
    (nextTickWidth, anchorClientX = null) => {
      const safeTickWidth = clampTickWidth(nextTickWidth);
      if (safeTickWidth === tickWidth) return;
      const scroller = timelineRef.current;
      const rect = scroller?.getBoundingClientRect?.();
      const anchorOffset = rect
        ? Math.max(
            0,
            Math.min(
              rect.width,
              (anchorClientX == null ? rect.left + rect.width / 2 : anchorClientX) - rect.left
            )
          )
        : 0;
      const oldLaneWidth = laneWidth;
      const oldScrollLeft = scroller?.scrollLeft || 0;
      const anchorRatio = oldLaneWidth > 0 ? (oldScrollLeft + anchorOffset) / oldLaneWidth : 0;
      setTickWidth(safeTickWidth);
      requestAnimationFrame(() => {
        if (!timelineRef.current) return;
        const nextLaneWidth = tickCount * safeTickWidth;
        timelineRef.current.scrollLeft = Math.max(0, anchorRatio * nextLaneWidth - anchorOffset);
      });
    },
    [laneWidth, tickCount, tickWidth]
  );

  const upsertLocal = (items) => {
    setEvents((prev) => {
      const map = new Map(prev.map((p) => [p.id, p]));
      items.forEach((i) => map.set(i.id, i));
      return Array.from(map.values());
    });
  };

  const clampAndMix = async (deviceId, payload) => {
    const now = Date.now();
    const startMs = new Date(payload.start_time).getTime();
    const endMs = new Date(payload.end_time).getTime();
    // Clear any pending timer for this event
    const timers = mixTimersRef.current;
    if (payload.id && timers.has(payload.id)) {
      clearTimeout(timers.get(payload.id));
      timers.delete(payload.id);
    }

    if (startMs <= now && endMs > now) {
      try {
        const scanId = createScanId(deviceId);
        debugPlaybackLog('Scheduler UI triggering scan-load-and-play (current slot)', {
          deviceId,
          scheduledItemId: payload.id || null,
          start_time: payload.start_time,
          end_time: payload.end_time,
          scanId,
        });
        await schedulerFetch('/api/scan-load-and-play', {
          method: 'POST',
          body: JSON.stringify({
            deviceId,
            force: true,
            mixNow: true,
            trigger: 'webserver:scheduler-current-slot',
            scanId,
          }),
        });
        setMessage('Mix Now triggered for current slot');
      } catch (err) {
        console.error('Failed to trigger Mix Now', err);
      }
    } else if (endMs > now && payload.id) {
      const delay = endMs - now;
      debugPlaybackLog('Scheduler UI scheduling scan-load-and-play timer', {
        deviceId,
        scheduledItemId: payload.id,
        delayMs: delay,
        end_time: payload.end_time,
      });
      const timer = setTimeout(async () => {
        try {
          const scanId = createScanId(deviceId);
          debugPlaybackLog('Scheduler UI triggering scan-load-and-play (timer fired)', {
            deviceId,
            scheduledItemId: payload.id,
            scanId,
          });
          await schedulerFetch('/api/scan-load-and-play', {
            method: 'POST',
            body: JSON.stringify({
              deviceId,
              force: true,
              mixNow: true,
              trigger: 'webserver:scheduler-timer',
              scanId,
            }),
          });
          setMessage('Mix Now triggered after shortened playlist ended');
        } catch (err) {
          console.error('Failed to trigger Mix Now after timeout', err);
        }
      }, delay);
      timers.set(payload.id, timer);
    }
  };

  const handleSave = async (payload, existingId = null, { mix = true } = {}) => {
    if (existingId) {
      const res = await schedulerFetch(`/api/scheduled-items/${existingId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (res?.item) {
        upsertLocal([res.item]);
        if (mix) {
          await clampAndMix(payload.deviceIds?.[0] || payload.device_id, { ...payload, id: res.item.id });
        }
      }
    } else {
      const res = await schedulerFetch('/api/scheduled-items', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const createdItems = res?.items || [];
      if (createdItems.length) {
        upsertLocal(createdItems);
        if (mix) {
          await clampAndMix(payload.deviceIds?.[0] || payload.device_id, { ...payload, id: createdItems[0].id });
        }
      }
    }
  };

  const findOverlappingLiveEvent = useCallback(
    (deviceId, start, end, ignoreId = null) => {
      const list = eventsByDevice.get(deviceId) || [];
      const s = start instanceof Date ? start : new Date(start);
      const e = end instanceof Date ? end : new Date(end);
      return (
        list.find((row) => {
          if (ignoreId && row.id === ignoreId) return false;
          if (String(row.item_type || '').toLowerCase() !== 'event') return false;
          const mode = String(row.event_mode || 'live').toLowerCase();
          if (mode === 'teaser') return false;
          const rowStart = new Date(row.start_time);
          const rowEnd = new Date(row.end_time);
          return rowStart < e && rowEnd > s;
        }) || null
      );
    },
    [eventsByDevice]
  );

  const findOverlappingNonTeaser = useCallback(
    (deviceId, start, end, ignoreId = null) => {
      const list = eventsByDevice.get(deviceId) || [];
      const s = start instanceof Date ? start : new Date(start);
      const e = end instanceof Date ? end : new Date(end);
      return (
        list.find((row) => {
          if (ignoreId && row.id === ignoreId) return false;
          const rowStart = new Date(row.start_time);
          const rowEnd = new Date(row.end_time);
          if (!(rowStart < e && rowEnd > s)) return false;
          if (String(row.item_type || '').toLowerCase() === 'event' && String(row.event_mode || '').toLowerCase() === 'teaser') {
            return false;
          }
          return true;
        }) || null
      );
    },
    [eventsByDevice]
  );

  const shortenOverlaps = useCallback(
    async (deviceId, newStart, ignoreId = null) => {
      const overlapping = (eventsByDevice.get(deviceId) || []).filter((ev) => {
        if (ignoreId && ev.id === ignoreId) return false;
        const evStart = new Date(ev.start_time);
        const evEnd = new Date(ev.end_time);
        return newStart > evStart && newStart < evEnd;
      });
      if (!overlapping.length) return;
      const updates = overlapping
        .map((ev) => {
          const evStart = new Date(ev.start_time);
          const trimmedEnd = new Date(Math.max(evStart.getTime() + MIN_EVENT_MS, newStart.getTime()));
          if (trimmedEnd.getTime() >= new Date(ev.end_time).getTime()) return null;
          return {
            ...ev,
            end_time: trimmedEnd.toISOString(),
          };
        })
        .filter(Boolean);
      if (!updates.length) return;

      setEvents((prev) =>
        prev.map((p) => {
          const match = updates.find((u) => u.id === p.id);
          return match ? { ...p, end_time: match.end_time } : p;
        })
      );

      await Promise.all(
        updates.map((u) =>
          handleSave(
            {
              start_time: u.start_time,
              end_time: u.end_time,
              deviceIds: [u.device_id],
              item_type: u.item_type,
              item_id: u.item_id,
              recurrence_rule: u.recurrence_rule || '',
              timezone: u.timezone || resolveTimezone(u.device_id) || 'UTC',
            },
            u.id,
            { mix: false }
          )
        )
      );
    },
    [eventsByDevice, handleSave]
  );

  const openNewAt = (date, deviceId) => {
    const start = date instanceof Date ? date : new Date(date);
    const defaultDuration = playlistDurations[regularPlaylists[0]?.id] || 60 * 60 * 1000;
    const end = new Date(start.getTime() + defaultDuration);
    const { start: safeStart, end: safeEnd } = clampToNow(start, end);
    const devId = deviceId || selectedDeviceId || devices[0]?.id || null;
    if (!devId) return;
    const timezone = resolveTimezone(devId);
    setEditing({
      id: null,
      item_type: 'playlist',
      item_id: regularPlaylists[0]?.id || '',
      deviceIds: [devId],
      start: fmtDateTimeLocal(safeStart),
      end: fmtDateTimeLocal(safeEnd),
      recurrence_rule: '',
      timezone,
      randomize: false,
    });
    setModalOpen(true);
  };

  const openDroppedScheduledItemEditor = useCallback(({
    deviceId,
    itemType,
    itemId,
    start,
    end,
    randomize = false,
    eventMode = 'live',
  }) => {
    const timezone = resolveTimezone(deviceId);
    setEditing({
      id: null,
      fromDrop: true,
      item_type: itemType,
      item_id: itemId,
      deviceIds: [deviceId],
      start: fmtDateTimeLocal(start, true),
      end: fmtDateTimeLocal(end, true),
      recurrence_rule: '',
      timezone,
      randomize,
      event_mode: eventMode,
      phaseKey: eventMode === 'teaser' ? 'teaser' : 'live',
    });
    setModalOpen(true);
  }, [resolveTimezone]);

  const openGigEditor = useCallback((gig = null, options = {}) => {
    const fallbackStart = options.start || new Date(Math.max(Date.now(), rangeStart.getTime()));
    const fallbackEnd = options.end || new Date(fallbackStart.getTime() + 3 * 60 * 60 * 1000);
    const seriesKey = gig ? getRecurringGigSeriesKey(gig) : null;
    if (seriesKey && Number(gig?.id || 0) > 0) {
      setSelectedGigIdsBySeries((current) => ({
        ...current,
        [seriesKey]: Number(gig.id),
      }));
    }
    setSelectedGigId(gig?.id || null);
    setPendingGigWindow({ start: fallbackStart, end: fallbackEnd });
    setGigModalOpen(true);
  }, [rangeStart]);

  useEffect(() => {
    const normalizedGigId = Number(requestedGigId || 0) || null;
    if (!normalizedGigId || !openRequestKey || lastHandledGigRequestKeyRef.current === openRequestKey) return;
    lastHandledGigRequestKeyRef.current = openRequestKey;
    openGigEditor(gigsById.get(normalizedGigId) || { id: normalizedGigId });
  }, [gigsById, openGigEditor, openRequestKey, requestedGigId]);

  const closeGigEditor = useCallback(() => {
    setGigModalOpen(false);
    setSelectedGigId(null);
    setPendingGigWindow(null);
  }, []);

  const openGigRecurringTemplateModal = useCallback(() => {
    setGigRecurringTemplateModalOpen(true);
  }, []);

  const closeGigRecurringTemplateModal = useCallback(() => {
    setGigRecurringTemplateModalOpen(false);
  }, []);

  const handleGigChanged = useCallback(async () => {
    await Promise.all([fetchGigLane(), fetchGigs()]);
  }, [fetchGigLane, fetchGigs]);

  const openCampaignEditor = useCallback((occurrence = null, window = null) => {
    const start = window?.start || (occurrence?.startsAtUtc ? new Date(occurrence.startsAtUtc) : new Date(Math.max(Date.now(), rangeStart.getTime())));
    const end = window?.end || (occurrence?.endsAtUtc ? new Date(occurrence.endsAtUtc) : new Date(start.getTime() + 2 * HOUR_MS));
    setSelectedProgramme(occurrence);
    setProgrammeForm({
      programmeType: 'promotion',
      title: occurrence?.title || '',
      startsAtUtc: fmtDateTimeLocal(start),
      endsAtUtc: fmtDateTimeLocal(end),
      targetGroupId: occurrence?.targetGroupId || occurrence?.phaseTargets?.[0]?.targetGroupId || '',
      templateKey: occurrence?.templateKey || 'venue-promotion-standard',
    });
    setProgrammeModalOpen(true);
  }, [rangeStart]);

  const openProgrammeFromLane = useCallback((occurrence) => {
    if (occurrence?.sourceType === 'gig') {
      setSelectedProgramme(occurrence);
      setProgrammeForm(null);
      setProgrammeModalOpen(true);
      return;
    }
    openCampaignEditor(occurrence);
  }, [openCampaignEditor]);

  const saveCampaignDraft = useCallback(async () => {
    if (!programmeForm || !activeSiteId || !area?.id) return;
    setProgrammeSaving(true);
    try {
      const payload = {
        siteId: activeSiteId,
        areaId: area.id,
        programmeType: programmeForm.programmeType,
        title: programmeForm.title,
        startsAtUtc: parseDateTimeLocal(programmeForm.startsAtUtc).toISOString(),
        endsAtUtc: parseDateTimeLocal(programmeForm.endsAtUtc).toISOString(),
        timezoneName: siteTimeZone,
        targetGroupId: Number(programmeForm.targetGroupId) || null,
        templateKey: programmeForm.templateKey,
      };
      const path = selectedProgramme?.id
        ? `/api/venue-programmes/${selectedProgramme.id}`
        : '/api/venue-programmes';
      const response = await appFetch(path, {
        method: selectedProgramme?.id ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });
      const occurrence = response.occurrence;
      setSelectedProgramme(occurrence);
      setMessage(selectedProgramme?.id ? 'Campaign updated and generated phases reconciled.' : 'Campaign draft created. Review its automation before publishing.');
      await fetchGigLane();
    } catch (error) {
      alert(error.message || 'Could not save campaign.');
    } finally {
      setProgrammeSaving(false);
    }
  }, [activeSiteId, area?.id, fetchGigLane, programmeForm, selectedProgramme?.id, siteTimeZone]);

  const moveCampaign = useCallback(async (occurrence, start, end) => {
    const snapMs = 5 * 60 * 1000;
    const snappedStart = new Date(Math.round(start.getTime() / snapMs) * snapMs);
    const duration = Math.max(MIN_EVENT_MS, end.getTime() - start.getTime());
    const snappedEnd = new Date(snappedStart.getTime() + Math.round(duration / snapMs) * snapMs);
    try {
      await appFetch(`/api/venue-programmes/${occurrence.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          startsAtUtc: snappedStart.toISOString(),
          endsAtUtc: snappedEnd.toISOString(),
          timezoneName: occurrence.timezoneName || siteTimeZone,
        }),
      });
      setMessage('Campaign moved; managed phases and creatives were reconciled without duplicate rows.');
      await fetchGigLane();
    } catch (error) {
      alert(error.message || 'Could not move campaign.');
      await fetchGigLane();
    }
  }, [fetchGigLane, siteTimeZone]);

  const cancelCampaign = useCallback(async () => {
    if (!selectedProgramme?.id) return;
    if (!window.confirm(`Cancel ${selectedProgramme.title}? Future generated actions will be suppressed.`)) return;
    setProgrammeSaving(true);
    try {
      await appFetch(`/api/venue-programmes/${selectedProgramme.id}/cancel`, {
        method: 'POST',
        body: '{}',
      });
      setMessage('Campaign cancelled. Future generated actions were suppressed and cleanup intent was retained.');
      setProgrammeModalOpen(false);
      await fetchGigLane();
    } catch (error) {
      alert(error.message || 'Could not cancel campaign.');
    } finally {
      setProgrammeSaving(false);
    }
  }, [fetchGigLane, selectedProgramme]);

  const openAdPlacementAt = ({
    overlayId = null,
    announcementId = null,
    contentType = null,
    deviceId = null,
    startDate = null,
  } = {}) => {
    const devId = deviceId || selectedDeviceId || devices[0]?.id || null;
    if (!devId) return;
    const start = startDate instanceof Date ? startDate : new Date();
    const safeStart = start < new Date() ? new Date() : start;
    const resolvedType =
      contentType || (announcementId ? 'announcement' : 'overlay');
    setAdEditing(
      buildAdEditingState({
        deviceId: devId,
        contentType: resolvedType,
        overlayId: resolvedType === 'overlay' ? (overlayId || overlays[0]?.id || '') : '',
        announcementId:
          resolvedType === 'announcement' ? (announcementId || announcements[0]?.id || '') : '',
        startDate: safeStart,
      })
    );
    setOverlaySlotPreview(null);
    setAdModalOpen(true);
  };

  const openAdSeriesEditor = (series) => {
    if (!series) return;
    setSelectedAdSeriesId(getSeriesSelectionId(series) || Number(series.id));
    const contentType = getAdContentType(series);
    const ownerDeviceId = series.sync_owner_device_id || series.device_id;
    setSelectedDeviceId(ownerDeviceId);
    onDeviceChange?.(ownerDeviceId);
    const seriesStart = new Date(series.start_time);
    const seriesEnd = new Date(series.end_time);
    setAdEditing(
      buildAdEditingState({
        id: series.sync_owner_series_id || series.id,
        deviceId: ownerDeviceId,
        contentType,
        overlayId: contentType === 'overlay' ? series.overlay_id : '',
        announcementId: contentType === 'announcement' ? series.announcement_id : '',
        startDate: seriesStart,
        repeatMode: series.repeat_mode || 'once',
        allDay: !!series.all_day,
        startTimeLocal: series.start_time_local || formatTimeInputValue(seriesStart),
        endTimeLocal: series.end_time_local || formatTimeInputValue(seriesEnd),
        impressionsPerHour: Number(series.impressions_per_hour) || 6,
        durationSec: Number(series.duration_sec) || 60,
        playContinuously: !!series.play_continuously,
        syncDeviceIds: Array.isArray(series.sync_device_ids) ? series.sync_device_ids : [],
        holdVisualSeconds: Math.max(0, Number(series.hold_visual_seconds) || 0),
        adTransparency: series.ad_transparency,
        playbackMode: series.playback_mode,
        requestedSlot: series.requested_slot,
        compatibleSlots: series.compatible_slots,
        priorityValue: series.priority_value,
        overridePolicy: series.override_policy,
      })
    );
    setOverlaySlotPreview(null);
    setAdModalOpen(true);
  };

  const openManualOverlayEditor = (overlayItem) => {
    if (!overlayItem) return;
    const start = new Date(overlayItem.start_time);
    const end = new Date(overlayItem.end_time);
    setManualOverlayEditing({
      id: overlayItem.id,
      deviceId: overlayItem.device_id,
      overlayId: overlayItem.overlay_id,
      start: fmtDateTimeLocal(start, true),
      end: fmtDateTimeLocal(end, true),
      duration_sec: Math.max(1, Number(overlayItem.duration_sec) || Math.round((end.getTime() - start.getTime()) / 1000) || 60),
      requestedSlot: overlayItem.requested_slot || overlayCache[overlayItem.overlay_id]?.type || 'full',
      compatibleSlots: overlayItem.compatible_slots || [],
      priorityValue: Number(overlayItem.priority_value) || 100,
      overridePolicy: overlayItem.override_policy || 'never',
      plannerDisposition: overlayItem.planner_disposition || 'pending',
      plannerReason: overlayItem.planner_reason?.reason || null,
    });
    setManualOverlayModalOpen(true);
  };

  const saveManualOverlay = async () => {
    if (!manualOverlayEditing) return;
    try {
      const start = parseDateTimeLocal(manualOverlayEditing.start);
      const end = parseDateTimeLocal(manualOverlayEditing.end);
      const allowed = validateRangeAcrossDevices([manualOverlayEditing.deviceId], start, end);
      if (!allowed.ok) {
        alert(allowed.message);
        return;
      }
      const durationSec = Math.max(1, Math.round((end.getTime() - start.getTime()) / 1000));
      const endpoint = manualOverlayEditing.id
        ? `/api/scheduled-overlays/${manualOverlayEditing.id}`
        : '/api/scheduled-overlays';
      await schedulerFetch(endpoint, {
        method: manualOverlayEditing.id ? 'PUT' : 'POST',
        body: JSON.stringify({
          deviceId: manualOverlayEditing.deviceId,
          overlayId: manualOverlayEditing.overlayId,
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          duration_sec: durationSec,
          requestedSlot: manualOverlayEditing.requestedSlot,
          compatibleSlots: manualOverlayEditing.compatibleSlots,
          priority: manualOverlayEditing.priorityValue,
          overridePolicy: manualOverlayEditing.overridePolicy,
        }),
      });
      setManualOverlayModalOpen(false);
      setManualOverlayEditing(null);
      await fetchScheduledOverlays(devices.map((d) => d.id));
    } catch (err) {
      console.error('[Scheduler] Failed to save manual overlay', err);
      alert(err.message || 'Could not save overlay.');
    }
  };

  const deleteManualOverlay = async () => {
    if (!manualOverlayEditing) return;
    if (!window.confirm('Delete this scheduled overlay?')) return;
    try {
      await schedulerFetch(`/api/scheduled-overlays/${manualOverlayEditing.id}?deviceId=${manualOverlayEditing.deviceId}`, {
        method: 'DELETE',
      });
      setManualOverlayModalOpen(false);
      setManualOverlayEditing(null);
      await fetchScheduledOverlays(devices.map((d) => d.id));
    } catch (err) {
      console.error('[Scheduler] Failed to delete manual overlay', err);
      alert(err.message || 'Could not delete overlay.');
    }
  };

  const saveEvent = async () => {
    if (!editing) return;
    setSaving(true);
    const timezone = editing.timezone || 'UTC';
    const start = parseDateTimeLocal(editing.start);
    const end = parseDateTimeLocal(editing.end);
    const { start: clampedStart, end: clampedEnd } = shouldPreservePastStart(editing)
      ? { start, end }
      : clampToNow(start, end);
    const primaryDevice = editing.deviceIds?.[0];
    const tzValue = timezone || resolveTimezone(primaryDevice);
    const payload = {
      deviceIds: editing.deviceIds,
      target_group_id: editing.targetGroupId || null,
      phase_key: editing.phaseKey || (editing.event_mode === 'teaser' ? 'teaser' : 'live'),
      item_type: editing.item_type,
      item_id: editing.item_id,
      event_mode: editing.event_mode || 'live',
      start_time: clampedStart.toISOString(),
      end_time: clampedEnd.toISOString(),
      recurrence_rule: editing.recurrence_rule,
      timezone: tzValue,
      randomize: editing.randomize ? 1 : 0,
    };

    try {
      if (!payload.deviceIds?.length && !payload.target_group_id) {
        alert('Select a target group or at least one device.');
        setSaving(false);
        return;
      }
      if (!payload.item_id) {
        alert('Choose content or command before saving.');
        setSaving(false);
        return;
      }
      const allowed = validateRangeAcrossDevices(payload.deviceIds, clampedStart, clampedEnd);
      if (!allowed.ok) {
        alert(allowed.message);
        setSaving(false);
        return;
      }
      for (const deviceId of payload.deviceIds) {
        if (payload.item_type === 'event' && payload.event_mode !== 'teaser') {
          if (findOverlappingNonTeaser(deviceId, clampedStart, clampedEnd, editing.id)) {
            alert('Events cannot overlap other scheduled items. Move the other blocks first.');
            return;
          }
        } else if (['app', 'playlist', 'ad'].includes(payload.item_type)) {
          if (findOverlappingLiveEvent(deviceId, clampedStart, clampedEnd, editing.id)) {
            alert('Cannot schedule over a live event. Adjust the time range.');
            return;
          }
        }
      }
      const primaryDevice = payload.deviceIds[0];
      await handleSave({ ...payload, device_id: primaryDevice }, editing.id);
      setModalOpen(false);
    } catch (err) {
      console.error('[Scheduler] Failed to save event', err);
      alert(getRequestErrorMessage(err, 'Could not save the schedule. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const previewAdOverlaySlots = async () => {
    if (!adEditing?.scheduleDate) return;
    const start = adEditing.allDay
      ? parseDateTimeLocal(`${adEditing.scheduleDate}T00:00`)
      : parseDateAndTimeLocal(adEditing.scheduleDate, adEditing.startTimeLocal);
    const end = adEditing.allDay
      ? addDays(start, 1)
      : parseDateAndTimeLocal(adEditing.scheduleDate, adEditing.endTimeLocal);
    if (!start || !end || end <= start) return;
    setOverlaySlotPreviewLoading(true);
    try {
      const result = await schedulerFetch('/api/overlay-slot-plan', {
        method: 'POST',
        body: JSON.stringify({
          deviceIds: [adEditing.deviceId, ...(adEditing.syncDeviceIds || [])],
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          requestedSlot: adEditingRequestedSlot,
          compatibleSlots: adEditingCompatibleSlots,
          priority: Number(adEditing.priorityValue) || 40,
          exclusive: adEditingRequestedSlot === 'full',
          overridePolicy: adEditing.overridePolicy,
          source: 'ad_series',
        }),
      });
      setOverlaySlotPreview(Array.isArray(result.devices) ? result.devices : []);
    } catch (error) {
      setOverlaySlotPreview([{ error: error.message || 'Could not preview overlay slots.' }]);
    } finally {
      setOverlaySlotPreviewLoading(false);
    }
  };

  const saveAdSeries = async () => {
    if (!adEditing) return;
    setSaving(true);
    try {
      const contentType = adEditing.contentType === 'announcement' ? 'announcement' : 'overlay';
      const scheduleDate = adEditing.scheduleDate;
      if (!scheduleDate) {
        alert('Choose a date.');
        return;
      }
      const constrainedTimes = adEditing.allDay
        ? { startTimeLocal: null, endTimeLocal: null }
        : constrainAdTimeRange({
            deviceId: adEditing.deviceId,
            scheduleDate,
            startTimeLocal: adEditing.startTimeLocal,
            endTimeLocal: adEditing.endTimeLocal,
          });
      if (!adEditing.allDay) {
        const startMinutes = timeStringToMinutes(constrainedTimes.startTimeLocal);
        const endMinutes = timeStringToMinutes(constrainedTimes.endTimeLocal);
        if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
          alert('End time must be after Start time.');
          return;
        }
      }
      const payload = {
        deviceId: adEditing.deviceId,
        contentType,
        overlayId: contentType === 'overlay' ? Number(adEditing.overlayId) : null,
        announcementId: contentType === 'announcement' ? Number(adEditing.announcementId) : null,
        scheduleDate,
        repeatMode: adEditing.repeatMode === 'daily' ? 'daily' : 'once',
        allDay: adEditing.allDay ? 1 : 0,
        startTimeLocal: adEditing.allDay ? null : constrainedTimes.startTimeLocal,
        endTimeLocal: adEditing.allDay ? null : constrainedTimes.endTimeLocal,
        impressions_per_hour: Number(adEditing.impressions_per_hour) || 6,
        duration_sec: Number(adEditing.duration_sec) || 60,
        play_continuously: contentType === 'announcement' ? 0 : (adEditing.play_continuously ? 1 : 0),
        syncDeviceIds: Array.isArray(adEditing.syncDeviceIds) ? adEditing.syncDeviceIds : [],
        hold_visual_seconds:
          contentType === 'announcement' ? Math.max(0, Number(adEditing.hold_visual_seconds) || 0) : 0,
        ad_transparency:
          contentType === 'overlay' ? clampAdTransparency(adEditing.ad_transparency) : 60,
        playbackMode:
          contentType === 'overlay' ? normalizeAdPlaybackMode(adEditing.playbackMode) : 'visual_only',
        requestedSlot: adEditingRequestedSlot,
        compatibleSlots: adEditingCompatibleSlots,
        priority_value: Number(adEditing.priorityValue) || 40,
        is_exclusive: adEditing.requestedSlot === 'full' ? 1 : 0,
        overridePolicy: adEditing.overridePolicy,
        ad_breaks: adSettings?.ad_breaks ? 1 : 0,
      };

      if (contentType === 'announcement' && !payload.announcementId) {
        alert('Choose an announcement.');
        return;
      }
      if (contentType === 'overlay' && !payload.overlayId) {
        alert('Choose an overlay.');
        return;
      }

      if (adEditing.id) {
        await schedulerFetch(`/api/scheduled-ad-series/${adEditing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await schedulerFetch('/api/scheduled-ad-series', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      await fetchAdSeriesForDevices(devices.map((d) => d.id), selectedDeviceId || payload.deviceId);
      await fetchScheduledOverlays(devices.map((d) => d.id));
      setAdModalOpen(false);
      setMessage('Ad scheduled');
    } catch (err) {
      console.error('[Scheduler] Failed to save ad series', err);
      alert(err.message || 'Could not save ad schedule.');
    } finally {
      setSaving(false);
    }
  };

  const deleteAdSeries = async () => {
    if (!adEditing?.id) return;
    const sure = window.confirm('Delete this ad schedule?');
    if (!sure) return;
    try {
      await schedulerFetch(`/api/scheduled-ad-series/${adEditing.id}?deviceId=${adEditing.deviceId}`, {
        method: 'DELETE',
      });
      await fetchAdSeriesForDevices(devices.map((d) => d.id), selectedDeviceId || adEditing.deviceId);
      await fetchScheduledOverlays(devices.map((d) => d.id));
      setAdModalOpen(false);
    } catch (err) {
      console.error('[Scheduler] Failed to delete ad series', err);
      alert(err.message || 'Could not delete ad schedule.');
    }
  };

  const toggleAdBreaks = async (nextVal) => {
    const settingsDeviceId = selectedDeviceId || devices[0]?.id || null;
    if (!settingsDeviceId) return;
    try {
      const res = await schedulerFetch('/api/scheduled-ad-settings', {
        method: 'PUT',
        body: JSON.stringify({ deviceId: settingsDeviceId, ad_breaks: nextVal ? 1 : 0 }),
      });
      setAdSettings(res.settings || { ad_breaks: nextVal ? 1 : 0 });
      await fetchScheduledOverlays(devices.map((d) => d.id));
    } catch (err) {
      console.error('[Scheduler] Failed to update ad breaks', err);
      alert(err.message || 'Could not update Ad Breaks setting.');
    }
  };

  const selectedAdRotation = useMemo(
    () => adRotations.find((rotation) => String(rotation.id) === String(selectedAdRotationId)) || adRotations[0] || null,
    [adRotations, selectedAdRotationId]
  );

  const adRotationPlaylistIds = useMemo(
    () => new Set(adRotations.map((rotation) => Number(rotation.playlist_id || rotation.id)).filter(Boolean)),
    [adRotations]
  );

  const regularPlaylists = useMemo(
    () => playlists.filter((playlist) => !adRotationPlaylistIds.has(Number(playlist.id))),
    [adRotationPlaylistIds, playlists]
  );

  const resetAdRotationForm = useCallback(() => {
    if (!selectedAdRotation) {
      setRotationForm({ name: '', mode: 'custom', duration_sec: 60, showInEpg: false, overlayIds: [] });
      return;
    }
    setRotationForm({
      name: selectedAdRotation.name || '',
      mode: selectedAdRotation.mode === 'all' ? 'all' : 'custom',
      duration_sec: Number(selectedAdRotation.duration_sec) || 60,
      showInEpg: selectedAdRotation.show_in_epg === true,
      overlayIds: Array.isArray(selectedAdRotation.overlay_ids) ? selectedAdRotation.overlay_ids.map(String) : [],
    });
  }, [selectedAdRotation]);

  useEffect(() => {
    resetAdRotationForm();
  }, [resetAdRotationForm]);

  const openCreateAdRotation = () => {
    const defaultIds = playableFullScreenAds.slice(0, 3).map((ad) => String(ad.id));
    setCreateRotationForm({
      name: `Ad Rotation ${adRotations.length + 1}`,
      mode: 'custom',
      duration_sec: 60,
      showInEpg: false,
      overlayIds: defaultIds,
    });
    setRotationModalOpen(true);
  };

  const saveAdRotation = async (rotationForm, rotationEditing = null) => {
    if (!activeSiteId) return;
    const mode = rotationForm.mode === 'all' ? 'all' : 'custom';
    const overlayIds = Array.isArray(rotationForm.overlayIds)
      ? rotationForm.overlayIds.map(String).filter(Boolean)
      : [];
    if (!String(rotationForm.name || '').trim()) {
      alert('Name the ad rotation.');
      return;
    }
    if (mode !== 'all' && !overlayIds.length) {
      alert('Choose at least one full-screen ad.');
      return;
    }
    try {
      setRotationSaving(true);
      const endpoint = rotationEditing?.id
        ? `/api/ad-rotations/${rotationEditing.id}`
        : '/api/ad-rotations';
      const res = await schedulerFetch(endpoint, {
        method: rotationEditing?.id ? 'PUT' : 'POST',
        body: JSON.stringify({
          siteId: activeSiteId,
          name: rotationForm.name.trim(),
          show_in_epg: rotationForm.showInEpg === true,
          mode,
          duration_sec: Math.max(1, Number(rotationForm.duration_sec) || 60),
          overlayIds,
        }),
      });
      await fetchAdRotations();
      await fetchPlaylists();
      if (res.rotation?.id) {
        setSelectedAdRotationId(res.rotation.id);
      } else if (rotationEditing?.id) {
        setSelectedAdRotationId(rotationEditing.id);
      }
      setRotationModalOpen(false);
      setMessage(rotationEditing?.id ? 'Ad rotation saved' : 'Ad rotation created');
    } catch (err) {
      console.error('[Scheduler] Failed to save ad rotation', err);
      alert(err.message || 'Could not save ad rotation.');
    } finally {
      setRotationSaving(false);
    }
  };

  const applyAllDayEveryDayRotation = () => {
    setEditing((prev) => {
      if (!prev) return prev;
      const start = startOfDay(parseDateTimeLocal(prev.start));
      return {
        ...prev,
        start: fmtDateTimeLocal(start),
        end: fmtDateTimeLocal(addDays(start, 1)),
        recurrence_rule: 'FREQ=DAILY',
      };
    });
  };

  const deleteEvent = async (id) => {
    try {
      await schedulerFetch(`/api/scheduled-items/${id}`, { method: 'DELETE' });
      setEvents((prev) => prev.filter((p) => p.id !== id));
      const timers = mixTimersRef.current;
      if (timers.has(id)) {
        clearTimeout(timers.get(id));
        timers.delete(id);
      }
      setModalOpen(false);
    } catch (err) {
      console.error('[Scheduler] Failed to delete event', err);
      alert('Could not delete the schedule.');
    }
  };

  const handleMoveOrResize = async (start, end, evt) => {
    const previewId = getScheduledItemRenderKey(evt);
    try {
      const isTeaserEvent = evt.item_type === 'event' && (evt.event_mode || 'live') === 'teaser';
      const isLiveEvent = evt.item_type === 'event' && (evt.event_mode || 'live') === 'live';
      const fixedEnd = isLiveEvent ? new Date(evt.end_time) : end;
      const durationMs = Math.max(fixedEnd.getTime() - start.getTime(), MIN_EVENT_MS);
      const snapped = snapToPrevious(evt.device_id, start, durationMs, evt.id);
      const desiredStart = snapped.start;
      const preservePastStart = shouldPreservePastStart(evt);
      const clamped = preservePastStart
        ? { start: desiredStart, end: isLiveEvent ? fixedEnd : snapped.end }
        : (isLiveEvent ? clampStartToNow(desiredStart, fixedEnd) : clampToNow(desiredStart, snapped.end));
      const safeStart = new Date(Math.min(clamped.start.getTime(), fixedEnd.getTime() - MIN_EVENT_MS));
      const safeEnd = isLiveEvent ? fixedEnd : clamped.end;

      if (!isLiveEvent && !isTeaserEvent) {
        const blocking = findOverlappingLiveEvent(evt.device_id, safeStart, safeEnd, evt.id);
        if (blocking) {
          alert('Cannot move/resize an item over a live event.');
          clearPreviewPosition(previewId);
          return;
        }
      }
      const allowed = validateRangeAcrossDevices([evt.device_id], safeStart, safeEnd);
      if (!allowed.ok) {
        alert(allowed.message);
        clearPreviewPosition(previewId);
        return;
      }
      if (isLiveEvent) {
        const overlap = findOverlappingNonTeaser(evt.device_id, safeStart, safeEnd, evt.id);
        if (overlap) {
          alert('Cannot move/resize a live event over other scheduled items. Move the other blocks first.');
          clearPreviewPosition(previewId);
          return;
        }
      }

      if (!isTeaserEvent && !isLiveEvent) {
        await shortenOverlaps(evt.device_id, safeStart, evt.id);
      }

      // Optimistically update UI
      setEvents((prev) =>
        prev.map((p) =>
          p.id === evt.id
            ? { ...p, start_time: safeStart.toISOString(), end_time: safeEnd.toISOString() }
            : p
        )
      );
      const payload = {
        start_time: safeStart.toISOString(),
        end_time: safeEnd.toISOString(),
        deviceIds: [evt.device_id],
        item_type: evt.item_type,
        item_id: evt.item_id,
        event_mode: evt.event_mode || 'live',
        recurrence_rule: evt.recurrence_rule || '',
        timezone: evt.timezone || resolveTimezone(evt.device_id) || 'UTC',
        randomize: evt.randomize ? 1 : 0,
      };

      await handleSave({ ...payload, device_id: evt.device_id }, evt.id);
    } catch (err) {
      console.error('[Scheduler] Failed to update event timing', err);
      setMessage('Unable to move/resize; please retry.');
    } finally {
      clearPreviewPosition(previewId);
    }
  };

  const handleMoveOrResizeOverlayItem = async (start, end, overlayItem) => {
    const series = overlayItem?.series_id ? seriesById.get(overlayItem.series_id) : null;
    if (!series || !series.play_continuously) return;
    try {
      const { start: safeStart, end: safeEnd } = clampToNow(start, end);
      const ownerDeviceId = series.sync_owner_device_id || series.device_id;
      const syncedDeviceIds = Array.isArray(series.sync_device_ids) ? series.sync_device_ids : [];
      const contentType = getAdContentType(series);
      const scheduleDate = formatDateInputValue(safeStart);
      const constrainedTimes = constrainAdTimeRange({
        deviceId: ownerDeviceId,
        scheduleDate,
        startTimeLocal: formatTimeInputValue(safeStart),
        endTimeLocal: formatTimeInputValue(safeEnd),
      });
      await schedulerFetch(`/api/scheduled-ad-series/${series.sync_owner_series_id || series.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          deviceId: ownerDeviceId,
          contentType,
          overlayId: contentType === 'overlay' ? series.overlay_id : null,
          announcementId: contentType === 'announcement' ? series.announcement_id : null,
          scheduleDate,
          repeatMode: series.repeat_mode === 'daily' ? 'daily' : 'once',
          allDay: 0,
          startTimeLocal: constrainedTimes.startTimeLocal,
          endTimeLocal: constrainedTimes.endTimeLocal,
          impressions_per_hour: series.impressions_per_hour,
          duration_sec: series.duration_sec,
          syncDeviceIds: syncedDeviceIds,
          hold_visual_seconds: Number(series.hold_visual_seconds) || 0,
          ad_transparency: clampAdTransparency(series.ad_transparency),
          playbackMode: normalizeAdPlaybackMode(series.playback_mode),
          play_continuously: contentType === 'announcement' ? 0 : 1,
          ad_breaks: adSettings?.ad_breaks ? 1 : 0,
        }),
      });
      await fetchAdSeriesForDevices(devices.map((d) => d.id), selectedDeviceId || ownerDeviceId);
      await fetchScheduledOverlays(devices.map((d) => d.id));
    } catch (err) {
      console.error('[Scheduler] Failed to update continuous ad window', err);
      alert(err.message || 'Could not update continuous ad window.');
    } finally {
      clearPreviewPosition(overlayItem._key);
    }
  };

  const handleMoveOrResizeManualOverlayItem = async (start, end, overlayItem) => {
    try {
      const { start: safeStart, end: safeEnd } = clampToNow(start, end);
      const allowed = validateRangeAcrossDevices([overlayItem.device_id], safeStart, safeEnd);
      if (!allowed.ok) {
        alert(allowed.message);
        return;
      }
      const durationSec = Math.max(1, Math.round((safeEnd.getTime() - safeStart.getTime()) / 1000));
      await schedulerFetch(`/api/scheduled-overlays/${overlayItem.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          deviceId: overlayItem.device_id,
          overlayId: overlayItem.overlay_id,
          start_time: safeStart.toISOString(),
          end_time: safeEnd.toISOString(),
          duration_sec: durationSec,
        }),
      });
      await fetchScheduledOverlays(devices.map((d) => d.id));
    } catch (err) {
      console.error('[Scheduler] Failed to update manual overlay item', err);
      alert(err.message || 'Could not update overlay timing.');
    } finally {
      clearPreviewPosition(overlayItem._key);
    }
  };

  const handleDropLane = (e, deviceId) => {
    e.preventDefault();
    clearLaneDropPreview(deviceId);
    setErrorMessage('');
    const manualOverlayId = Number(e.dataTransfer.getData('manual-overlay-id'));
    const overlayId = Number(e.dataTransfer.getData('overlay-id'));
    const announcementId = Number(e.dataTransfer.getData('announcement-id'));
    const adRotationId = Number(e.dataTransfer.getData('ad-rotation-id'));
    const playlistId = Number(e.dataTransfer.getData('playlist-id'));
    const appPackage = e.dataTransfer.getData('app-package');
    const contentTargetId = Number(e.dataTransfer.getData('content-target-id')) || null;
    const eventInstanceId = Number(e.dataTransfer.getData('event-instance-id'));
    const eventMode = (e.dataTransfer.getData('event-mode') || 'live').toLowerCase();
    const { start: droppedStart } = resolveLaneDropPlacement(e.clientX, e.currentTarget);
    const startMs = droppedStart.getTime();

    if (manualOverlayId) {
      const overlay = overlayCache[manualOverlayId]
        || overlays.find((item) => item.id === manualOverlayId)
        || defaultAdOverlays.find((item) => item.id === manualOverlayId);
      const durationSec = Math.max(1, Number(overlay?.duration) || 7);
      openManualOverlayEditor({
        id: null,
        device_id: deviceId,
        overlay_id: manualOverlayId,
        start_time: droppedStart,
        end_time: new Date(startMs + durationSec * 1000),
        duration_sec: durationSec,
        requested_slot: overlay?.type || 'full',
      });
      return;
    }

    if (overlayId) {
      openAdPlacementAt({ overlayId, contentType: 'overlay', deviceId, startDate: new Date(startMs) });
      return;
    }

    if (announcementId) {
      openAdPlacementAt({ announcementId, contentType: 'announcement', deviceId, startDate: new Date(startMs) });
      return;
    }

    if (eventInstanceId) {
      const inst = eventInstances.find((i) => i.id === eventInstanceId) || null;
      const target = inst?.target_time ? new Date(inst.target_time) : null;
      const postDurationSec = Number(inst?.post_duration) || DEFAULT_EVENT_POST_DURATION_SEC;
      const derivedEnd = target ? new Date(target.getTime() + postDurationSec * 1000) : null;
      const desiredStartForLive = target ? new Date(target.getTime() - DEFAULT_LIVE_EVENT_PRESTART_MS) : new Date(startMs);
      const liveAlreadyScheduled = (eventsByDevice.get(deviceId) || []).some(
        (ev) =>
          ev.item_type === 'event' &&
          (ev.event_mode || 'live') === 'live' &&
          Number(ev.item_id) === Number(eventInstanceId)
      );

      if (eventMode === 'teaser') {
        const hasLiveForDevice = (eventsByDevice.get(deviceId) || []).some(
          (ev) =>
            ev.item_type === 'event' &&
            (ev.event_mode || 'live') === 'live' &&
            Number(ev.item_id) === Number(eventInstanceId)
        );
        if (!hasLiveForDevice) {
          alert('Schedule the live event first before adding teasers.');
          return;
        }
      }

      if (eventMode !== 'teaser' && liveAlreadyScheduled) {
        alert('This live event is already scheduled for this device. Drag the existing block to adjust its start.');
        return;
      }

      const endDate =
        eventMode === 'teaser'
          ? new Date(startMs + DEFAULT_EVENT_TEASER_MS)
          : derivedEnd || new Date(startMs + 30 * 60 * 1000);
      const now = new Date();
      const startDate = eventMode === 'teaser' ? new Date(startMs) : desiredStartForLive;
      const safeStart = startDate < now ? now : startDate;
      const minStartMs = endDate.getTime() - MIN_EVENT_MS;
      const adjustedStart = new Date(Math.min(safeStart.getTime(), minStartMs));
      if (adjustedStart >= endDate) {
        alert('This event has already ended (or is too close to ending). Choose an earlier start time.');
        return;
      }

      openDroppedScheduledItemEditor({
        deviceId,
        itemType: 'event',
        itemId: eventInstanceId,
        start: adjustedStart,
        end: endDate,
        eventMode: eventMode === 'teaser' ? 'teaser' : 'live',
      });
      return;
    }

    const itemType = appPackage || contentTargetId ? 'app' : adRotationId ? 'ad' : playlistId ? 'playlist' : null;
    if (!itemType) return;
    const rotation = adRotations.find((entry) => Number(entry.id) === adRotationId);
    const itemId = itemType === 'app'
      ? (contentTargetId ? encodeScheduledContentTarget(contentTargetId) : appPackage)
      : itemType === 'ad'
        ? rotation?.playlist_id || rotation?.id || adRotationId
        : playlistId;
    const durationMs = DEFAULT_DROPPED_ITEM_DURATION_MS;
    const { start, end } = droppedStart < new Date()
      ? { start: droppedStart, end: new Date(droppedStart.getTime() + durationMs) }
      : snapToPrevious(deviceId, droppedStart, durationMs, null);
    // Drops create drafts; Save applies schedule changes after configuration.
    openDroppedScheduledItemEditor({ deviceId, itemType, itemId, start, end });
  };

  const handleDragOverLane = (e, deviceId) => {
    if (!timelineInteractive) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const { x, start } = resolveLaneDropPlacement(e.clientX, e.currentTarget);
    setLaneDropPreview({
      deviceId,
      x,
      start,
    });
  };

  const renderTicks = () => {
    if (compactTimeline) {
      return visibleWindows.map((window, index) => {
        const width = Math.max(72, ((window.endMs - window.startMs) / visibleDurationMs) * laneWidth);
        const segmentStart = new Date(window.startMs);
        const segmentEnd = new Date(window.endMs);
        return (
          <div
            key={`window:${window.startMs}:${window.endMs}:${index}`}
            className="relative h-full border-l border-white/10 px-3"
            style={{ minWidth: width, width }}
          >
            <div className="flex h-full flex-col items-start justify-center leading-tight">
              <div className="text-[10px] font-semibold text-slate-100">
                {segmentStart.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
              </div>
              <div className="text-[10px] text-slate-400">
                {timeLabel(segmentStart)} – {timeLabel(segmentEnd)}
              </div>
            </div>
          </div>
        );
      });
    }

    const ticks = [];
    for (let tickIndex = 0; tickIndex < tickCount; tickIndex += 1) {
      const date = new Date(rangeStart.getTime() + tickIndex * tickHours * HOUR_MS);
      const isStartOfDayTick = date.getHours() === 0;
      const isDayView = viewMode === 'day';
      const isWeekView = viewMode === 'week';
      const primaryLabel = isDayView
        ? timeLabel(date)
        : isWeekView
          ? (isStartOfDayTick
            ? date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
            : timeLabel(date))
          : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const secondaryLabel = isDayView
        ? (tickIndex === 0 || isStartOfDayTick ? dateLabel(date) : null)
        : isWeekView
          ? (isStartOfDayTick ? null : date.toLocaleDateString([], { weekday: 'short' }))
          : date.toLocaleDateString([], { weekday: 'short' });

      ticks.push(
        <div
          key={tickIndex}
          className={`relative border-l px-2 h-full ${
            isStartOfDayTick ? 'border-white/20' : 'border-white/10'
          }`}
          style={{ minWidth: tickWidth, width: tickWidth }}
        >
          <div className={`absolute -left-[1px] top-0 bottom-0 border-l ${isStartOfDayTick ? 'border-white/10' : 'border-white/5'}`} />
          <div className="flex h-full flex-col items-start justify-center leading-tight">
            <div className="text-[10px] font-semibold text-slate-200">{primaryLabel}</div>
            {secondaryLabel ? <div className="text-[10px] text-slate-500">{secondaryLabel}</div> : null}
          </div>
        </div>
      );
    }
    return ticks;
  };

  const nowLine = () => {
    const now = new Date();
    if (now < rangeStart || now > rangeEnd) return null;
    const x = compactTimeline
      ? projectTimeToLaneX({
          time: now,
          rangeStart,
          rangeEnd,
          laneWidth,
          visibleWindows,
        })
      : (now.getTime() - rangeStart.getTime()) / msPerPx;
    if (x == null) return null;
    return (
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-rose-400 z-20"
        style={{ left: x }}
      >
        <div className="absolute -left-1 -top-2 text-[10px] bg-rose-500 text-white px-1 rounded">
          Now
        </div>
      </div>
    );
  };

  const summary = useMemo(() => {
    if (!editing) return '';
    const type = typeMeta(editing.item_type).label;
    const scheduledAppTarget = parseScheduledAppTarget(editing.item_id);
    const item =
      editing.item_type === 'app'
        ? scheduledAppTarget.mode === 'assigned'
          ? assignedDestinations.find((entry) => entry.id === editing.item_id)?.label || 'Assigned app destination'
          : scheduledAppTarget.mode === 'target'
          ? `${savedLinksById[scheduledAppTarget.contentTargetId]?.name || 'Saved URI'} (Link)`
          : `${appsByPackage[scheduledAppTarget.packageName]?.app_name ||
          appsByPackage[scheduledAppTarget.packageName]?.package_name ||
          scheduledAppTarget.packageName ||
          '(choose)'}${scheduledAppTarget.mode === 'intent' ? ' (Intent)' : ''}`
        : editing.item_type === 'ad'
          ? adRotations.find((rotation) => Number(rotation.playlist_id || rotation.id) === Number(editing.item_id))?.name ||
            playlistCache[editing.item_id]?.name ||
            editing.item_id ||
            '(choose)'
          : regularPlaylists.find((p) => p.id === editing.item_id)?.name || editing.item_id || '(choose)';
    const whenDate = parseDateTimeLocal(editing.start);
    const when = whenDate.toLocaleString();
    const rec = rruleLabel(editing.recurrence_rule);
    const devs =
      editing.deviceIds?.length === 1
        ? devices.find((d) => d.id === editing.deviceIds[0])?.name || 'Device'
        : `${editing.deviceIds?.length || 0} devices`;
    return `${type} • ${item} • ${when} • ${rec} • ${devs}`;
  }, [editing, regularPlaylists, devices, appsByPackage, savedLinksById, adRotations, playlistCache]);

  const everyDayPowerSchedule =
    DAY_KEYS.map((dayKey) => powerEditor.schedule?.[dayKey]).find((day) => day?.enabled) ||
    powerEditor.schedule?.monday ||
    { enabled: true, on: '09:00', off: '17:00' };

  const laneContent = (deviceObj) => {
    const devEvents = (eventsByDevice.get(deviceObj.id) || []).filter(
      (event) => showPlaylists || !isTimelinePlaylistItem(event)
    );
    const devOverlayItems = showAds ? scheduledOverlaysByDevice.get(deviceObj.id) || [] : [];
    const visuals = powerVisualsByDevice[deviceObj.id] || { closedSegments: [], markers: [] };
    return (
      <div
        className="relative border-b border-white/10"
        style={{ height: LANE_HEIGHT }}
        onDrop={timelineInteractive ? (e) => handleDropLane(e, deviceObj.id) : undefined}
        onDragOver={timelineInteractive ? (e) => handleDragOverLane(e, deviceObj.id) : undefined}
        onDragLeave={timelineInteractive ? () => clearLaneDropPreview(deviceObj.id) : undefined}
      >
        {!compactTimeline && visuals.closedSegments.map((segment, index) => {
          const left = Math.max(0, (segment.start.getTime() - rangeStart.getTime()) / msPerPx);
          const width = Math.max(0, (segment.end.getTime() - segment.start.getTime()) / msPerPx);
          if (width <= 0) return null;
          return (
            <div
              key={`closed:${deviceObj.id}:${index}`}
              className="absolute inset-y-0 bg-black/55 border-x border-black/40 pointer-events-none"
              style={{ left, width, zIndex: 2 }}
            >
              {width > 72 ? (
                <div className="absolute top-1 left-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  Closed
                </div>
              ) : null}
            </div>
          );
        })}
        {!compactTimeline && visuals.markers.map((marker, index) => {
          const x = (marker.at.getTime() - rangeStart.getTime()) / msPerPx;
          if (x < 0 || x > laneWidth) return null;
          const isOn = marker.type === 'on';
          return (
            <div
              key={`marker:${deviceObj.id}:${index}`}
              className="absolute inset-y-0 pointer-events-none"
              style={{ left: x, zIndex: 18 }}
            >
              <div className={`absolute inset-y-0 w-[2px] ${isOn ? 'bg-emerald-400/90' : 'bg-rose-400/90'}`} />
              <div
                className={`absolute top-1 -translate-x-1/2 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] ${
                  isOn ? 'bg-emerald-500/90 text-white' : 'bg-rose-500/90 text-white'
                }`}
              >
                {isOn ? 'On' : 'Off'}
              </div>
            </div>
          );
        })}
        {nowLine()}
        {timelineInteractive && laneDropPreview?.deviceId === deviceObj.id ? (
          <div
            className="absolute inset-y-0 pointer-events-none"
            style={{ left: laneDropPreview.x, zIndex: 34 }}
          >
            <div className="absolute inset-y-0 w-[2px] bg-cyan-300/95" />
            <div className="absolute -translate-x-1/2 -top-1 rounded-full bg-cyan-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-lg">
              {timeLabel(laneDropPreview.start)}
            </div>
          </div>
        ) : null}
        {devEvents.map((ev) => (
          <EventBlock
            key={getScheduledItemRenderKey(ev)}
            event={ev}
            previewId={getScheduledItemRenderKey(ev)}
            laneStart={rangeStart.getTime()}
            msPerPx={msPerPx}
            laneWidth={laneWidth}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            visibleWindows={compactTimeline ? visibleWindows : null}
            interactive={timelineInteractive}
            onMove={(s, e) => handleMoveOrResize(s, e, ev)}
            onResize={(s, e) => handleMoveOrResize(s, e, ev)}
            onDelete={(evt) => deleteEvent(evt.id)}
            draftPosition={draftPositions[getScheduledItemRenderKey(ev)]}
            onPreview={updatePreviewPosition}
            onPreviewEnd={clearPreviewPosition}
            formatTimeLabel={timeLabel}
            resolveItemName={(event) => {
              if (event.item_type === 'playlist' || event.item_type === 'ad') {
                return (
                  playlistCache[event.item_id]?.name ||
                  regularPlaylists.find((p) => p.id === event.item_id)?.name ||
                  `Playlist ${event.item_id}`
                );
              }
              if (event.item_type === 'overlay') {
                return overlayCache[event.item_id]?.name || `Overlay ${event.item_id}`;
              }
              if (event.item_type === 'event') {
                return eventInstances.find((i) => i.id === event.item_id)?.name || `Event ${event.item_id}`;
              }
              if (event.item_type === 'app') {
                const target = parseScheduledAppTarget(event.item_id);
                if (target.mode === 'assigned') return assignedDestinations.find((entry) => entry.id === event.item_id)?.label || 'Assigned app destination';
                if (target.mode === 'target') {
                  return savedLinksById[target.contentTargetId]?.name || `Saved URI ${target.contentTargetId}`;
                }
                return (
                  `${appsByPackage[target.packageName]?.app_name ||
                    appsByPackage[target.packageName]?.package_name ||
                    `App ${target.packageName}`}${target.mode === 'intent' ? ' (Intent)' : ''}`
                );
              }
              return String(event.item_id || '');
            }}
            onOpen={(evt) => {
              setSelectedDeviceId(evt.device_id);
              if (evt.item_type === 'event' && (evt.event_mode || 'live') === 'live') {
                window.dispatchEvent(
                  new CustomEvent('ss:open-event-instance', {
                    detail: { eventInstanceId: evt.item_id, deviceId: evt.device_id },
                  })
                );
              } else {
                setEditing({
                  ...evt,
                  deviceIds: [evt.device_id],
                  targetGroupId: evt.programme_occurrence?.target_group_id || null,
                  start: fmtDateTimeLocal(new Date(evt.start_time)),
                  end: fmtDateTimeLocal(new Date(evt.end_time)),
                  timezone: evt.timezone || resolveTimezone(evt.device_id) || 'UTC',
                  randomize: !!evt.randomize,
                });
                setModalOpen(true);
              }
            }}
          />
        ))}

        {devOverlayItems.map((item) => {
          const series = item.series_id ? seriesById.get(item.series_id) : null;
          const isContinuous = !!series?.play_continuously;
          const isManual = item.source && String(item.source) !== 'ad_series';
          const contentType = getAdContentType(item);
          const title =
            contentType === 'announcement'
              ? announcementCache[item.announcement_id]?.title || `Announcement ${item.announcement_id}`
              : overlayCache[item.overlay_id]?.name || `Overlay ${item.overlay_id}`;
          const type =
            contentType === 'announcement'
              ? 'announcement'
              : overlayCache[item.overlay_id]?.type || null;
          const selectionId =
            getSeriesSelectionId(series) || (item.series_id ? Number(item.series_id) : null);
          const style = selectionId
            ? getAdSeriesColorStyle(selectionId)
            : overlayTypeStyle(type);
          const isSelected = selectedAdSeriesId && selectionId === selectedAdSeriesId;
          return (
            <OverlayBlock
              key={item._key}
              item={item}
              laneStart={rangeStart.getTime()}
              msPerPx={msPerPx}
              laneWidth={laneWidth}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              visibleWindows={compactTimeline ? visibleWindows : null}
              interactive={timelineInteractive}
              draftPosition={draftPositions[item._key]}
              onPreview={updatePreviewPosition}
              onPreviewEnd={clearPreviewPosition}
              formatTimeLabel={timeLabel}
              title={title}
              label={`${title} · ${item.selected_slot || item.requested_slot || 'full'}${item.planner_disposition && item.planner_disposition !== 'scheduled' ? ` · ${item.planner_disposition}` : ''}`}
              selected={!!isSelected}
              isResizable={isManual || isContinuous}
              className={`${style.className} ${isSelected ? 'ring-2 ring-white/95' : ''}`}
              style={
                isSelected
                  ? { boxShadow: '0 0 0 1px rgba(255,255,255,.65), 0 8px 18px rgba(15,23,42,.45)' }
                  : { boxShadow: '0 8px 18px rgba(15,23,42,.35)' }
              }
              onMoveOrResize={(s, e) => (isManual ? handleMoveOrResizeManualOverlayItem(s, e, item) : handleMoveOrResizeOverlayItem(s, e, item))}
              onOpen={() => {
                if (isManual) {
                  openManualOverlayEditor(item);
                  return;
                }
                if (series) {
                  openAdSeriesEditor(series);
                }
              }}
            />
          );
        })}
      </div>
    );
  };

  const gigsLaneContent = () => (
    <GigsCampaignsLane
      occurrences={gigLaneRows}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
      laneWidth={laneWidth}
      visibleWindows={compactTimeline ? visibleWindows : null}
      nowLine={nowLine}
      interactive={timelineInteractive}
      onOpen={openProgrammeFromLane}
      onMove={moveCampaign}
    />
  );

  const groupedSchedulerListItems = useMemo(() => {
    const groups = new Map();
    schedulerListItems.forEach((item) => {
      const start = new Date(item.start);
      const groupKey = Number.isNaN(start.getTime())
        ? 'unknown'
        : start.toLocaleDateString([], {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey).push(item);
    });
    return Array.from(groups.entries());
  }, [schedulerListItems]);

  const previousLabel = viewMode === 'month' ? 'Previous Month' : viewMode === 'week' ? 'Previous Week' : 'Previous Day';
  const nextLabel = viewMode === 'month' ? 'Next Month' : viewMode === 'week' ? 'Next Week' : 'Next Day';
  const dateInputType = viewMode === 'month' ? 'month' : 'date';
  const dateInputValue = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    if (viewMode === 'month') {
      return `${year}-${month}`;
    }
    return fmtDateTimeLocal(selectedDate).slice(0, 10);
  }, [selectedDate, viewMode]);

  const handleDateInputChange = (value) => {
    if (!value) return;
    pendingTimelineScrollRef.current = null;
    if (viewMode === 'month') {
      setSelectedDate(startOfMonth(parseDateTimeLocal(`${value}-01T00:00`)));
      return;
    }
    setSelectedDate(startOfDay(parseDateTimeLocal(`${value}T00:00`)));
  };

  const getGigOccurrenceState = useCallback(
    (gig) => gigOccurrenceStateByGigId.get(Number(gig?.id || 0)) || null,
    [gigOccurrenceStateByGigId]
  );

  const getGigSummary = useCallback((gig) => (
    gig?.performerSummary ||
    (Array.isArray(gig?.performers)
      ? gig.performers
          .filter((performer) => !['replaced', 'cancelled'].includes(String(performer?.status || '').toLowerCase()))
          .map((performer) => performer?.displayName)
          .filter(Boolean)
          .join(' • ')
      : '') ||
    'No performer assigned'
  ), []);

  const openGigFromScheduler = useCallback((gig) => {
    if (!gig) return;
    openGigEditor(gig, {
      start: gig?.startsAtUtc ? new Date(gig.startsAtUtc) : undefined,
      end: gig?.endsAtUtc ? new Date(gig.endsAtUtc) : undefined,
    });
  }, [openGigEditor]);

  const jumpToGigOccurrence = useCallback((gig, direction) => {
    const occurrenceState = getGigOccurrenceState(gig);
    if (!occurrenceState?.isRecurringSeries) return;
    const targetGig = direction < 0 ? occurrenceState.previousGig : occurrenceState.nextGig;
    if (!targetGig) return;

    setSelectedGigIdsBySeries((current) => ({
      ...current,
      [occurrenceState.seriesKey]: Number(targetGig.id),
    }));
    focusSchedulerDate(new Date(targetGig.startsAtUtc || targetGig.starts_at_utc));
  }, [focusSchedulerDate, getGigOccurrenceState]);

  const selectedGig = useMemo(
    () => gigsById.get(Number(selectedGigId || 0)) || null,
    [gigsById, selectedGigId]
  );

  const selectedGigOccurrenceState = useMemo(
    () => (selectedGig ? getGigOccurrenceState(selectedGig) : null),
    [getGigOccurrenceState, selectedGig]
  );

  const jumpModalGigOccurrence = useCallback((direction) => {
    if (!selectedGigOccurrenceState?.isRecurringSeries) return;
    const targetGig = direction < 0 ? selectedGigOccurrenceState.previousGig : selectedGigOccurrenceState.nextGig;
    if (!targetGig) return;

    setSelectedGigIdsBySeries((current) => ({
      ...current,
      [selectedGigOccurrenceState.seriesKey]: Number(targetGig.id),
    }));
    setSelectedGigId(Number(targetGig.id));
    setPendingGigWindow({
      start: targetGig?.startsAtUtc ? new Date(targetGig.startsAtUtc) : null,
      end: targetGig?.endsAtUtc ? new Date(targetGig.endsAtUtc) : null,
    });
    focusSchedulerDate(new Date(targetGig.startsAtUtc || targetGig.starts_at_utc));
  }, [focusSchedulerDate, selectedGigOccurrenceState]);

  const renderSchedulerList = () => (
    <div className="grid gap-4">
      {groupedSchedulerListItems.length ? (
        groupedSchedulerListItems.map(([groupLabel, items]) => (
          <div key={groupLabel} className="rounded-2xl bg-black/20 p-4 ring-1 ring-white/10">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{groupLabel}</div>
            <div className="mt-3 grid gap-2">
              {items.map((item) => {
                const warningCount = Array.isArray(item.warnings) ? item.warnings.length : 0;
                const start = new Date(item.start);
                const end = new Date(item.end);
                const fullGig = item.kind === 'gig' ? gigsById.get(Number(String(item.id).split(':')[1])) : null;
                const occurrenceState = fullGig ? getGigOccurrenceState(fullGig) : null;
                return (
                  <div key={item.id} className="rounded-xl bg-white/5 px-3 py-3 ring-1 ring-white/10">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-sm font-semibold text-white">{item.title}</div>
                          <Badge className="bg-white/10 text-slate-200">{item.kind}</Badge>
                          {warningCount ? (
                            <Badge className="bg-amber-500/15 text-amber-100">{warningCount} warning{warningCount === 1 ? '' : 's'}</Badge>
                          ) : null}
                        </div>
                        <div className="mt-1 text-xs text-slate-400">
                          {item.deviceName} • {Number.isNaN(start.getTime()) ? 'Time TBD' : `${start.toLocaleString()} – ${Number.isNaN(end.getTime()) ? 'TBD' : end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                        </div>
                        {item.subtitle ? (
                          <div className="mt-1 text-xs text-slate-500">{item.subtitle}</div>
                        ) : null}
                        {warningCount ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {item.warnings.map((warning) => (
                              <span key={`${item.id}:${warning}`} className="rounded-full bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100 ring-1 ring-amber-400/20">
                                {warning}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      {item.kind === 'gig' ? (
                        <div className="flex flex-wrap items-center gap-2">
                          {occurrenceState?.isRecurringSeries ? (
                            <>
                              <button
                                type="button"
                                onClick={() => jumpToGigOccurrence(fullGig, -1)}
                                disabled={!occurrenceState?.hasPrevious}
                                className="rounded-xl bg-white/10 px-2.5 py-2 text-xs font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {'<<'}
                              </button>
                              <button
                                type="button"
                                onClick={() => jumpToGigOccurrence(fullGig, 1)}
                                disabled={!occurrenceState?.hasNext}
                                className="rounded-xl bg-white/10 px-2.5 py-2 text-xs font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {'>>'}
                              </button>
                            </>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => openGigFromScheduler(fullGig)}
                            className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20"
                          >
                            Edit
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      ) : (
        <div className="rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-slate-400">
          No scheduled items in this visible range.
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-900 via-slate-950 to-black text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <header className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-white/5 ring-1 ring-white/10">
            <Tv2 className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold leading-tight text-white">Timeline</h1>
          </div>
        </header>

        <div className="space-y-6">
          <div className="p-4 rounded-2xl bg-white/5 ring-1 ring-white/10 min-w-0 overflow-hidden">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-white/5 p-2 ring-1 ring-white/10">
                    <CalendarIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Timeline</div>
                    <div className="text-xs text-slate-400">{rangeLabel}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {loading ? <Badge className="bg-white/5">Loading…</Badge> : null}
                  <Badge className="bg-white/5">{surfaceMode === 'timeline' ? 'Timeline View' : 'List View'}</Badge>
                  <Badge className="bg-white/5">Devices: {devices.length || 0}</Badge>
                  <Badge className="bg-indigo-500/10 text-indigo-100">Gigs & campaigns: {gigLaneRows.length || 0}</Badge>
                  {compactTimeline ? <Badge className="bg-emerald-500/10 text-emerald-100">Closed hours hidden</Badge> : null}
                </div>
              </div>

              <div className="flex flex-wrap items-start gap-3">
                <div className="w-16 pt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">View</div>
                <div className="flex flex-1 flex-wrap gap-2">
                  <button
                    onClick={() => setSurfaceMode('timeline')}
                    className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${surfaceMode === 'timeline' ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'}`}
                  >
                    Timeline
                  </button>
                  <button
                    onClick={() => setSurfaceMode('list')}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${surfaceMode === 'list' ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'}`}
                  >
                    <List className="h-3.5 w-3.5" />
                    List
                  </button>
                  {Object.entries(VIEW_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setViewMode(key);
                        setTickWidth(preset.tickWidth);
                      }}
                      className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${
                        viewMode === key ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      pendingTimelineScrollRef.current = null;
                      setSelectedDate((prev) => resolveViewAdvanceDate(prev, viewMode, -1));
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs bg-white/5 hover:bg-white/10"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    {previousLabel}
                  </button>
                  <label className="text-xs text-slate-300 flex items-center gap-2 rounded-xl bg-black/20 px-2.5 py-1.5 ring-1 ring-white/10">
                    <span>{viewMode === 'month' ? 'Month' : 'Date'}</span>
                    <input
                      type={dateInputType}
                      value={dateInputValue}
                      onChange={(e) => handleDateInputChange(e.target.value)}
                      className="bg-transparent text-white outline-none"
                    />
                  </label>
                  <button
                    onClick={() => {
                      pendingTimelineScrollRef.current = null;
                      setSelectedDate((prev) => resolveViewAdvanceDate(prev, viewMode, 1));
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs bg-white/5 hover:bg-white/10"
                  >
                    {nextLabel}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={goToNow}
                    className="px-3 py-1.5 rounded-xl ring-1 ring-indigo-400/50 text-xs bg-indigo-600/80 hover:bg-indigo-600 text-white"
                  >
                    Now
                  </button>
                  {surfaceMode === 'timeline' ? (
                    <>
                      <div className="flex items-center gap-1 rounded-xl bg-black/20 px-2 py-1 ring-1 ring-white/10 text-xs text-slate-300">
                        <span>Zoom</span>
                        <button
                          onClick={() => setTimelineZoom(tickWidth + TIMELINE_ZOOM_STEP)}
                          className="px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                        >
                          +
                        </button>
                        <button
                          onClick={() => setTimelineZoom(tickWidth - TIMELINE_ZOOM_STEP)}
                          className="px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                        >
                          -
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAds((current) => !current)}
                        className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${showAds ? 'bg-white/15 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                      >
                        Show Ads
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPlaylists((current) => !current)}
                        className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${showPlaylists ? 'bg-white/15 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                      >
                        Show Playlists
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap items-start gap-3">
                <div className="w-16 pt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Create</div>
                <div className="flex flex-1 flex-wrap gap-2">
                  <button
                    onClick={() => openNewAt(new Date())}
                    disabled={!devices.length}
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-indigo-600/90 hover:bg-indigo-600 disabled:opacity-60 text-xs"
                  >
                    <Plus className="w-4 h-4" /> New Event
                  </button>
                  <button
                    onClick={() => openGigEditor(null)}
                    disabled={!activeSiteId}
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-sky-600/90 hover:bg-sky-600 disabled:opacity-60 text-xs"
                  >
                    <Plus className="w-4 h-4" /> New Gig
                  </button>
                  <button
                    onClick={() => openCampaignEditor(null)}
                    disabled={!activeSiteId || !area?.id}
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-emerald-600/90 hover:bg-emerald-600 disabled:opacity-60 text-xs"
                  >
                    <Plus className="w-4 h-4" /> New Campaign
                  </button>
                  <button
                    onClick={openGigRecurringTemplateModal}
                    disabled={!activeSiteId}
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-violet-600/90 hover:bg-violet-600 disabled:opacity-60 text-xs"
                  >
                    <Plus className="w-4 h-4" /> Recurring Template
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4">
              {surfaceMode === 'timeline' ? (
                <div className="flex bg-white/5 rounded-xl overflow-hidden ring-1 ring-white/10">
                  <div className="w-40 shrink-0 border-r border-white/10 bg-white/5">
                    <div style={{ height: TICK_HEIGHT }} className="border-b border-white/10" />
                    <div
                      className="border-b border-indigo-300/20 bg-indigo-500/10 px-3 flex flex-col justify-center"
                      style={{ height: LANE_HEIGHT }}
                    >
                      <div className="font-semibold text-sm text-white">Gigs & Campaigns</div>
                      <div className="text-[11px] text-slate-400">
                        {gigTimelineLoading ? 'Loading…' : `${gigLaneRows.length} visible in range`}
                      </div>
                    </div>
                    {devices.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => {
                          setSelectedDeviceId(d.id);
                          onDeviceChange?.(d.id);
                        }}
                        className={`w-full text-left px-3 border-b border-white/10 flex flex-col justify-center ${
                          selectedDeviceId === d.id ? 'bg-white/10' : ''
                        }`}
                        style={{ height: LANE_HEIGHT }}
                      >
                        <div className="font-semibold text-sm text-white">{d.name}</div>
                        <div className="text-[11px] text-slate-400">{d.type}</div>
                      </button>
                    ))}
                    {!devices.length ? (
                      <div
                        className="px-3 flex items-center text-[11px] uppercase tracking-[0.2em] text-slate-500"
                        style={{ height: LANE_HEIGHT }}
                      >
                        No devices yet
                      </div>
                    ) : null}
                  </div>
                  <div
                    className="flex-1 overflow-x-auto overflow-y-hidden"
                    ref={timelineRef}
                  >
                    <div className="relative" style={{ width: laneWidth }}>
                      <div
                        className="flex text-xs text-slate-300 items-center border-b border-white/10"
                        style={{ width: laneWidth, height: TICK_HEIGHT }}
                      >
                        {renderTicks()}
                      </div>
                      <div style={{ position: 'relative' }}>
                        {gigsLaneContent()}
                      </div>
                      {devices.map((d) => (
                        <div key={d.id} style={{ position: 'relative' }}>
                          {laneContent(d)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : renderSchedulerList()}
            </div>
          </div>

          <div className="grid gap-4 min-w-0">
              <SectionCard
                title="Content & Commands"
                icon={Layers}
                actions={
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'apps', label: 'Apps' },
                      { key: 'links', label: 'Saved URIs' },
                      { key: 'playlist', label: 'Playlists' },
                      { key: 'events', label: 'Events' },
                      { key: 'ad', label: 'Ads' },
                      { key: 'power', label: 'Power' },
                      { key: 'default-ads', label: 'Default Ads' },
                      { key: 'overlay', label: 'Overlay Ads' },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${
                          activeTab === tab.key ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                }
              >
                {activeTab === 'apps' && (
                  <div className="grid gap-2">
                    <div className="text-xs text-slate-400">
                      Drag apps into the timeline to schedule when a device should launch them.
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-white/10 hover:bg-white/20 text-xs"
                        onClick={fetchApps}
                        disabled={appsStatus === 'loading'}
                      >
                        Refresh Apps
                      </button>
                    </div>
                    {appsMessage ? <div className="text-xs text-rose-200">{appsMessage}</div> : null}
                    {appsStatus === 'loading' && <div className="text-sm text-slate-400">Loading apps…</div>}
                    {assignedDestinations.map((destination) => <div key={destination.id}
                      draggable={destination.schedulerReady ?? destination.ready} aria-disabled={!(destination.schedulerReady ?? destination.ready)}
                      onDragStart={(event) => {
                        if (!(destination.schedulerReady ?? destination.ready)) { event.preventDefault(); return; }
                        event.dataTransfer.setData('app-package', destination.id);
                        event.dataTransfer.setData('app-label', destination.label);
                        event.dataTransfer.effectAllowed = 'copy';
                        applyStartAnchoredDragImage(event, destination.label, '#22d3ee');
                      }} className="px-3 py-2 rounded-xl bg-white/5 ring-1 ring-white/10">
                      <strong>{destination.label}</strong><div>{(destination.schedulerReady ?? destination.ready) ? 'Drag into timeline to schedule' : destination.scheduleIssue === 'shared_confirmation_required' ? 'Confirm shared Screen changes in Site Control → Apps' : 'Needs setup in Site Control → Apps'}</div>
                    </div>)}
                    {!enabledApps.length && !assignedDestinations.length && appsStatus !== 'loading' ? (
                      <div className="text-sm text-slate-400">No enabled, launchable apps found for this device.</div>
                    ) : null}
                    <div className="grid gap-2">
                      {enabledApps.map((app) => {
                        const label = app.app_name || app.package_name || 'Unknown App';
                        const intentReady = hasIntentLaunchRecipe(appProfilesByPackage[app.package_name]);
                        return (
                          <div
                            key={app.package_name || label}
                            data-app-package={app.package_name}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('app-package', String(app.package_name || ''));
                              e.dataTransfer.setData('app-label', String(label));
                              e.dataTransfer.effectAllowed = 'copy';
                              applyStartAnchoredDragImage(e, label, '#22d3ee');
                            }}
                            className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 cursor-grab"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="font-semibold text-sm text-white">{label}</div>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1 text-[11px] hover:bg-white/20"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onEditDeviceApps?.(selectedDeviceId);
                                }}
                              >
                                <Pencil className="h-3 w-3" /> Edit
                              </button>
                            </div>
                            <div className="text-xs text-slate-400">{app.package_name}</div>
                            {intentReady ? (
                              <div className="text-[11px] text-emerald-200 mt-1">Intent launch configured</div>
                            ) : null}
                            <div className="text-[11px] text-cyan-200 mt-1">Drag into timeline to schedule</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {activeTab === 'links' && (
                  <div className="grid gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs text-slate-400">
                        Drag a saved URI into the timeline. Links use the app and launch strategy selected when saved.
                      </div>
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-xl bg-cyan-500/20 px-3 py-2 text-xs text-cyan-100 hover:bg-cyan-500/30"
                        onClick={() => setLinkModalOpen(true)}
                      >
                        <Plus className="h-4 w-4" /> Add Link
                      </button>
                    </div>
                    {!savedLinks.length ? (
                      <div className="text-sm text-slate-400">No saved URIs for this device.</div>
                    ) : null}
                    {savedLinks.map((target) => (
                      <div
                        key={target.id}
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData('content-target-id', String(target.id));
                          event.dataTransfer.effectAllowed = 'copy';
                          applyStartAnchoredDragImage(event, target.name || 'Saved URI', '#14b8a6');
                        }}
                        className="cursor-grab rounded-xl bg-white/5 px-3 py-2 ring-1 ring-white/10 hover:bg-white/10"
                      >
                        <div className="flex items-center gap-2 font-semibold text-sm text-white">
                          <Link className="h-4 w-4 text-teal-300" /> {target.name || 'Saved URI'}
                        </div>
                        <div className="mt-1 text-xs text-slate-400">{target.appName || target.packageName}</div>
                        <div className="truncate text-[11px] text-teal-200">{target.seed?.url || ''}</div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'playlist' && (
                  <div className="grid gap-2">
                    {regularPlaylists.length ? (
                      regularPlaylists.map((p) => (
                        <div
                          key={p.id}
                          data-playlist-id={p.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('playlist-id', String(p.id));
                            e.dataTransfer.effectAllowed = 'copy';
                            applyStartAnchoredDragImage(e, p.name || `Playlist ${p.id}`, '#818cf8');
                          }}
                          className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 cursor-grab"
                        >
                          <div className="font-semibold text-sm text-white">{p.name}</div>
                          <div className="text-xs text-slate-400">{p.media_type || 'playlist'}</div>
                          <div className="text-[11px] text-indigo-200 mt-1">
                            Drag into timeline to schedule
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-slate-400">No playlists found.</div>
                    )}
                  </div>
                )}

                {activeTab === 'events' && (
                  <div className="grid gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs text-slate-400">
                        Upcoming events for this site. Open any entry to edit the gig and review artist submissions.
                      </div>
                      <label className="flex items-center gap-2 text-xs text-slate-300">
                        <span>Filter</span>
                        <select
                          value={eventsTabFilter}
                          onChange={(event) => setEventsTabFilter(event.target.value)}
                          className="rounded-xl bg-black/30 px-3 py-2 ring-1 ring-white/10"
                        >
                          <option value="all">All</option>
                          <option value="recurring">Recurring</option>
                          <option value="one-off">One-off</option>
                          <option value="multi-day">Multi-day</option>
                          <option value="needs-attention">Needs attention</option>
                        </select>
                      </label>
                    </div>

                    {gigListLoading ? <div className="text-sm text-slate-400">Loading upcoming events…</div> : null}

                    {!gigListLoading && upcomingEventSeriesItems.length ? (
                      upcomingEventSeriesItems.map((seriesItem) => {
                        const gig = seriesItem.currentGig;
                        const gigDisplayStatus = getGigDisplayStatus(gig);
                        const warnings = buildGigWarnings(gig);
                        const warningCount = warnings.length;
                        const summary = getGigSummary(gig);
                        return (
                          <div key={seriesItem.seriesKey} className="rounded-xl bg-white/5 px-3 py-3 ring-1 ring-white/10">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="text-sm font-semibold text-white">{gig.title || `Gig ${gig.id}`}</div>
                                  <Badge className={gigDisplayStatus === 'completed' ? 'bg-cyan-500/15 text-cyan-100' : 'bg-white/10 text-slate-200'}>
                                    {formatGigDisplayStatusLabel(gig)}
                                  </Badge>
                                  {isRecurringGig(gig) ? (
                                    <Badge className="bg-violet-500/15 text-violet-100">Recurring</Badge>
                                  ) : (
                                    <Badge className="bg-slate-500/20 text-slate-200">One-off</Badge>
                                  )}
                                  {seriesItem.isRecurringSeries ? (
                                    <Badge className="bg-violet-500/10 text-violet-100">
                                      {seriesItem.currentIndex + 1} / {seriesItem.totalCount}
                                    </Badge>
                                  ) : null}
                                  {isMultiDayGig(gig) ? (
                                    <Badge className="bg-sky-500/15 text-sky-100">Multi-day</Badge>
                                  ) : null}
                                  {warningCount ? (
                                    <Badge className="bg-amber-500/15 text-amber-100">{warningCount} warning{warningCount === 1 ? '' : 's'}</Badge>
                                  ) : null}
                                </div>
                                <div className="mt-1 text-xs text-slate-400">
                                  {new Date(gig.startsAtUtc).toLocaleString()} – {new Date(gig.endsAtUtc).toLocaleString()}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">{summary}</div>
                                {warningCount ? (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {warnings.map((warning) => (
                                      <span key={`${gig.id}:${warning}`} className="rounded-full bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100 ring-1 ring-amber-400/20">
                                        {warning}
                                      </span>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                {seriesItem.isRecurringSeries ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => jumpToGigOccurrence(gig, -1)}
                                      disabled={!seriesItem.hasPrevious}
                                      className="rounded-xl bg-white/10 px-2.5 py-2 text-xs font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      {'<<'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => jumpToGigOccurrence(gig, 1)}
                                      disabled={!seriesItem.hasNext}
                                      className="rounded-xl bg-white/10 px-2.5 py-2 text-xs font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      {'>>'}
                                    </button>
                                  </>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => openGigFromScheduler(gig)}
                                  className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20"
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                            <ProgrammeAutomationPreview
                              gig={gig}
                              siteId={site?.id || area?.site_id || area?.siteId}
                              targetGroups={targetGroups}
                              token={token}
                            />
                          </div>
                        );
                      })
                    ) : null}

                    {!gigListLoading && !upcomingEventSeriesItems.length ? (
                      <div className="text-sm text-slate-400">No upcoming events found for this filter.</div>
                    ) : null}
                  </div>
                )}

                {activeTab === 'event' && (
                  <div className="grid gap-2">
                    <div className="text-xs text-slate-400">
                      Drag live events into the timeline (exclusive). Drag teasers to show as non-blocking overlays.
                    </div>
                    {eventInstances.length ? (
                      eventInstances.map((inst) => (
                        <div key={inst.id} className="px-3 py-2 rounded-xl bg-white/5 ring-1 ring-white/10">
                          <div className="font-semibold text-sm text-white">{inst.name || `Event ${inst.id}`}</div>
                          <div className="text-xs text-slate-400">
                            Target: {inst.target_time ? new Date(inst.target_time).toLocaleString() : '—'}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">
                            Live window defaults to 1 hour before target.
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <div
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('event-instance-id', String(inst.id));
                                e.dataTransfer.setData('event-mode', 'live');
                                e.dataTransfer.effectAllowed = 'copy';
                                applyStartAnchoredDragImage(e, `${inst.name || `Event ${inst.id}`} • Live`, '#fb923c');
                              }}
                              className="px-3 py-2 rounded-xl bg-orange-500/15 hover:bg-orange-500/25 ring-1 ring-orange-200/20 cursor-grab text-xs text-orange-100 flex items-center justify-between"
                            >
                              <span>Live</span>
                              <span className="text-[10px] text-orange-200/80">Exclusive</span>
                            </div>
                            <div
                              draggable={
                                !!inst.teaser_enabled &&
                                !!selectedDeviceId &&
                                (eventsByDevice.get(selectedDeviceId) || []).some(
                                  (ev) =>
                                    ev.item_type === 'event' &&
                                    (ev.event_mode || 'live') === 'live' &&
                                    Number(ev.item_id) === Number(inst.id)
                                )
                              }
                              onDragStart={(e) => {
                                const hasLiveForSelected = !!selectedDeviceId && (eventsByDevice.get(selectedDeviceId) || []).some(
                                  (ev) =>
                                    ev.item_type === 'event' &&
                                    (ev.event_mode || 'live') === 'live' &&
                                    Number(ev.item_id) === Number(inst.id)
                                );
                                if (!inst.teaser_enabled || !hasLiveForSelected) return;
                                e.dataTransfer.setData('event-instance-id', String(inst.id));
                                e.dataTransfer.setData('event-mode', 'teaser');
                                e.dataTransfer.effectAllowed = 'copy';
                                applyStartAnchoredDragImage(e, `${inst.name || `Event ${inst.id}`} • Teaser`, '#818cf8');
                              }}
                              className={`px-3 py-2 rounded-xl ring-1 text-xs flex items-center justify-between ${
                                inst.teaser_enabled &&
                                !!selectedDeviceId &&
                                (eventsByDevice.get(selectedDeviceId) || []).some(
                                  (ev) =>
                                    ev.item_type === 'event' &&
                                    (ev.event_mode || 'live') === 'live' &&
                                    Number(ev.item_id) === Number(inst.id)
                                )
                                  ? 'bg-indigo-500/15 hover:bg-indigo-500/25 ring-indigo-200/20 cursor-grab text-indigo-100'
                                  : 'bg-white/5 ring-white/10 text-slate-400 opacity-60 cursor-not-allowed'
                              }`}
                            >
                              <span>Teaser</span>
                              <span className="text-[10px] text-indigo-200/80">30s</span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-slate-400">No event instances found.</div>
                    )}
                  </div>
                )}

                {activeTab === 'ad' && (
                  <div className="grid gap-3">
                    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 grid gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-white">Rotating Ads</div>
                          <div className="text-xs text-slate-400">
                            Full-screen ad playlists replace background video for the scheduled block.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={openCreateAdRotation}
                          disabled={rotationSaving || !availableFullScreenAds.length}
                          className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-rose-600/90 hover:bg-rose-600 disabled:opacity-60 text-xs"
                        >
                          <Plus className="w-4 h-4" /> Add Rotating Ads
                        </button>
                      </div>

                      <div className="grid md:grid-cols-[minmax(220px,280px),1fr] gap-3">
                        <div className="grid gap-2 content-start">
                          {adRotations.map((rotation) => {
                            const selected = String(selectedAdRotation?.id) === String(rotation.id);
                            return (
                              <div
                                key={rotation.id}
                                draggable
                                onClick={() => setSelectedAdRotationId(rotation.id)}
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('ad-rotation-id', String(rotation.id));
                                  e.dataTransfer.effectAllowed = 'copy';
                                  applyStartAnchoredDragImage(e, rotation.name || `Ad Rotation ${rotation.id}`, '#f87171');
                                }}
                                className={`px-3 py-2 rounded-xl ring-1 cursor-grab ${
                                  selected ? 'bg-rose-500/15 ring-rose-200/40' : 'bg-white/5 hover:bg-white/10 ring-white/10'
                                }`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="font-semibold text-sm text-white truncate">{rotation.name}</div>
                                  <Badge className="bg-white/10">{Number(rotation.duration_sec) || 60}s</Badge>
                                </div>
                                <div className="text-xs text-slate-400">
                                  {rotation.mode === 'all' ? 'Everything' : `${rotation.item_count || 0} selected`}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                  <span className="text-[11px] text-rose-100">Drag into timeline</span>
                                </div>
                              </div>
                            );
                          })}
                          {!adRotations.length ? (
                            <div className="text-sm text-slate-400">No ad rotations available.</div>
                          ) : null}
                        </div>

                        <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3 grid gap-3">
                          {selectedAdRotation ? (
                            <AdRotationEditor
                              key={selectedAdRotation.id}
                              rotationForm={rotationForm}
                              setRotationForm={setRotationForm}
                              rotationEditing={selectedAdRotation}
                              adRotations={adRotations}
                              availableFullScreenAds={availableFullScreenAds}
                              playableFullScreenAds={playableFullScreenAds}
                              rotationSaving={rotationSaving}
                              onCancel={resetAdRotationForm}
                              onSave={() => saveAdRotation(rotationForm, selectedAdRotation)}
                            />
                          ) : (
                            <div className="text-sm text-slate-400">Select a saved rotation to edit it.</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-slate-400">
                        Drag a visual overlay or announcement into the timeline to create an ad schedule.
                      </div>
                      <button
                        onClick={() => openAdPlacementAt({})}
                        className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-indigo-600/90 hover:bg-indigo-600 text-xs"
                      >
                        <Plus className="w-4 h-4" /> New Ad
                      </button>
                    </div>

                    <label className="flex items-center gap-2 text-xs text-slate-200">
                      <input
                        type="checkbox"
                        checked={!!adSettings?.ad_breaks}
                        onChange={(e) => toggleAdBreaks(e.target.checked)}
                      />
                      Ad Breaks (group ads together)
                    </label>

                    <div className="grid gap-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs text-slate-300 font-semibold">Available ad content</div>
                        <div className="flex flex-wrap gap-2">
                          {['full', 'top', 'bottom', 'announcements'].map((t) => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => setOverlayTypeFilter(t)}
                              className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${
                                overlayTypeFilter === t ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              {t === 'announcements' ? 'Announcements' : t.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                      {overlayTypeFilter === 'announcements' ? (
                        announcements.length ? (
                          announcements.map((item) => (
                            <div
                              key={item.id}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('announcement-id', String(item.id));
                                e.dataTransfer.effectAllowed = 'copy';
                                applyStartAnchoredDragImage(e, item.title || `Announcement ${item.id}`, '#22d3ee');
                              }}
                              className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 cursor-grab"
                            >
                              <div className="font-semibold text-sm text-white">{item.title || `Announcement ${item.id}`}</div>
                              <div className="text-xs text-slate-400">Audio announcement</div>
                              <div className="text-[11px] text-cyan-200 mt-1">Drag into timeline to schedule</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-slate-400">No announcements found.</div>
                        )
                      ) : (
                        (overlays || [])
                          .filter((o) => !isDefaultAdOverlay(o))
                          .filter((o) => {
                            if (!activeSiteId) return true;
                            if (o.site_id == null) return true;
                            return String(o.site_id) === String(activeSiteId);
                          })
                          .filter((o) => (o.type || '').toLowerCase() === activeOverlayVisualFilter)
                          .map((o) => (
                            <div
                              key={o.id}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('overlay-id', String(o.id));
                                e.dataTransfer.effectAllowed = 'copy';
                                applyStartAnchoredDragImage(e, o.name || `Overlay ${o.id}`, '#a78bfa');
                              }}
                              className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 cursor-grab"
                            >
                              <div className="font-semibold text-sm text-white">{o.name || `Overlay ${o.id}`}</div>
                              <div className="text-xs text-slate-400">{o.type || 'overlay'}</div>
                              <div className="text-[11px] text-indigo-200 mt-1">Drag into timeline to schedule</div>
                            </div>
                          ))
                      )}
                      {overlayTypeFilter !== 'announcements' && !overlays.length ? (
                        <div className="text-sm text-slate-400">No overlays found.</div>
                      ) : null}
                    </div>

                    <div className="grid gap-2">
                      <div className="text-xs text-slate-300 font-semibold">Scheduled ads</div>
                      {visibleAdSeries.length ? (
                        visibleAdSeries.map((s) => {
                          const contentType = getAdContentType(s);
                          const ov = contentType === 'overlay' ? overlayCache[s.overlay_id] : null;
                          const announcement = contentType === 'announcement' ? announcementCache[s.announcement_id] : null;
                          const title =
                            contentType === 'announcement'
                              ? announcement?.title || `Announcement ${s.announcement_id}`
                              : ov?.name || `Overlay ${s.overlay_id}`;
                          const type = contentType === 'announcement' ? 'announcement' : ov?.type || null;
                          const selectionId = getSeriesSelectionId(s) || Number(s.id);
                          const style = getAdSeriesColorStyle(selectionId || type || title);
                          const selected = selectedAdSeriesId === selectionId;
                          return (
                            <div
                              key={selectionId || s.id}
                              className={`px-3 py-2 rounded-xl ring-1 flex items-center justify-between gap-2 ${
                                style.rowClassName || 'bg-white/5 ring-white/10'
                              } ${
                                selected ? 'ring-white/60' : ''
                              }`}
                              onClick={() => openAdSeriesEditor(s)}
                            >
                              <div className="min-w-0">
                                <div className="text-sm text-white font-semibold truncate">{title}</div>
                                <div className="text-[11px] text-slate-400">
                                  {contentType !== 'announcement' && s.play_continuously
                                    ? 'Play continuously'
                                    : `${Number(s.impressions_per_hour) || 6}/hr • ${Number(s.duration_sec) || 60}s`}
                                </div>
                                <div className="text-[11px] text-slate-500">
                                  {s.repeat_mode === 'daily' ? 'Every day' : (s.schedule_date || formatDateInputValue(s.start_time))}
                                  {' • '}
                                  {s.all_day
                                    ? 'All day within power hours'
                                    : `${s.start_time_local || formatTimeInputValue(s.start_time)}-${s.end_time_local || formatTimeInputValue(s.end_time)}`}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                  <span className={`inline-flex w-3 h-3 rounded ${style.dotClassName || 'bg-white'}`} />
                                  <span className="text-[10px] text-slate-500">{String(type || 'unknown')}</span>
                                  {Array.isArray(s.sync_device_ids) && s.sync_device_ids.length ? (
                                    <span className="text-[10px] text-cyan-200">
                                      Syncs to {s.sync_device_ids.length} TV{s.sync_device_ids.length === 1 ? '' : 's'}
                                    </span>
                                  ) : null}
                                  {contentType === 'announcement' && Number(s.hold_visual_seconds) > 0 ? (
                                    <span className="text-[10px] text-cyan-200">
                                      Hold visual +{Number(s.hold_visual_seconds)}s
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              <button
                                className="text-[10px] px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAdSeriesEditor(s);
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-sm text-slate-400">No ads scheduled yet.</div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'overlay' && (
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-slate-400">Drag an overlay into the timeline to schedule it once.</div>
                      <div className="flex flex-wrap gap-2">
                        {['full', 'top', 'bottom'].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setOverlayTypeFilter(t)}
                            className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${
                              activeOverlayVisualFilter === t ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            {t.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid gap-2">
                      {(overlays || [])
                        .filter((o) => !isDefaultAdOverlay(o))
                        .filter((o) => {
                          if (!activeSiteId) return true;
                          if (o.site_id == null) return true;
                          return String(o.site_id) === String(activeSiteId);
                        })
                        .filter((o) => (o.type || '').toLowerCase() === activeOverlayVisualFilter)
                        .map((o) => (
                          <div
                            key={o.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('manual-overlay-id', String(o.id));
                              e.dataTransfer.effectAllowed = 'copy';
                              applyStartAnchoredDragImage(e, o.name || `Overlay ${o.id}`, '#a78bfa');
                            }}
                            className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 cursor-grab"
                          >
                            <div className="font-semibold text-sm text-white">{o.name || `Overlay ${o.id}`}</div>
                            <div className="text-xs text-slate-400">{o.type || 'overlay'}</div>
                            <div className="text-[11px] text-indigo-200 mt-1">Drag into timeline to schedule</div>
                          </div>
                        ))}
                      {!overlays.length && <div className="text-sm text-slate-400">No overlays found.</div>}
                    </div>
                  </div>
                )}

                {activeTab === 'default-ads' && (
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-slate-400">Active (URL-filled) default ads for this site.</div>
                      <div className="flex gap-2">
                        {['full', 'top', 'bottom'].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setOverlayTypeFilter(t)}
                            className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${
                              activeOverlayVisualFilter === t ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            {t.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid gap-2">
                      {defaultAdOverlays
                        .filter((o) => (o.type || '').toLowerCase() === activeOverlayVisualFilter)
                        .map((o) => (
                          <div
                            key={o.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('manual-overlay-id', String(o.id));
                              e.dataTransfer.effectAllowed = 'copy';
                              applyStartAnchoredDragImage(e, o.default_ad_label || o.name || `Overlay ${o.id}`, '#a78bfa');
                            }}
                            className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 cursor-grab"
                          >
                            <div className="font-semibold text-sm text-white">{o.default_ad_label || o.name}</div>
                            <div className="text-xs text-slate-400">{o.type || 'overlay'}</div>
                            <div className="text-[11px] text-indigo-200 mt-1">Drag into timeline to schedule</div>
                          </div>
                        ))}
                      {!defaultAdOverlays.length && (
                        <div className="text-sm text-slate-400">
                          No active default ads yet. Fill in URLs in Settings → Site Details.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'power' && (
                  <div className="grid gap-4">
                    <div className="text-xs text-slate-400">
                      Set venue-wide power hours for the selected devices. Outside these hours the timeline is blocked and playback is prevented.
                    </div>
                    {powerEditor.enabled && !assistantStatus.loading && !assistantStatus.connected ? (
                      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-amber-100 bg-amber-500/10 border border-amber-300/20 rounded-xl px-3 py-3">
                        <div className="min-w-0">
                          Google Assistant {assistantStatus.needsReauth ? 'needs reconnecting' : 'is not connected'} for this site.
                          {' '}Power schedules will still block the timeline, but automatic{' '}
                          <span className="font-semibold">{'Power on/off <device name>'}</span>{' '}commands will not be sent until it is linked.
                          {assistantStatus.lastError ? ` Last error: ${assistantStatus.lastError}.` : ''}
                        </div>
                        <button
                          type="button"
                          onClick={() => window.location.assign('/control-panel?tab=Settings&settingsSubTab=Integrations')}
                          className="shrink-0 rounded-xl px-3 py-2 bg-amber-400/15 hover:bg-amber-400/25 ring-1 ring-amber-200/20"
                        >
                          Open Integrations
                        </button>
                      </div>
                    ) : null}
                    {powerEditor.mixed ? (
                      <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-300/20 rounded-xl px-3 py-2">
                        The selected devices currently have different power schedules. Saving here will overwrite them with one shared schedule.
                      </div>
                    ) : null}
                    <div className="grid xl:grid-cols-[minmax(240px,1fr),minmax(200px,260px),minmax(220px,280px),auto] gap-4 items-start">
                      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 grid gap-3">
                        <label className="flex items-center justify-between gap-3 text-sm text-slate-100">
                          <span>Select all devices</span>
                          <input
                            type="checkbox"
                            className="accent-emerald-500"
                            checked={allPowerDevicesSelected}
                            onChange={(e) => {
                              setPowerSelectedDeviceIds(
                                e.target.checked ? devices.map((entry) => entry.id) : []
                              );
                            }}
                          />
                        </label>
                        <div className="max-h-64 overflow-auto pr-1 grid gap-2">
                          {devices.map((entry) => {
                            const checked = powerSelectedDeviceIds.includes(entry.id);
                            return (
                              <label
                                key={entry.id}
                                className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 ring-1 ${
                                  checked ? 'bg-white/10 ring-white/20' : 'bg-white/5 ring-white/10'
                                }`}
                              >
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-white truncate">{entry.name}</div>
                                  <div className="text-[11px] text-slate-400 truncate">{entry.type || entry.time_zone || 'Device'}</div>
                                </div>
                                <input
                                  type="checkbox"
                                  className="accent-emerald-500"
                                  checked={checked}
                                  onChange={(e) => {
                                    setPowerSelectedDeviceIds((prev) => {
                                      if (e.target.checked) {
                                        return Array.from(new Set([...prev, entry.id]));
                                      }
                                      return prev.filter((id) => id !== entry.id);
                                    });
                                  }}
                                />
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 grid gap-3">
                        <label className="flex items-center justify-between gap-3 text-sm text-slate-100">
                          <span>Enabled</span>
                          <input
                            type="checkbox"
                            className="accent-emerald-500"
                            checked={!!powerEditor.enabled}
                            onChange={(e) => setPowerEditor((prev) => ({ ...prev, enabled: e.target.checked, mixed: false }))}
                          />
                        </label>
                        <div className="grid gap-2">
                          <label className="text-xs text-slate-400">On time</label>
                          <input
                            type="time"
                            className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                            value={everyDayPowerSchedule.on || '09:00'}
                            disabled={powerEditor.mode !== 'every_day'}
                            onChange={(e) => updateEveryDayField('on', e.target.value)}
                          />
                        </div>
                        <div className="grid gap-2">
                          <label className="text-xs text-slate-400">Off time</label>
                          <input
                            type="time"
                            className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                            value={everyDayPowerSchedule.off || '17:00'}
                            disabled={powerEditor.mode !== 'every_day'}
                            onChange={(e) => updateEveryDayField('off', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 grid gap-3">
                        <div className="grid gap-2">
                          <label className="text-xs text-slate-400">Schedule mode</label>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setPowerMode('every_day')}
                              className={`px-3 py-2 rounded-xl ring-1 ring-white/10 text-xs ${
                                powerEditor.mode === 'every_day' ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              Every day
                            </button>
                            <button
                              type="button"
                              onClick={() => setPowerMode('per_day')}
                              className={`px-3 py-2 rounded-xl ring-1 ring-white/10 text-xs ${
                                powerEditor.mode === 'per_day' ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              Different each day
                            </button>
                          </div>
                        </div>

                        {powerEditor.mode === 'per_day' ? (
                          <div className="grid gap-2">
                            {DAY_KEYS.map((dayKey) => {
                              const day = powerEditor.schedule?.[dayKey] || { enabled: false, on: '09:00', off: '17:00' };
                              return (
                                <div key={dayKey} className="grid grid-cols-[88px,auto,1fr,1fr] items-center gap-2 text-xs">
                                  <span className="text-slate-300">{DAY_LABELS[dayKey]}</span>
                                  <input
                                    type="checkbox"
                                    className="accent-emerald-500"
                                    checked={!!day.enabled}
                                    onChange={(e) => updatePerDaySchedule(dayKey, { enabled: e.target.checked })}
                                  />
                                  <input
                                    type="time"
                                    className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                                    value={day.on || '09:00'}
                                    disabled={!day.enabled}
                                    onChange={(e) => updatePerDaySchedule(dayKey, { on: e.target.value })}
                                  />
                                  <input
                                    type="time"
                                    className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                                    value={day.off || '17:00'}
                                    disabled={!day.enabled}
                                    onChange={(e) => updatePerDaySchedule(dayKey, { off: e.target.value })}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-400">
                            One on/off window will be applied to all seven days.
                          </div>
                        )}

                        <div className="text-xs text-slate-500">
                          {hasSiteOpeningHours
                            ? 'Defaults were loaded from Site Details opening hours.'
                            : 'No Google opening hours were found, so a manual schedule is shown.'}
                        </div>
                      </div>

                      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 flex flex-col gap-3">
                        <button
                          type="button"
                          onClick={savePowerSchedule}
                          disabled={powerSaving || powerLoading || !powerSelectedDeviceIds.length}
                          className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 bg-emerald-600/90 hover:bg-emerald-600 disabled:opacity-60"
                        >
                          <Save className="w-4 h-4" />
                          {powerSaving ? 'Saving…' : 'Save'}
                        </button>
                        <div className="text-xs text-slate-400">
                          Selected devices: {powerSelectedDeviceIds.length || 0}
                        </div>
                        <div className="text-xs text-slate-500">
                          On/Off markers appear on the timeline after save and closed time is shaded as unschedulable.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </SectionCard>
            </div>
          </div>

        {message ? (
          <div className="mt-4 text-sm text-emerald-300 bg-emerald-900/30 border border-emerald-700/50 px-3 py-2 rounded">
            {message}
          </div>
        ) : null}
        {errorMessage ? (
          <div className="mt-4 text-sm text-rose-200 bg-rose-950/50 border border-rose-700/60 px-3 py-2 rounded" role="alert">
            {errorMessage}
          </div>
        ) : null}

        <Modal
          open={programmeModalOpen}
          onClose={() => !programmeSaving && setProgrammeModalOpen(false)}
          title={selectedProgramme?.id ? selectedProgramme.title : 'New Venue Campaign'}
        >
          {programmeForm ? (
            <div className="grid gap-4">
              <div className="rounded-xl bg-cyan-500/10 p-3 text-xs text-cyan-50 ring-1 ring-cyan-300/20">
                This creates a venue campaign such as Happy Hour. Use New Gig for performers and live music.
                Times are entered in your local timezone and campaign blocks snap to five minutes when dragged.
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-300">Type</span>
                  <select
                    value={programmeForm.programmeType}
                    disabled
                    className="rounded-xl bg-black/30 p-2 text-slate-300 ring-1 ring-white/10"
                  >
                    <option value="promotion">Venue campaign</option>
                  </select>
                </label>
                <label className="grid gap-1 text-sm md:col-span-2">
                  <span className="text-slate-300">Title</span>
                  <input
                    value={programmeForm.title}
                    onChange={(event) => setProgrammeForm((current) => ({ ...current, title: event.target.value }))}
                    placeholder="Happy Hour"
                    className="rounded-xl bg-black/30 p-2 ring-1 ring-white/10"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-300">Starts</span>
                  <input
                    type="datetime-local"
                    step="1"
                    value={programmeForm.startsAtUtc}
                    onChange={(event) => setProgrammeForm((current) => ({ ...current, startsAtUtc: event.target.value }))}
                    className="rounded-xl bg-black/30 p-2 ring-1 ring-white/10"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-300">Ends</span>
                  <input
                    type="datetime-local"
                    step="1"
                    value={programmeForm.endsAtUtc}
                    onChange={(event) => setProgrammeForm((current) => ({ ...current, endsAtUtc: event.target.value }))}
                    className="rounded-xl bg-black/30 p-2 ring-1 ring-white/10"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-300">Target group</span>
                  <select
                    value={programmeForm.targetGroupId}
                    onChange={(event) => setProgrammeForm((current) => ({ ...current, targetGroupId: event.target.value }))}
                    className="rounded-xl bg-black/30 p-2 ring-1 ring-white/10"
                  >
                    <option value="">Choose a target group</option>
                    {targetGroups.map((group) => (
                      <option key={group.id} value={group.id} disabled={!group.resolution?.diagnostics?.canPublish}>
                        {group.name}{group.resolution?.diagnostics?.partial ? ' · partial' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-300">Template</span>
                  <select
                    value={programmeForm.templateKey}
                    disabled
                    className="rounded-xl bg-black/30 p-2 text-slate-300 ring-1 ring-white/10"
                  >
                    <option value="venue-promotion-standard">Venue promotion</option>
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  {selectedProgramme?.id && selectedProgramme.status !== 'cancelled' ? (
                    <button
                      type="button"
                      onClick={cancelCampaign}
                      disabled={programmeSaving}
                      className="rounded-xl bg-rose-600/80 px-3 py-2 text-sm hover:bg-rose-600 disabled:opacity-50"
                    >
                      Cancel campaign
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={saveCampaignDraft}
                  disabled={programmeSaving || !programmeForm.title.trim() || !programmeForm.targetGroupId}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold hover:bg-emerald-500 disabled:opacity-50"
                >
                  {programmeSaving ? 'Saving…' : selectedProgramme?.id ? 'Save changes' : 'Create draft & preview'}
                </button>
              </div>
              {selectedProgramme?.id ? (
                <ProgrammeAutomationPreview
                  occurrence={selectedProgramme}
                  siteId={activeSiteId}
                  targetGroups={targetGroups}
                  token={token}
                  onChanged={fetchGigLane}
                />
              ) : null}
            </div>
          ) : selectedProgramme?.id ? (
            <div className="grid gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
                <div className="text-sm text-slate-300">
                  <span className="font-semibold text-white">{selectedProgramme.programmeType}</span>
                  {' · '}{selectedProgramme.status} · {selectedProgramme.runtimeStatus}
                </div>
                {selectedProgramme.sourceType === 'gig' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setProgrammeModalOpen(false);
                      openGigEditor(gigsById.get(Number(selectedProgramme.sourceId)) || { id: Number(selectedProgramme.sourceId) });
                    }}
                    className="rounded-xl bg-sky-600 px-3 py-2 text-sm font-semibold hover:bg-sky-500"
                  >
                    Edit gig details
                  </button>
                ) : null}
              </div>
              <ProgrammeAutomationPreview
                occurrence={selectedProgramme}
                siteId={activeSiteId}
                targetGroups={targetGroups}
                token={token}
                onChanged={fetchGigLane}
              />
            </div>
          ) : null}
        </Modal>

        <GigEditorModal
          open={gigModalOpen}
          gigId={selectedGigId}
          siteId={activeSiteId}
          siteName={site?.name || site?.site_name || area?.site_name || area?.siteName || ''}
          defaultStart={pendingGigWindow?.start || null}
          defaultEnd={pendingGigWindow?.end || null}
          hasPreviousGig={!!selectedGigOccurrenceState?.hasPrevious}
          hasNextGig={!!selectedGigOccurrenceState?.hasNext}
          onPreviousGig={() => jumpModalGigOccurrence(-1)}
          onNextGig={() => jumpModalGigOccurrence(1)}
          onClose={closeGigEditor}
          onChanged={handleGigChanged}
          onMessage={setMessage}
        />

        <GigRecurringTemplateModal
          open={gigRecurringTemplateModalOpen}
          siteId={activeSiteId}
          onClose={closeGigRecurringTemplateModal}
          onGenerated={handleGigChanged}
          onMessage={setMessage}
        />

        <Modal
          open={linkModalOpen}
          onClose={() => !linkSaving && setLinkModalOpen(false)}
          title="Add saved URI"
        >
          <div className="grid gap-4">
            <label className="grid gap-1 text-sm">
              <span className="text-slate-300">Link</span>
              <input
                type="url"
                className="rounded-xl bg-black/30 p-2 ring-1 ring-white/10"
                placeholder="https://open.spotify.com/playlist/..."
                value={linkForm.url}
                onChange={(event) => setLinkForm((prev) => ({ ...prev, url: event.target.value }))}
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-slate-300">Name (optional)</span>
              <input
                className="rounded-xl bg-black/30 p-2 ring-1 ring-white/10"
                placeholder="Morning Spotify playlist"
                value={linkForm.title}
                onChange={(event) => setLinkForm((prev) => ({ ...prev, title: event.target.value }))}
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-slate-300">Open with</span>
              <select
                className="rounded-xl bg-black/30 p-2 ring-1 ring-white/10"
                value={linkForm.profileId}
                onChange={(event) => setLinkForm((prev) => ({ ...prev, profileId: event.target.value }))}
              >
                <option value="">Choose an enabled app</option>
                {appProfiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.displayName || profile.packageName}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                checked={linkForm.showOnPhoneRemote}
                onChange={(event) => setLinkForm((prev) => ({
                  ...prev,
                  showOnPhoneRemote: event.target.checked,
                }))}
              />
              Also show this link on the phone remote
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl bg-white/10 px-3 py-2 hover:bg-white/20"
                onClick={() => setLinkModalOpen(false)}
                disabled={linkSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-emerald-600 px-3 py-2 hover:bg-emerald-500 disabled:opacity-50"
                onClick={handleSaveLink}
                disabled={linkSaving || !linkForm.url.trim() || !linkForm.profileId}
              >
                {linkSaving ? 'Saving…' : 'Save Link'}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editing?.id || editing?.fromDrop ? 'Edit Event' : 'New Event'}
        >
          {editing && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <div className="grid gap-1">
                  <label className="text-xs text-slate-300">Type</label>
                  <div className="flex flex-wrap gap-2">
                    {ITEM_TYPES.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setEditing({ ...editing, item_type: t.id })}
                        className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 ${
                          editing.item_type === t.id ? 'bg-indigo-600/90' : 'bg-white/10 hover:bg-white/20'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-1">
                  <label className="text-xs text-slate-300">Venue target</label>
                  <select
                    value={editing.targetGroupId || ''}
                    disabled={Boolean(editing.id && editing.targetGroupId)}
                    onChange={(event) => {
                      const targetGroupId = Number(event.target.value) || null;
                      const group = targetGroups.find((entry) => entry.id === targetGroupId);
                      const resolvedIds = group
                        ? resolveSchedulerTargetGroupDeviceIds(group, editing.item_type)
                        : editing.deviceIds;
                      setEditing({
                        ...editing,
                        targetGroupId,
                        deviceIds: targetGroupId ? resolvedIds : editing.deviceIds,
                      });
                    }}
                    className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                  >
                    <option value="">Choose devices manually</option>
                    {targetGroups.map((group) => (
                      <option key={group.id} value={group.id} disabled={!group.resolution?.diagnostics?.canPublish}>
                        {group.name}{group.resolution?.diagnostics?.partial ? ' · partial' : ''}
                      </option>
                    ))}
                  </select>
                  {editing.targetGroupId ? (
                    <>
                      <div className="text-xs text-slate-400">
                        {targetGroupAvailabilityMessage(
                          targetGroups.find((group) => group.id === Number(editing.targetGroupId))
                        )}
                      </div>
                      <label className="text-xs text-slate-300">Programme phase</label>
                      <select
                        value={editing.phaseKey || (editing.event_mode === 'teaser' ? 'teaser' : 'live')}
                        onChange={(event) => setEditing({ ...editing, phaseKey: event.target.value })}
                        className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                      >
                        <option value="live">Live / main programme</option>
                        <option value="teaser">Teaser</option>
                        <option value="starts_soon">Starts soon</option>
                        <option value="welcome">Welcome / starting</option>
                        <option value="now_playing">Now playing</option>
                        <option value="break">Break</option>
                        <option value="hard_sell">Hard sell</option>
                      </select>
                    </>
                  ) : null}
                  <label className="text-xs text-slate-300">Resolved devices</label>
                  <select
                    multiple
                    value={editing.deviceIds}
                    disabled={Boolean(editing.targetGroupId)}
                    onChange={(e) => {
                      const arr = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
                      setEditing({ ...editing, deviceIds: arr, targetGroupId: null });
                    }}
                    className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2 h-28"
                  >
                    {devices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-slate-400">Timezone: {editing.timezone || 'UTC'}</div>
                </div>

                <div className="grid gap-1">
                  <label className="text-xs text-slate-300">Content / Command</label>
                  {editing.item_type === 'playlist' && (
                    <select
                      className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                      value={editing.item_id}
                      onChange={(e) => {
                        const newId = Number(e.target.value);
                        const startDate = parseDateTimeLocal(editing.start);
                        const dur =
                          playlistDurations[newId] ||
                          (parseDateTimeLocal(editing.end).getTime() - startDate.getTime());
                        const newEnd = new Date(startDate.getTime() + dur);
                        setEditing({
                          ...editing,
                          item_id: newId,
                          end: fmtDateTimeLocal(newEnd),
                        });
                      }}
                    >
                      <option value="">— choose playlist —</option>
                      {regularPlaylists.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {editing.item_type === 'ad' && (
                    <select
                      className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                      value={editing.item_id}
                      onChange={(e) => {
                        const newId = Number(e.target.value);
                        const startDate = parseDateTimeLocal(editing.start);
                        const dur =
                          playlistDurations[newId] ||
                          (parseDateTimeLocal(editing.end).getTime() - startDate.getTime());
                        const newEnd = new Date(startDate.getTime() + dur);
                        setEditing({
                          ...editing,
                          item_id: newId,
                          end: fmtDateTimeLocal(newEnd),
                        });
                      }}
                    >
                      <option value="">— choose ad rotation —</option>
                      {adRotations.map((rotation) => (
                        <option key={rotation.id} value={rotation.playlist_id || rotation.id}>
                          {rotation.name} ({Number(rotation.duration_sec) || 60}s)
                        </option>
                      ))}
                    </select>
                  )}
                  {editing.item_type === 'app' && (
                    <div className="grid gap-2">
                      <select
                        className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                        value={editing.item_id || ''}
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          if (!selectedValue) {
                            setEditing({ ...editing, item_id: '' });
                            return;
                          }
                          const startDate = parseDateTimeLocal(editing.start);
                          const newEnd = new Date(startDate.getTime() + DEFAULT_APP_DURATION_MS);
                          setEditing({
                            ...editing,
                            item_id: selectedValue,
                            end: fmtDateTimeLocal(newEnd),
                          });
                        }}
                      >
                        <option value="">— choose app or saved URI —</option>
                        <optgroup label="Apps">
                          {enabledApps.map((app) => (
                            <option key={app.package_name || app.app_name} value={app.package_name}>
                              {app.app_name || app.package_name}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Assigned apps and channels">
                          {assignedDestinations.map((destination) => <option key={destination.id} value={destination.id} disabled={!(destination.schedulerReady ?? destination.ready)}>{destination.label}</option>)}
                        </optgroup>
                        <optgroup label="Saved URIs">
                          {savedLinks.map((target) => (
                            <option key={target.id} value={encodeScheduledContentTarget(target.id)}>
                              {target.name} — {target.appName || target.packageName}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                      {!['target', 'assigned'].includes(parseScheduledAppTarget(editing.item_id).mode)
                        && parseScheduledAppTarget(editing.item_id).packageName ? (
                        <div className="grid gap-1">
                          <label className="text-xs text-slate-300">Launch method</label>
                          <select
                            className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                            value={parseScheduledAppTarget(editing.item_id).mode}
                            onChange={(e) => {
                              const target = parseScheduledAppTarget(editing.item_id);
                              setEditing({
                                ...editing,
                                item_id: encodeScheduledAppTarget(target.packageName, e.target.value),
                              });
                            }}
                          >
                            <option value="package">Open app normally</option>
                            <option
                              value="intent"
                              disabled={!hasIntentLaunchRecipe(
                                appProfilesByPackage[parseScheduledAppTarget(editing.item_id).packageName]
                              )}
                            >
                              Launch with configured Intent
                            </option>
                          </select>
                          {!hasIntentLaunchRecipe(
                            appProfilesByPackage[parseScheduledAppTarget(editing.item_id).packageName]
                          ) ? (
                            <div className="text-[11px] text-slate-400">
                              Configure and enable a URI launch recipe in Apps before scheduling an Intent launch.
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  )}
                  {editing.item_type !== 'playlist' && editing.item_type !== 'ad' && editing.item_type !== 'app' && (
                    <input
                      className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                      placeholder="Command or Overlay ID"
                      value={editing.item_id}
                      onChange={(e) => setEditing({ ...editing, item_id: e.target.value })}
                    />
                  )}
                </div>
              </div>

              <div className="grid gap-2">
                <div className="grid md:grid-cols-2 gap-2">
                  <div className="grid gap-1">
                    <label className="text-xs text-slate-300">Start</label>
                    <input
                      type="datetime-local"
                      step="1"
                      className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                      value={editing.start}
                      onChange={(e) => setEditing({ ...editing, start: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-1">
                    <label className="text-xs text-slate-300">End</label>
                    <input
                      type="datetime-local"
                      step="1"
                      className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                      value={editing.end}
                      onChange={(e) => setEditing({ ...editing, end: e.target.value })}
                    />
                  </div>
                </div>

                {(editing.item_type === 'playlist' || editing.item_type === 'ad') && (
                  <label className="flex items-center gap-2 text-xs text-slate-200">
                    <input
                      type="checkbox"
                      className="accent-indigo-500"
                      checked={!!editing.randomize}
                      onChange={(e) => setEditing({ ...editing, randomize: e.target.checked })}
                    />
                    Randomize playlist before playback
                  </label>
                )}

                <div className="grid gap-1">
                  <label className="text-xs text-slate-300">Recurrence</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'None', rule: '' },
                      { label: 'Daily', rule: 'FREQ=DAILY' },
                      { label: 'Weekly', rule: 'FREQ=WEEKLY' },
                      { label: 'Monthly', rule: 'FREQ=MONTHLY' },
                      { label: 'Yearly', rule: 'FREQ=YEARLY' },
                    ].map((opt) => (
                      <button
                        key={opt.label}
                        onClick={() => setEditing({ ...editing, recurrence_rule: opt.rule })}
                        className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 ${
                          editing.recurrence_rule === opt.rule ? 'bg-indigo-600/90' : 'bg-white/10 hover:bg-white/20'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="text-xs text-slate-400">
                    Rule: {editing.recurrence_rule || '—'} ({rruleLabel(editing.recurrence_rule)})
                  </div>
                </div>

                {editing.item_type === 'ad' && (
                  <button
                    type="button"
                    onClick={applyAllDayEveryDayRotation}
                    disabled={saving || !editing.start}
                    className="justify-self-start rounded-xl bg-white/10 px-3 py-2 text-xs text-white hover:bg-white/20 disabled:opacity-60"
                  >
                    All Day Every Day
                  </button>
                )}

                <div className="flex items-center justify-between mt-2">
                  <div className="text-xs text-slate-400">
                    Summary:{' '}
                    <span className="text-slate-200 font-medium text-white">
                      {summary}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {editing.id && (
                      <button
                        onClick={() => deleteEvent(editing.id)}
                        className="px-3 py-2 rounded-xl bg-red-500/15 hover:bg-red-500/25"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={saveEvent}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-emerald-600/90 hover:bg-emerald-600 disabled:opacity-60"
                    >
                      <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          open={manualOverlayModalOpen}
          onClose={() => {
            setManualOverlayModalOpen(false);
            setManualOverlayEditing(null);
          }}
          title="Edit Scheduled Overlay"
        >
          {manualOverlayEditing && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-xs text-slate-300">Overlay</label>
                <select
                  className="px-3 py-2 rounded-xl bg-white/10 ring-1 ring-white/10 text-white"
                  value={manualOverlayEditing.overlayId}
                  onChange={(e) => {
                    const overlayId = Number(e.target.value);
                    const requestedSlot = overlayCache[overlayId]?.type || 'full';
                    setManualOverlayEditing((prev) => ({
                      ...prev,
                      overlayId,
                      requestedSlot,
                      compatibleSlots: requestedSlot === 'full'
                        ? ['full']
                        : [requestedSlot, requestedSlot === 'top' ? 'bottom' : 'top'],
                      overridePolicy: 'never',
                    }));
                  }}
                >
                  {Object.values(overlayCache)
                    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name || `Overlay ${o.id}`} ({o.type || 'overlay'})
                      </option>
                    ))}
                </select>
              </div>

              <div className="grid gap-2">
                <label className="text-xs text-slate-300">Device</label>
                <div className="px-3 py-2 rounded-xl bg-white/5 ring-1 ring-white/10 text-sm text-slate-200">
                  {devices.find((d) => d.id === manualOverlayEditing.deviceId)?.name || `Device ${manualOverlayEditing.deviceId}`}
                </div>
              </div>

              <div className="grid gap-2">
                <label className="text-xs text-slate-300">Start</label>
                <input
                  type="datetime-local"
                  step="1"
                  className="px-3 py-2 rounded-xl bg-white/10 ring-1 ring-white/10 text-white"
                  value={manualOverlayEditing.start}
                  onChange={(e) => setManualOverlayEditing((prev) => ({ ...prev, start: e.target.value }))}
                />
              </div>

              <div className="grid gap-2">
                <label className="text-xs text-slate-300">End</label>
                <input
                  type="datetime-local"
                  step="1"
                  className="px-3 py-2 rounded-xl bg-white/10 ring-1 ring-white/10 text-white"
                  value={manualOverlayEditing.end}
                  onChange={(e) => setManualOverlayEditing((prev) => ({ ...prev, end: e.target.value }))}
                />
              </div>

              <div className="grid gap-2">
                <label className="text-xs text-slate-300">Visual slot and priority</label>
                <div className="rounded-xl bg-white/5 px-3 py-2 text-sm text-slate-200 ring-1 ring-white/10">
                  {manualOverlayEditing.requestedSlot || 'full'} · priority {manualOverlayEditing.priorityValue}
                </div>
                {manualOverlayEditing.plannerDisposition ? (
                  <div className="text-[11px] text-slate-400">
                    Last plan: {manualOverlayEditing.plannerDisposition}
                    {manualOverlayEditing.plannerReason ? ` · ${manualOverlayEditing.plannerReason}` : ''}
                  </div>
                ) : null}
              </div>

              <div className="grid gap-2">
                <label className="text-xs text-slate-300">Conflict policy</label>
                {manualOverlayEditing.requestedSlot === 'full' ? (
                  <label className="flex items-start gap-2 rounded-xl bg-amber-500/10 px-3 py-2 text-xs text-amber-100 ring-1 ring-amber-300/10">
                    <input
                      type="checkbox"
                      checked={manualOverlayEditing.overridePolicy === 'explicit'}
                      onChange={(event) => setManualOverlayEditing((prev) => ({
                        ...prev,
                        overridePolicy: event.target.checked ? 'explicit' : 'never',
                      }))}
                    />
                    Allow this full-screen overlay to replace stronger planned content.
                  </label>
                ) : (
                  <label className="flex items-start gap-2 rounded-xl bg-white/5 px-3 py-2 text-xs text-slate-200 ring-1 ring-white/10">
                    <input
                      type="checkbox"
                      checked={(manualOverlayEditing.compatibleSlots || []).length > 1}
                      onChange={(event) => setManualOverlayEditing((prev) => ({
                        ...prev,
                        compatibleSlots: event.target.checked
                          ? [prev.requestedSlot, prev.requestedSlot === 'top' ? 'bottom' : 'top']
                          : [prev.requestedSlot],
                      }))}
                    />
                    Move to the other compact slot if needed.
                  </label>
                )}
              </div>

              <div className="md:col-span-2 flex justify-between gap-2 mt-2">
                {manualOverlayEditing.id && (
                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl bg-red-600/80 hover:bg-red-600 text-white text-sm"
                    onClick={deleteManualOverlay}
                  >
                    Delete
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm"
                    onClick={() => {
                      setManualOverlayModalOpen(false);
                      setManualOverlayEditing(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl bg-indigo-600/90 hover:bg-indigo-600 text-white text-sm"
                    onClick={saveManualOverlay}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          open={rotationModalOpen}
          onClose={() => {
            setRotationModalOpen(false);
          }}
          title="Add Rotating Ads"
        >
          <AdRotationEditor
            rotationForm={createRotationForm}
            setRotationForm={setCreateRotationForm}
            adRotations={adRotations}
            availableFullScreenAds={availableFullScreenAds}
            playableFullScreenAds={playableFullScreenAds}
            rotationSaving={rotationSaving}
            onCancel={() => setRotationModalOpen(false)}
            onSave={() => saveAdRotation(createRotationForm)}
          />
        </Modal>

        <Modal
          open={adModalOpen}
          onClose={() => setAdModalOpen(false)}
          title={adEditing?.id ? 'Edit Ad' : 'New Ad'}
        >
          {adEditing && (
            <div className="grid gap-4">
              <div className="grid gap-4">
                <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-4">
                  <div className="grid gap-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Ad Content
                    </label>
                    <select
                      className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                      value={
                        adEditing.contentType === 'announcement'
                          ? `announcement:${adEditing.announcementId || ''}`
                          : `overlay:${adEditing.overlayId || ''}`
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        const [nextType, rawId] = val.split(':');
                        const nextId = rawId ? Number(rawId) : '';
                        setAdEditing((prev) => ({
                          ...prev,
                          contentType: nextType === 'announcement' ? 'announcement' : 'overlay',
                          overlayId: nextType === 'overlay' ? nextId : '',
                          announcementId: nextType === 'announcement' ? nextId : '',
                          play_continuously: nextType === 'announcement' ? false : !!prev.play_continuously,
                          duration_sec:
                            nextType === 'announcement'
                              ? Number(prev.duration_sec) || 30
                              : Number(prev.duration_sec) || 60,
                          hold_visual_seconds:
                            nextType === 'announcement'
                              ? Math.max(0, Number(prev.hold_visual_seconds) || 0)
                              : 0,
                          playbackMode:
                            nextType === 'announcement'
                              ? 'visual_only'
                              : normalizeAdPlaybackMode(prev.playbackMode),
                          ad_transparency:
                            nextType === 'overlay' ? clampAdTransparency(prev.ad_transparency) : 60,
                          requestedSlot: null,
                          compatibleSlots: null,
                          overridePolicy: 'never',
                        }));
                        setOverlaySlotPreview(null);
                      }}
                    >
                      <option value="overlay:">— choose content —</option>
                      <optgroup label="Overlays">
                        {overlays.map((o) => (
                          <option key={o.id} value={`overlay:${o.id}`}>
                            {o.name || `Overlay ${o.id}`} ({o.type || 'overlay'})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Announcements">
                        {announcements.map((item) => (
                          <option key={item.id} value={`announcement:${item.id}`}>
                            {item.title || `Announcement ${item.id}`}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {adEditing.contentType === 'overlay' ? (
                    <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3 grid gap-3">
                      <div className="grid gap-1">
                        <label className="text-xs font-semibold uppercase text-slate-400">
                          Display Surface
                        </label>
                        <select
                          className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                          value={normalizeAdPlaybackMode(adEditing.playbackMode)}
                          onChange={(e) =>
                            setAdEditing((prev) => ({
                              ...prev,
                              playbackMode: normalizeAdPlaybackMode(e.target.value),
                            }))
                          }
                        >
                          <option value="auto">Auto - Suitestream app, system overlay over other apps</option>
                          <option value="suitestream">Suitestream app only</option>
                          <option value="system">System overlay</option>
                        </select>
                      </div>

                      <div className="grid gap-2">
                        <div className="flex items-center justify-between gap-3">
                          <label className="text-xs font-semibold uppercase text-slate-400">
                            Ad Transparency
                          </label>
                          <span className="text-xs font-semibold text-white">
                            {clampAdTransparency(adEditing.ad_transparency)}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={5}
                          className="w-full accent-emerald-400"
                          value={clampAdTransparency(adEditing.ad_transparency)}
                          onChange={(e) =>
                            setAdEditing((prev) => ({
                              ...prev,
                              ad_transparency: clampAdTransparency(e.target.value),
                            }))
                          }
                        />
                        <div className="text-[11px] text-slate-400">
                          Overrides this scheduled ad only. 0% is solid, 100% is transparent.
                        </div>
                      </div>

                      <div className="grid gap-3 rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
                        <div className="grid gap-2 md:grid-cols-2">
                          <label className="grid gap-1 text-xs text-slate-300">
                            <span>Preferred visual slot</span>
                            <select
                              value={adEditingRequestedSlot}
                              disabled
                              className="rounded-lg bg-slate-950/70 p-2 text-white ring-1 ring-white/10"
                            >
                              <option value="top">Top</option>
                              <option value="bottom">Bottom</option>
                              <option value="full">Full screen</option>
                            </select>
                          </label>
                          <label className="grid gap-1 text-xs text-slate-300">
                            <span>Priority</span>
                            <select
                              value={adEditing.priorityValue || 40}
                              onChange={(event) => setAdEditing((current) => ({
                                ...current,
                                priorityValue: Number(event.target.value),
                              }))}
                              className="rounded-lg bg-slate-950/70 p-2 text-white ring-1 ring-white/10"
                            >
                              <option value={40}>General promotion</option>
                              <option value={50}>Generated teaser</option>
                              <option value={60}>Custom campaign</option>
                              <option value={80}>Event transition</option>
                              <option value={100}>Direct manager intervention</option>
                            </select>
                          </label>
                        </div>
                        {adEditingRequestedSlot !== 'full' ? (
                          <label className="flex items-center gap-2 text-xs text-slate-200">
                            <input
                              type="checkbox"
                              checked={adEditingCompatibleSlots.length > 1}
                              onChange={(event) => setAdEditing((current) => ({
                                ...current,
                                compatibleSlots: event.target.checked
                                  ? [adEditingRequestedSlot, adEditingRequestedSlot === 'top' ? 'bottom' : 'top']
                                  : [adEditingRequestedSlot],
                              }))}
                            />
                            Move to the other compact slot when the preferred slot is occupied
                          </label>
                        ) : (
                          <label className="flex items-start gap-2 text-xs text-amber-100">
                            <input
                              type="checkbox"
                              checked={adEditing.overridePolicy === 'explicit'}
                              onChange={(event) => setAdEditing((current) => ({
                                ...current,
                                overridePolicy: event.target.checked ? 'explicit' : 'never',
                              }))}
                            />
                            Allow this full-screen item to replace stronger overlapping content. This saved choice is audited in the slot decision.
                          </label>
                        )}
                        <button
                          type="button"
                          onClick={previewAdOverlaySlots}
                          disabled={overlaySlotPreviewLoading}
                          className="rounded-lg bg-cyan-600/80 px-3 py-2 text-xs font-semibold text-white hover:bg-cyan-600 disabled:opacity-50"
                        >
                          {overlaySlotPreviewLoading ? 'Checking Screens…' : 'Preview Screen slots'}
                        </button>
                        {overlaySlotPreview ? (
                          <div className="grid gap-1.5">
                            {overlaySlotPreview.map((entry, index) => {
                              const target = devices.find((deviceEntry) => Number(deviceEntry.id) === Number(entry.deviceId));
                              const decision = entry.decision;
                              return (
                                <div key={entry.deviceId || index} className="rounded-lg bg-black/20 px-2.5 py-2 text-[11px] text-slate-300">
                                  <span className="font-semibold text-white">{target?.name || `Screen ${entry.deviceId || index + 1}`}</span>
                                  {entry.error ? ` · ${entry.error}` : ` · ${decision?.disposition || 'unknown'}${decision?.selectedSlot ? ` in ${decision.selectedSlot}` : ''} · ${decision?.reason || 'No reason returned'}`}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-cyan-500/10 ring-1 ring-cyan-300/10 px-3 py-2 text-[11px] text-cyan-100">
                      Announcement ads use the full-screen visual surface so synced TVs stay aligned with the audio.
                    </div>
                  )}

                  <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3 grid gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Schedule</div>
                        <div className="text-sm text-white">
                          The drop point sets the starting slot. Power hours are used as the default window.
                        </div>
                      </div>
                      <label className="inline-flex items-center gap-2 text-xs text-slate-200">
                        <input
                          type="checkbox"
                          checked={!!adEditing.allDay}
                          onChange={(e) =>
                            setAdEditing((prev) => ({
                              ...prev,
                              allDay: e.target.checked,
                            }))
                          }
                        />
                        All Day
                      </label>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="grid gap-1">
                        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Date
                        </label>
                        <input
                          type="date"
                          className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                          value={adEditing.scheduleDate || ''}
                          onChange={(e) =>
                            setAdEditing((prev) => {
                              const scheduleDate = e.target.value;
                              const constrained = prev.allDay
                                ? null
                                : constrainAdTimeRange({
                                    deviceId: prev.deviceId,
                                    scheduleDate,
                                    startTimeLocal: prev.startTimeLocal,
                                    endTimeLocal: prev.endTimeLocal,
                                  });
                              return {
                                ...prev,
                                scheduleDate,
                                startTimeLocal: constrained?.startTimeLocal || prev.startTimeLocal,
                                endTimeLocal: constrained?.endTimeLocal || prev.endTimeLocal,
                              };
                            })
                          }
                        />
                      </div>

                      <div className="grid gap-1">
                        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Repeat
                        </label>
                        <select
                          className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                          value={adEditing.repeatMode || 'once'}
                          onChange={(e) =>
                            setAdEditing((prev) => ({
                              ...prev,
                              repeatMode: e.target.value === 'daily' ? 'daily' : 'once',
                            }))
                          }
                        >
                          <option value="once">Once</option>
                          <option value="daily">Every Day</option>
                        </select>
                      </div>
                    </div>

                    {!adEditing.allDay ? (
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="grid gap-1">
                          <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Start Time
                          </label>
                          <input
                            type="time"
                            className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                            value={adEditing.startTimeLocal || ''}
                            min={adEditingPowerWindow?.startTimeLocal || undefined}
                            max={adEditingPowerWindow?.endTimeLocal || undefined}
                            onChange={(e) =>
                              setAdEditing((prev) => {
                                const constrained = constrainAdTimeRange({
                                  deviceId: prev.deviceId,
                                  scheduleDate: prev.scheduleDate,
                                  startTimeLocal: e.target.value,
                                  endTimeLocal: prev.endTimeLocal,
                                });
                                return {
                                  ...prev,
                                  startTimeLocal: constrained.startTimeLocal,
                                  endTimeLocal: constrained.endTimeLocal,
                                };
                              })
                            }
                          />
                        </div>

                        <div className="grid gap-1">
                          <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            End Time
                          </label>
                          <input
                            type="time"
                            className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                            value={adEditing.endTimeLocal || ''}
                            min={adEditing.startTimeLocal || adEditingPowerWindow?.startTimeLocal || undefined}
                            max={adEditingPowerWindow?.endTimeLocal || undefined}
                            onChange={(e) =>
                              setAdEditing((prev) => {
                                const constrained = constrainAdTimeRange({
                                  deviceId: prev.deviceId,
                                  scheduleDate: prev.scheduleDate,
                                  startTimeLocal: prev.startTimeLocal,
                                  endTimeLocal: e.target.value,
                                });
                                return {
                                  ...prev,
                                  startTimeLocal: constrained.startTimeLocal,
                                  endTimeLocal: constrained.endTimeLocal,
                                };
                              })
                            }
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="text-[11px] text-slate-400">
                      {adEditingPowerWindow?.hasPowerWindow && adEditingPowerWindow?.startTimeLocal && adEditingPowerWindow?.endTimeLocal
                        ? `Current power window on this date: ${adEditingPowerWindow.startTimeLocal}-${adEditingPowerWindow.endTimeLocal}. End time is capped to power off, and later power schedule changes will update generated ad slots automatically.`
                        : 'No active power window is configured for that date, so the saved schedule may not generate any play slots until power hours are available.'}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-[minmax(0,220px),1fr] gap-3">
                    <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3">
                      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Primary Device</div>
                      <div className="mt-2 text-sm font-semibold text-white">
                        {adEditingPrimaryDevice?.name || `Device ${adEditing.deviceId}`}
                      </div>
                      <div className="text-xs text-slate-400">
                        {adEditingPrimaryDevice?.type || 'Unknown type'}
                      </div>
                    </div>

                    <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3 grid gap-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Sync To TVs</div>
                          <div className="text-sm text-white">
                            Also schedule this ad on extra TVs in the same area.
                          </div>
                        </div>
                        {adEditingSyncCandidates.length ? (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-[11px]"
                              onClick={() =>
                                setAdEditing((prev) => ({
                                  ...prev,
                                  syncDeviceIds: adEditingSyncCandidates.map((entry) => entry.id),
                                }))
                              }
                            >
                              Select All TVs
                            </button>
                            <button
                              type="button"
                              className="px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-[11px]"
                              onClick={() =>
                                setAdEditing((prev) => ({
                                  ...prev,
                                  syncDeviceIds: [],
                                }))
                              }
                            >
                              Clear
                            </button>
                          </div>
                        ) : null}
                      </div>

                      {adEditingSyncCandidates.length ? (
                        <div className="grid gap-2 max-h-48 overflow-auto pr-1">
                          {adEditingSyncCandidates.map((entry) => {
                            const checked = (adEditing.syncDeviceIds || []).includes(entry.id);
                            return (
                              <label
                                key={entry.id}
                                className="flex items-start gap-3 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 px-3 py-2"
                              >
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={checked}
                                  onChange={(e) =>
                                    setAdEditing((prev) => ({
                                      ...prev,
                                      syncDeviceIds: e.target.checked
                                        ? [...new Set([...(prev.syncDeviceIds || []), entry.id])]
                                        : (prev.syncDeviceIds || []).filter((id) => id !== entry.id),
                                    }))
                                  }
                                />
                                <span className="min-w-0">
                                  <span className="block text-sm font-semibold text-white truncate">{entry.name}</span>
                                  <span className="block text-[11px] text-slate-400">{entry.type}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400">
                          No extra TVs are available in this area for sync.
                        </div>
                      )}

                      <div className="text-[11px] text-slate-400">
                        {adEditing.contentType === 'announcement'
                          ? 'Primary device plays the announcement and shows the full visual. Synced TVs only show the full visual at the same scheduled times.'
                          : 'Synced TVs will receive the same overlay schedule when this ad is saved.'}
                      </div>
                    </div>
                  </div>

                  {adEditing.contentType !== 'announcement' ? (
                    <label className="flex items-center gap-2 text-xs text-slate-200">
                      <input
                        type="checkbox"
                        checked={!!adEditing.play_continuously}
                        onChange={(e) =>
                          setAdEditing((prev) => ({ ...prev, play_continuously: e.target.checked }))
                        }
                      />
                      Play continuously (only this overlay during the window)
                    </label>
                  ) : (
                    <div className="rounded-xl bg-cyan-500/10 ring-1 ring-cyan-300/10 px-3 py-2 text-[11px] text-cyan-100">
                      Announcements always run by cadence. Continuous mode is disabled for audio announcement playback.
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-4">
                <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-4">
                  {!adEditing.play_continuously || adEditing.contentType === 'announcement' ? (
                    <>
                      <div className="grid gap-1">
                        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Target Impressions Per Hour
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={3600}
                          className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                          value={adEditing.impressions_per_hour}
                          onChange={(e) =>
                            setAdEditing((prev) => ({
                              ...prev,
                              impressions_per_hour: Number(e.target.value),
                            }))
                          }
                        />
                        <div className="text-[11px] text-slate-400">Default: 6 (one every 10 minutes).</div>
                      </div>

                      <div className="grid gap-1">
                        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Play Time (Seconds)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={3600}
                          className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                          value={adEditing.duration_sec}
                          onChange={(e) =>
                            setAdEditing((prev) => ({
                              ...prev,
                              duration_sec: Number(e.target.value),
                            }))
                          }
                        />
                        <div className="text-[11px] text-slate-400">
                          {adEditing.contentType === 'announcement'
                            ? 'Use the approximate spoken announcement length so synced TVs hold the full overlay for the right base duration.'
                            : 'Default: 60 seconds.'}
                        </div>
                      </div>

                      {adEditing.contentType === 'announcement' ? (
                        <div className="grid gap-1">
                          <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Keep Visual On Display For Extra Seconds
                          </label>
                          <input
                            type="number"
                            min={0}
                            max={3600}
                            className="rounded-xl bg-black/30 ring-1 ring-white/10 p-2"
                            value={adEditing.hold_visual_seconds}
                            onChange={(e) =>
                              setAdEditing((prev) => ({
                                ...prev,
                                hold_visual_seconds: Math.max(0, Number(e.target.value) || 0),
                              }))
                            }
                          />
                          <div className="text-[11px] text-slate-400">
                            After the announcement audio ends, the full-screen visual can stay up while the background
                            video or music resumes.
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-4 text-sm text-slate-400">
                      This overlay stays visible for the full scheduled window. Drag the bar in the timeline to adjust
                      the repeating time window.
                    </div>
                  )}

                  <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3">
                    <label className="flex items-center gap-2 text-xs text-slate-200">
                      <input
                        type="checkbox"
                        checked={!!adSettings?.ad_breaks}
                        onChange={(e) => toggleAdBreaks(e.target.checked)}
                      />
                      Ad Breaks (group ads together)
                    </label>
                    <div className="text-[11px] text-slate-400 mt-1">
                      Saving a synced ad applies this same ad-break mode to every included device so their schedules stay aligned.
                    </div>
                  </div>

                  <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3 text-[11px] text-slate-400">
                    Devices included: {1 + (adEditing.syncDeviceIds?.length || 0)}.
                    {adEditing.syncDeviceIds?.length
                      ? ` ${adEditing.syncDeviceIds.length} extra TV${adEditing.syncDeviceIds.length === 1 ? '' : 's'} selected.`
                      : ' No extra TVs selected.'}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                  {adEditing.id ? (
                    <button
                      onClick={deleteAdSeries}
                      className="px-3 py-2 rounded-xl bg-red-500/15 hover:bg-red-500/25"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : null}
                  <button
                    onClick={saveAdSeries}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-emerald-600/90 hover:bg-emerald-600 disabled:opacity-60"
                  >
                    <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
